/**
 * GameScene — インゲームメインシーン
 * CMJGameWnd + CMJTblGame + CMJTblPaif + CMJTblDraw 相当 (AP-09 §2-1〜§2-6)
 *
 * ── ゲーム座標系 ──────────────────────────────────────────────────────────
 * Phaser ワールド = 1019×735 (GAME_WIDTH × GAME_HEIGHT)
 * ゲームボード: x=5, y=31, w=789, h=704
 * サイドバー:   x=794, y=31, w=225, h=704 (mj_sideBg.png)
 * ボード中心:   (399, 383)
 *
 * ── タイルコード → スプライトフレームインデックス (PaiCode.h 準拠) ──────
 *   MAN (kind=0): 0x01-0x09 → frame 0-8
 *   SOU (kind=1): 0x11-0x19 → frame 9-17
 *   PIN (kind=2): 0x21-0x29 → frame 18-26
 *   風牌 (kind=3): 0x31-0x34 → frame 27-30
 *   三元牌 (kind=3): 0x35-0x37 → frame 31-33
 *
 * ── 4 プレイヤー配置 ──────────────────────────────────────────────────────
 * odr → loc = (4 + odr - myOdr) % 4 (MJTblDraw::Odr2Loc)
 * 手牌: MJTblDraw5.cpp tehpos / tehcol / mohofs
 * 捨て牌: MJTblDraw5.cpp sthpos / sthcol / sthrow / sthrot
 *
 * ── SignalR イベント ──────────────────────────────────────────────────────
 * smmc4e     → 牌情報リスト (SendPaiInfo)
 * playing    → MJPID_INIHAN / MJPID_INIKYO / MJPID_ACTION / MJPID_ENDKYO
 * playing/MJPID_ENDKYO → 局結果
 */
import Phaser from 'phaser'
import * as SignalR from '../api/signalr'
import type { CreateGameOptions } from '../game/GameInstance'
import { DEFAULT_GAME_ASSIST_CONFIG, GAME_ASSIST_CONFIG_EVENT, toGameAssistConfig, type GameAssistConfig } from '../game/assistConfig'
import { assistTileMask, decideAutoDiscardDelayMs, decideDiscardSource, decideTimedDiscardIndex, decideTouchTileAction, DISCARD_SOURCE_MARKER_DEPTH, isDrawnTilePosition, offsetDiscardSourceMarker, sortHandForDisplay, waitGuideWorldY } from '../game/assistLogic'
import {
  GAME_AUTO_PASS_HOLD_EVENT,
  GAME_AUTO_CONTROL_EVENT,
  GAME_KYOKU_STARTED_EVENT,
  GAME_LOCAL_REACH_EVENT,
  getAutoControlDelayMs,
  resolveAutoControlAction,
  resolveLegacyTimerMode,
  shouldSuspendAutoPassForPrompt,
  type AutoControlState,
} from '../game/autoControl'
import { emitGameLoadProgress } from '../game/gameLoadProgress'
import { reorderHanchanPlayers } from '../game/hanchanOrder'
import {
  getLegacyHoraPresentations,
  LEGACY_EFFECT_FRAME_MS,
  LEGACY_HORA_FIRE_DURATION_MS,
  LEGACY_YAKUMAN_FINISH_DURATION_MS,
  numberedLegacyKeys,
  type LegacyHoraPresentation,
} from '../game/legacyAnimations'
import {
  DESKTOP_INGAME_LAYOUT,
  getIngameLayout,
  isCenteredIngameLayout,
  isMobileIngameLayout,
  MOBILE_DEAD_WALL_SHIFT_X,
  MOBILE_DISCARD_CENTER_INFO_OFFSETS,
  type IngameLayoutMode,
} from '../game/ingameLayout'
import {
  centerMobileEffectPoint,
  MOBILE_PLAYFIELD_OFFSET_Y,
  mobileCenterHudOffset,
  mobileVisibleWorldBounds,
  mobileVisibleWorldLayoutKey,
  responsiveDesktopCenterOffset,
  responsiveDesktopSeatOffset,
  responsiveDesktopVisibleWorldBounds,
} from '../game/mobileIngameViewport'
import { canCompleteGameResync, restoreVisiblePaiCodes, shouldUsePendingInitPaiInfo } from '../game/resyncState'
import { buildInitialHandIndices, inferInitialDealStart } from '../game/replayInitialDeal'
import { getLegacyBoardSkinId } from '../utils/legacySkinPalette'
import {
  beginPaifuRecording,
  cancelPaifuRecording,
  GAME_PAIFU_RECORDING_CONFIG_EVENT,
  interruptPaifuRecording,
  recordPaifuPacket,
  replaceRecordedPaifuPackets,
  setPaifuRecordingMode,
} from '../game/paifuRecording'
import { useAuthStore } from '../store/authStore'
import { getDefaultAvatarUrl, getHighResolutionGameAvatarUrl } from '../utils/resources'
import { getUiFontFamily, getUiFontSize } from '../utils/typography'
import {
  MID_BAD, MID_FESRIC, MID_GOOD, MID_NORMAL, MID_RICHI, MID_TEN_ALLLAST, MID_TEN_NANBA, MID_TEN_REACH1, MID_TEN_REACH2, MID_TEN_TONBA,
  playMajakBgm, playMajakCallVoice, playMajakSfx, playMajakSid,
  SID_DICE, SID_EFFECT_R_LV1, SID_EFFECT_R_LV2, SID_EFFECT_R_LV3, SID_EFFECT_T_LV1, SID_EFFECT_T_LV2, SID_EFFECT_T_LV3, SID_EFFECT_YAKUMAN, SID_EXPOSE, SID_FURO, SID_RICSTK, SID_SIPAI, SID_THROW, SID_THROW_DORA, SID_THROW_RICH, SID_TIME, SID_TURN,
} from '../utils/majakSound'

const PAIFU_ROTATE_EVENT = 'majak:paifu-rotate'
const PAIFU_HAND_OPEN_EVENT = 'majak:paifu-hand-open'
const PAIFU_GRAPH_EVENT = 'majak:paifu-graph'
const PAIFU_REPLAY_PACKET_EVENT = 'majak:paifu-replay-packet'
const PAIFU_REPLAY_READY_EVENT = 'majak:paifu-replay-ready'
const DEBUG_GAME = import.meta.env.VITE_DEBUG_GAME === '1'
const ASK_END_SET_EVENT = 'majak:ask-end-set'
const KYO_RESULT_ACTION_EVENT = 'majak:kyo-result-action'
const KYO_RESULT_PROGRESS_EVENT = 'majak:kyo-result-progress'
const KYO_RESULT_CONFIRMED_EVENT = 'majak:kyo-result-confirmed'
const GAME_FOCUS_CHAT_EVENT = 'majak:game-focus-chat'
const GAME_STATUS_EVENT = 'majak:game-status'
const GAME_EFFECT_WINDOW_EVENT = 'majak:game-effect-window'
const GAME_SYNC_EVENT = 'majak:game-sync'
const GAME_FLOW_TRACE_PREFIX = '[GameFlow]'
const DISCARD_PROBE_PREFIX = '[GameScene/DiscardProbe]'
const RESYNC_PROBE_PREFIX = '[GameScene/ResyncProbe]'
const IMG = '/assets/images/game'
const UI_SINGLE_DELIVERY_EVENTS = new Set(['turnChange', 'actionPromptStart', 'actionPromptEnd'])
const CUSTOM_BGM_ID_EXTRA = 100008
const CUSTOM_BGM_ID_TENGOKU = 100009
const CUSTOM_ITEM_TYPE_BG_EXTRA = 11
const CUSTOM_ITEM_TYPE_BG_TENGOKU = 12
const CUSTOM_DEFAULT_ID_COSTUME = 100011
const AVAILABLE_COSTUME_IDS = new Set([9, 10, 11])
const TIME_WARNING_START_MS = 5000

/* ========================================================================
 * 定数
 * ======================================================================== */
let INGAME_LAYOUT = getIngameLayout()
let BOARD_X = INGAME_LAYOUT.board.x,   BOARD_Y = INGAME_LAYOUT.board.y
let BOARD_W = INGAME_LAYOUT.board.width, BOARD_H = INGAME_LAYOUT.board.height
const FLUID_BOARD_BACKGROUND_SCALE = 1.5
const BOARD_BACKGROUND_SAFE_WIDTH = 580
const BOARD_BACKGROUND_SAFE_HEIGHT = 460
let SIDE_PANEL = INGAME_LAYOUT.sidePanel
let DRAGON_OVERLAY = INGAME_LAYOUT.dragonOverlay
let CENTER_INFO = INGAME_LAYOUT.centerInfo
let X_PANEL = INGAME_LAYOUT.panel.x
let Y_PANEL = INGAME_LAYOUT.panel.y
let W_PANEL = INGAME_LAYOUT.panel.width
let H_PANEL = INGAME_LAYOUT.panel.height
let Z_PANEL = INGAME_LAYOUT.panel.depth
let ACTION_BUTTON_LAYOUT = INGAME_LAYOUT.actionButtons
const DESKTOP_DISCARD_COLS = 6
const MOBILE_DISCARD_COLS = 10
const MOBILE_NON_SELF_TILE_SCALE = 0.70
const MOBILE_TILE_SCALE = MOBILE_NON_SELF_TILE_SCALE
const MOBILE_SELF_HAND_TILE_SCALE = 0.95
const MOBILE_OTHER_HAND_TILE_SCALE = MOBILE_NON_SELF_TILE_SCALE
const MOBILE_OTHER_HAND_STEP_RATIO = 0.72
const MOBILE_TOP_HAND_TILE_OFFSET = -2
const MOBILE_SIDE_HAND_Y_OFFSET = 0
const MOBILE_LEFT_SIDE_HAND_OUTER_OFFSET = 20
const MOBILE_RIGHT_SIDE_HAND_OUTER_OFFSET = 8
const MOBILE_TOP_MELD_OUTER_OFFSET_Y = 16
const MOBILE_BOTTOM_MELD_OUTER_OFFSET_Y = 16
const MOBILE_TOP_MELD_OUTER_OFFSET_X = -32
const MOBILE_BOTTOM_MELD_OUTER_OFFSET_X = 32
const MOBILE_DISCARD_LAYOUT_SCALE = 1.20
const MOBILE_CONTENT_BASE_ASPECT = 375 / 667
const MOBILE_CONTENT_SCALE_MIN = 0.68
const MOBILE_OPPONENT_SUMMARY_TILE_SCALE = 0.64
const MOBILE_HUD_AVATAR_WIDTH = 44
const MOBILE_HUD_AVATAR_INSET_X = 14
const MOBILE_HUD_AVATAR_INSET_TOP = 8
const MOBILE_SIDE_HAND_AVATAR_GAP = 8
const MOBILE_LEFT_SIDE_HAND_INNER_OFFSET = 28
const MOBILE_DEAD_WALL_AVATAR_GAP = 86
const MOBILE_DEAD_WALL_AVATAR_Y_OFFSET = 14
const MOBILE_SELF_HAND_BOTTOM_INSET = 12
const MOBILE_SELF_HAND_FIXED_COUNT = 14
const MOBILE_OTHER_HAND_FIXED_COUNT = 14
const TOP_HAND_ANCHOR_TILE_COUNT = 13
const MOBILE_SELF_HAND_DEPTH = 900
const LEGACY_GEM_EFFECT_SIZE = { width: 345, height: 353 }
const LEGACY_YAKUMAN_FINISH_SIZE = { width: 563, height: 435 }
const LEGACY_TSUMO_EFFECT_THICKNESS = 164
const MATCH_START_SEAT_REVEAL_DURATION_MS = 3100
const LEGACY_WAREME_PRESENTATION_DURATION_MS = 3700
const RESPONSIVE_DESKTOP_TOP_HAND_OFFSET_X = 25
const MATCH_START_SEAT_POSITIONS = [
  { x: 379, y: 506 },
  { x: 627, y: 325 },
  { x: 379, y: 131 },
  { x: 117, y: 325 },
] as const
const MATCH_START_OPEN_OFFSETS = [
  { x: 0, y: 0 },
  { x: -15, y: 18 },
  { x: 0, y: 17 },
  { x: 0, y: 18 },
] as const
let DISCARD_COLS = DESKTOP_DISCARD_COLS
let CURRENT_INGAME_LAYOUT_MODE: IngameLayoutMode = 'desktop'

function applyIngameLayout(mode: IngameLayoutMode) {
  CURRENT_INGAME_LAYOUT_MODE = mode
  INGAME_LAYOUT = getIngameLayout(mode)
  DISCARD_COLS = isMobileIngameLayout(mode) ? MOBILE_DISCARD_COLS : DESKTOP_DISCARD_COLS
  BOARD_X = INGAME_LAYOUT.board.x
  BOARD_Y = INGAME_LAYOUT.board.y
  BOARD_W = INGAME_LAYOUT.board.width
  BOARD_H = INGAME_LAYOUT.board.height
  SIDE_PANEL = INGAME_LAYOUT.sidePanel
  DRAGON_OVERLAY = INGAME_LAYOUT.dragonOverlay
  CENTER_INFO = INGAME_LAYOUT.centerInfo
  X_PANEL = INGAME_LAYOUT.panel.x
  Y_PANEL = INGAME_LAYOUT.panel.y
  W_PANEL = INGAME_LAYOUT.panel.width
  H_PANEL = INGAME_LAYOUT.panel.height
  Z_PANEL = INGAME_LAYOUT.panel.depth
  ACTION_BUTTON_LAYOUT = INGAME_LAYOUT.actionButtons
  OPN_OFS = INGAME_LAYOUT.handOpenOffset
  TEH_POS = INGAME_LAYOUT.handPosition
  TEH_COL = INGAME_LAYOUT.handStep
  MOH_OFS = INGAME_LAYOUT.drawTileOffset
  STH_POS = INGAME_LAYOUT.discardPosition
  STH_COL = INGAME_LAYOUT.discardStep
  STH_ROW = INGAME_LAYOUT.discardRowStep
  STH_ROT = INGAME_LAYOUT.rotatedDiscardOffset
  WAN_POS = INGAME_LAYOUT.deadWall.position
  WAN_EXPOSE_OFFSET_Y = INGAME_LAYOUT.deadWall.exposeOffsetY
  BOARD_EFFECT_POS = INGAME_LAYOUT.boardEffectPosition
  REACH_TILE_EFFECT_OFFSET = INGAME_LAYOUT.reachTileEffectOffset
  REACH_TILE_EFFECT_MOVE = INGAME_LAYOUT.reachTileEffectMove
  PAIFU_GRAPH = INGAME_LAYOUT.paifuGraph.bounds
  PAIFU_GRAPH_ROWS = INGAME_LAYOUT.paifuGraph.rows
  PAIFU_GRAPH_MELD_GAP = INGAME_LAYOUT.paifuGraph.optionMeldGap
}

function boardLocalPoint(point: { x: number; y: number }): { x: number; y: number } {
  const offset = responsiveDesktopCenterOffset(CURRENT_INGAME_LAYOUT_MODE)
  return { x: BOARD_X + point.x + offset.x, y: BOARD_Y + point.y + offset.y }
}

function boardEdgePoint(point: { x: number; y: number }, loc: 0 | 1 | 2 | 3, mode: IngameLayoutMode): { x: number; y: number } {
  const offset = responsiveDesktopSeatOffset(mode, loc)
  return { x: BOARD_X + point.x + offset.x, y: BOARD_Y + point.y + offset.y }
}

let OPN_OFS = INGAME_LAYOUT.handOpenOffset
let TEH_POS = INGAME_LAYOUT.handPosition
let TEH_COL = INGAME_LAYOUT.handStep
let MOH_OFS = INGAME_LAYOUT.drawTileOffset
let STH_POS = INGAME_LAYOUT.discardPosition
let STH_COL = INGAME_LAYOUT.discardStep
let STH_ROW = INGAME_LAYOUT.discardRowStep
let STH_ROT = INGAME_LAYOUT.rotatedDiscardOffset
const BIPAI_MAX_COUNT = 136
const VIEWER_OPEN_POS = 4
const DEAD_WALL_COUNT = 14
const DEAD_WALL_START = BIPAI_MAX_COUNT - DEAD_WALL_COUNT
const DEAD_WALL_TILE_WIDTH = 31
let WAN_POS = INGAME_LAYOUT.deadWall.position
let WAN_EXPOSE_OFFSET_Y = INGAME_LAYOUT.deadWall.exposeOffsetY
let BOARD_EFFECT_POS = INGAME_LAYOUT.boardEffectPosition
let REACH_TILE_EFFECT_OFFSET = INGAME_LAYOUT.reachTileEffectOffset
let REACH_TILE_EFFECT_MOVE = INGAME_LAYOUT.reachTileEffectMove
let PAIFU_GRAPH = INGAME_LAYOUT.paifuGraph.bounds
let PAIFU_GRAPH_ROWS = INGAME_LAYOUT.paifuGraph.rows
const PAIFU_GRAPH_OPTIONS = [
  { key: 'mj_opt_0', optionIndex: 0, defaultFrame: 1 },
  { key: 'mj_opt_3', optionIndex: 3, defaultFrame: 0 },
  { key: 'mj_opt_1', optionIndex: 1, defaultFrame: 2 },
  { key: 'mj_optron', optionIndex: 12, defaultFrame: 0 },
  { key: 'mj_opt_5', optionIndex: 5, defaultFrame: 2 },
  { key: 'mj_opt_2', optionIndex: 4, defaultFrame: 0 },
  { key: 'mj_optwar', optionIndex: 8, defaultFrame: 0 },
  { key: 'mj_opttip', optionIndex: 9, defaultFrame: 0 },
  { key: 'mj_opt_4', optionIndex: 2, defaultFrame: 2 },
  { key: 'mj_opt_6', optionIndex: 6, defaultFrame: 0 },
  { key: 'mj_opt_7', optionIndex: 7, defaultFrame: 0 },
] as const
let PAIFU_GRAPH_MELD_GAP = INGAME_LAYOUT.paifuGraph.optionMeldGap

/* ========================================================================
 * PaiCode → フレームインデックス変換 (PaiCode.h 準拠)
 * ======================================================================== */
export function paiToFrame(code: number): number {
  if (code <= 0) return 36          // 無効・裏牌は末尾
  const kind   = (code >> 4) & 0xF
  const number = code & 0xF
  if (kind < 3) return kind * 9 + (number - 1)  // MAN/SOU/PIN
  if (number >= 1 && number <= 4) return 27 + (number - 1)  // 風牌
  if (number >= 5 && number <= 7) return 31 + (number - 5)  // 三元牌
  return 36
}

/* ========================================================================
 * 捨て牌グリッド位置 (3列×6行 中心起点)
 * odr=0 下, odr=1 右, odr=2 上, odr=3 左
 * ======================================================================== */
function odrToLoc(odr: number, viewOdr: number): 0 | 1 | 2 | 3 {
  return ((4 + odr - viewOdr) % 4) as 0 | 1 | 2 | 3
}

function handPos(loc: 0 | 1 | 2 | 3, idx: number, isDrawTile: boolean, isOpenMode = false): { x: number; y: number } {
  const tableLoc = loc === 0 ? 4 : loc
  const handIndex = loc === 2 ? TOP_HAND_ANCHOR_TILE_COUNT - 1 - idx : idx
  const offset = isDrawTile
    ? loc === 2 ? { x: -MOH_OFS[tableLoc].x, y: MOH_OFS[tableLoc].y } : MOH_OFS[tableLoc]
    : { x: 0, y: 0 }
  const openOffset = isOpenMode ? OPN_OFS[tableLoc] : { x: 0, y: 0 }
  const point = {
    x: TEH_POS[tableLoc].x + TEH_COL[tableLoc].x * handIndex + offset.x + openOffset.x,
    y: TEH_POS[tableLoc].y + TEH_COL[tableLoc].y * handIndex + offset.y + openOffset.y,
  }
  if (CURRENT_INGAME_LAYOUT_MODE === 'responsiveDesktop') {
    const position = boardLocalPoint(point)
    if (loc === 0) return { x: position.x + X_PANEL - TEH_POS[4].x, y: position.y }
    if (loc === 2) return { x: position.x + RESPONSIVE_DESKTOP_TOP_HAND_OFFSET_X, y: position.y }
    return position
  }
  return boardEdgePoint(point, loc, CURRENT_INGAME_LAYOUT_MODE)
}

function mobileOuterHandPos(loc: 0 | 1 | 2 | 3, idx: number, _count: number, isDrawTile: boolean, scale: number): { x: number; y: number } | null {
  const bounds = mobileVisibleWorldBounds()
  if (!bounds) return null
  const centerX = (bounds.left + bounds.right) / 2
  const centerY = (bounds.top + bounds.bottom) / 2
  const inset = 12
  const drawGap = isDrawTile ? 8 * scale : 0

  if (loc === 0 || loc === 2) {
    const tileWidth = 37 * scale
    const step = loc === 0 ? tileWidth : tileWidth * MOBILE_OTHER_HAND_STEP_RATIO
    const layoutCount = loc === 0 ? MOBILE_SELF_HAND_FIXED_COUNT : MOBILE_OTHER_HAND_FIXED_COUNT
    const totalWidth = Math.max(0, layoutCount - 1) * step + tileWidth
    const startX = centerX - totalWidth / 2 + (loc === 2 ? step * MOBILE_TOP_HAND_TILE_OFFSET : 0)
    const x = loc === 2
      ? startX + idx * step + drawGap
      : startX + idx * step + drawGap
    const y = loc === 0
      ? bounds.bottom - 63 * scale - MOBILE_SELF_HAND_BOTTOM_INSET
      : bounds.top + inset
    return { x, y }
  }

  const tileWidth = 63 * scale
  const tileHeight = 37 * scale
  const step = tileHeight * MOBILE_OTHER_HAND_STEP_RATIO
  const layoutCount = MOBILE_OTHER_HAND_FIXED_COUNT
  const totalHeight = Math.max(0, layoutCount - 1) * step + tileHeight
  const avatarInnerGap = MOBILE_HUD_AVATAR_INSET_X + MOBILE_HUD_AVATAR_WIDTH + MOBILE_SIDE_HAND_AVATAR_GAP
  const x = loc === 1
    ? bounds.right - avatarInnerGap - tileWidth + MOBILE_RIGHT_SIDE_HAND_OUTER_OFFSET
    : bounds.left + avatarInnerGap + MOBILE_LEFT_SIDE_HAND_INNER_OFFSET - MOBILE_LEFT_SIDE_HAND_OUTER_OFFSET
  const y = loc === 1
    ? centerY - totalHeight / 2 + MOBILE_SIDE_HAND_Y_OFFSET + Math.max(0, layoutCount - 1 - idx) * step - drawGap
    : centerY - totalHeight / 2 + MOBILE_SIDE_HAND_Y_OFFSET + idx * step + drawGap
  return { x, y }
}

function mobileContentScale(): number {
  const bounds = mobileVisibleWorldBounds()
  if (!bounds) return 1
  const visibleWidth = bounds.right - bounds.left
  const visibleHeight = bounds.bottom - bounds.top
  if (visibleWidth <= 0 || visibleHeight <= 0) return 1
  return Phaser.Math.Clamp(
    (visibleHeight / visibleWidth) / MOBILE_CONTENT_BASE_ASPECT,
    MOBILE_CONTENT_SCALE_MIN,
    1,
  )
}

function handTexture(loc: 0 | 1 | 2 | 3): { key: string; frame?: number } {
  if (loc === 0) return { key: 'hai_tachi_0' }
  return { key: `hai_tachi_${loc}` }
}

function concealedHandTexture(loc: 0 | 1 | 2 | 3): { key: string; frame?: number } {
  return { key: `hai_tachi_${loc}` }
}

function openHandTexture(loc: 0 | 1 | 2 | 3): string {
  return loc === 0 ? 'hai_omote' : `hai_open_${loc}`
}

function handDepth(y: number, idx: number): number {
  return 10 + y * 0.01 + idx * 0.0001
}

function discardTexture(loc: 0 | 1 | 2 | 3, flag: number): string {
  const dir = flag === 2 ? (loc + 1) % 4 : loc
  return dir === 0 ? 'hai_sute' : `hai_open_${dir}`
}

function discardBasePos(loc: 0 | 1 | 2 | 3, mode: IngameLayoutMode): { x: number; y: number } {
  if (isMobileIngameLayout(mode)) {
    const offset = MOBILE_DISCARD_CENTER_INFO_OFFSETS[loc]
    return { x: CENTER_INFO.x + offset.x, y: CENTER_INFO.y + offset.y }
  }
  return STH_POS[loc]
}

function downTexture(loc: 0 | 1 | 2 | 3): string {
  return loc === 2 ? 'hai_ura_2' : loc === 1 || loc === 3 ? 'hai_ura_1' : 'hai_ura_0'
}

function discardPos(loc: 0 | 1 | 2 | 3, idx: number, flag: number, mode: IngameLayoutMode): { x: number; y: number } {
  const col = idx % DISCARD_COLS
  const row = Math.floor(idx / DISCARD_COLS)
  const base = discardBasePos(loc, mode)
  const layoutScale = isMobileIngameLayout(mode)
    ? MOBILE_DISCARD_LAYOUT_SCALE * (MOBILE_NON_SELF_TILE_SCALE / 0.70) * mobileContentScale()
    : 1
  let x = base.x + (STH_COL[loc].x * col + STH_ROW[loc].x * row) * layoutScale
  let y = base.y + (STH_COL[loc].y * col + STH_ROW[loc].y * row) * layoutScale
  if (flag === 2) {
    x += STH_ROT[loc].x * layoutScale
    y += STH_ROT[loc].y * layoutScale
  } else if (flag === 1) {
    const nextLoc = (loc + 1) % 4
    x += (STH_ROW[nextLoc].x - STH_COL[loc].x) * layoutScale
    y += (STH_ROW[nextLoc].y - STH_COL[loc].y) * layoutScale
  }
  const centerOffset = isMobileIngameLayout(mode) ? mobileCenterHudOffset(mode) : { x: 0, y: 0 }
  x += centerOffset.x
  y += centerOffset.y
  return boardLocalPoint({ x, y })
}

function mobileMeldBasePos(loc: 0 | 1 | 2 | 3, meldScale: number): { x: number; y: number } | null {
  const handScale = (loc === 0 ? MOBILE_SELF_HAND_TILE_SCALE : MOBILE_OTHER_HAND_TILE_SCALE) * mobileContentScale()
  const handEndIndex = loc === 2 ? 0 : MOBILE_OTHER_HAND_FIXED_COUNT - 1
  const handEnd = mobileOuterHandPos(loc, handEndIndex, MOBILE_OTHER_HAND_FIXED_COUNT, false, handScale)
  if (!handEnd) return null

  const desktopHandLoc = loc === 0 ? 4 : loc
  const desktopHandEnd = {
    x: DESKTOP_INGAME_LAYOUT.handPosition[desktopHandLoc].x
      + DESKTOP_INGAME_LAYOUT.handStep[desktopHandLoc].x * (MOBILE_OTHER_HAND_FIXED_COUNT - 1),
    y: DESKTOP_INGAME_LAYOUT.handPosition[desktopHandLoc].y
      + DESKTOP_INGAME_LAYOUT.handStep[desktopHandLoc].y * (MOBILE_OTHER_HAND_FIXED_COUNT - 1),
  }
  const desktopMeldBase = DESKTOP_INGAME_LAYOUT.meldPosition[loc]
  return {
    x: handEnd.x + (desktopMeldBase.x - desktopHandEnd.x) * meldScale
      + (loc === 2 ? MOBILE_TOP_MELD_OUTER_OFFSET_X : loc === 0 ? MOBILE_BOTTOM_MELD_OUTER_OFFSET_X : 0),
    y: handEnd.y + (desktopMeldBase.y - desktopHandEnd.y) * meldScale
      + (loc === 2 ? -MOBILE_TOP_MELD_OUTER_OFFSET_Y : loc === 0 ? MOBILE_BOTTOM_MELD_OUTER_OFFSET_Y : 0),
  }
}

function responsiveMeldBasePos(loc: 0 | 1 | 2 | 3): { x: number; y: number } {
  return boardLocalPoint(DESKTOP_INGAME_LAYOUT.meldPosition[loc])
}

function meldPos(loc: 0 | 1 | 2 | 3, row: number, col: number, flag: number, mode: IngameLayoutMode = 'desktop', scale = 1): { x: number; y: number } {
  const dir = flag === 2 ? ((loc + 1) % 4) as 0 | 1 | 2 | 3 : loc
  const meldLayout = DESKTOP_INGAME_LAYOUT
  const mobileBase = isMobileIngameLayout(mode) ? mobileMeldBasePos(loc, scale) : null
  const responsiveBase = mode === 'responsiveDesktop' ? responsiveMeldBasePos(loc) : null
  const base = mobileBase ?? meldLayout.meldPosition[loc]
  const stepScale = isMobileIngameLayout(mode) ? scale : 1
  const scaleX = stepScale
  const scaleY = stepScale
  const rowStep = { x: meldLayout.discardRowStep[loc].x * scaleX, y: meldLayout.discardRowStep[loc].y * scaleY }
  const colStep = { x: meldLayout.discardStep[loc].x * scaleX, y: meldLayout.discardStep[loc].y * scaleY }
  const rotStep = { x: meldLayout.rotatedDiscardOffset[loc].x * scaleX, y: meldLayout.rotatedDiscardOffset[loc].y * scaleY }
  let x = (responsiveBase?.x ?? base.x) - rowStep.x * row - colStep.x * (col + 1)
  let y = (responsiveBase?.y ?? base.y) - rowStep.y * row - colStep.y * (col + 1)
  if (flag === 2) {
    x += colStep.x + rotStep.x - meldLayout.discardRowStep[dir].x * scaleX
    y += colStep.y + rotStep.y - meldLayout.discardRowStep[dir].y * scaleY
  } else if (flag === 1) {
    const nextLoc = ((loc + 1) % 4) as 0 | 1 | 2 | 3
    x += colStep.x - meldLayout.discardRowStep[nextLoc].x * scaleX
    y += colStep.y - meldLayout.discardRowStep[nextLoc].y * scaleY
  }
  return mobileBase || responsiveBase ? { x, y } : boardEdgePoint({ x, y }, loc, mode)
}

function meldTexture(loc: 0 | 1 | 2 | 3, flag: number, isDown: boolean): { key: string; frame?: number } {
  if (isDown) return { key: downTexture(loc), frame: 0 }
  const dir = flag === 2 ? ((loc + 1) % 4) as 0 | 1 | 2 | 3 : loc
  return { key: dir === 0 ? 'hai_sute' : `hai_open_${dir}` }
}

function skinTextureCandidate(key: string): string {
  return `${key}_skin`
}

function themeColorToTint(color: string | undefined): number | undefined {
  if (!color || !/^#[0-9a-f]{6}$/i.test(color)) return undefined
  return Number.parseInt(color.slice(1), 16)
}

function activeThemeBoardColor(): string | undefined {
  if (typeof document === 'undefined') return undefined
  const color = getComputedStyle(document.documentElement).getPropertyValue('--majak-popup-command-color').trim()
  return /^#[0-9a-f]{6}$/i.test(color) ? color : undefined
}

function darkenThemeTint(color: number): number {
  const rgb = Phaser.Display.Color.IntegerToRGB(color)
  return Phaser.Display.Color.GetColor(
    Math.round(rgb.r * 0.48),
    Math.round(rgb.g * 0.48),
    Math.round(rgb.b * 0.48),
  )
}

function mixThemeTint(color: number, target: number, amount: number): number {
  const source = Phaser.Display.Color.IntegerToRGB(color)
  const destination = Phaser.Display.Color.IntegerToRGB(target)
  return Phaser.Display.Color.GetColor(
    Math.round(source.r + (destination.r - source.r) * amount),
    Math.round(source.g + (destination.g - source.g) * amount),
    Math.round(source.b + (destination.b - source.b) * amount),
  )
}

function mobileDeadWallBasePos(): { x: number; y: number } | null {
  const bounds = mobileVisibleWorldBounds()
  if (!bounds) return null
  const tileStep = DEAD_WALL_TILE_WIDTH * MOBILE_NON_SELF_TILE_SCALE * mobileContentScale()
  const groupWidth = tileStep * 7
  const rightAvatarLeft = bounds.right - MOBILE_HUD_AVATAR_WIDTH - MOBILE_HUD_AVATAR_INSET_X
  return {
    x: rightAvatarLeft - MOBILE_DEAD_WALL_AVATAR_GAP - groupWidth - BOARD_X + MOBILE_DEAD_WALL_SHIFT_X,
    y: bounds.top + MOBILE_HUD_AVATAR_INSET_TOP - BOARD_Y
      + WAN_EXPOSE_OFFSET_Y * MOBILE_NON_SELF_TILE_SCALE * mobileContentScale()
      + MOBILE_DEAD_WALL_AVATAR_Y_OFFSET + MOBILE_PLAYFIELD_OFFSET_Y,
  }
}

function deadWallPos(idx: number, mode: IngameLayoutMode): { x: number; y: number } {
  const base = isMobileIngameLayout(mode) ? mobileDeadWallBasePos() ?? WAN_POS : WAN_POS
  const tileScale = isMobileIngameLayout(mode) ? MOBILE_NON_SELF_TILE_SCALE * mobileContentScale() : 1
  const tileStep = isMobileIngameLayout(mode) ? DEAD_WALL_TILE_WIDTH : TEH_COL[0].x
  return boardLocalPoint({
    x: base.x + (6 - Math.floor(idx / 2)) * tileStep * tileScale,
    y: base.y - (idx % 2 === 0 ? WAN_EXPOSE_OFFSET_Y * tileScale : 0),
  })
}

function paiToSerial(code: number): number {
  const kind = (code >> 4) & 0xF
  const number = code & 0xF
  if (kind >= 0 && kind < 3 && number >= 1 && number <= 9) return kind * 9 + (number - 1)
  if (kind === 3 && number >= 1 && number <= 4) return 27 + (number - 1)
  if (kind === 3 && number >= 5 && number <= 7) return 31 + (number - 5)
  return -1
}

function serialToPaiCode(serial: number): number {
  if (serial >= 0 && serial < 27) return ((Math.floor(serial / 9) & 0xF) << 4) | ((serial % 9) + 1)
  if (serial >= 27 && serial < 31) return 0x30 | (serial - 26)
  if (serial >= 31 && serial < 34) return 0x30 | (serial - 26)
  return 0
}

function isTouchPointer(pointer?: Phaser.Input.Pointer): boolean {
  if (!pointer) return false
  if (pointer.wasTouch) return true
  const event = pointer.event as Event & { pointerType?: string }
  return event.pointerType === 'touch' || event.type.startsWith('touch')
}

function hasHoraFormAfterAdd(counts: number[], handCount: number, addSerial: number): boolean {
  if (addSerial < 0 || addSerial >= 34 || counts[addSerial] >= 4) return false
  counts[addSerial]++
  const result = checkHoraCounts(counts, handCount + 1)
  counts[addSerial]--
  return result
}

function checkTempaiAfterDiscard(hand: TileState[], discardIdx: number): boolean {
  const counts = Array.from({ length: 34 }, () => 0)
  for (let idx = 0; idx < hand.length; idx++) {
    if (idx === discardIdx) continue
    const serial = paiToSerial(hand[idx].code)
    if (serial < 0) return false
    counts[serial]++
  }
  const handCount = Math.max(0, hand.length - 1)
  for (let serial = 0; serial < 34; serial++) {
    if (hasHoraFormAfterAdd(counts, handCount, serial)) return true
  }
  return false
}

function checkHoraCounts(counts: number[], handCount: number): boolean {
  const work = [...counts]
  return checkKokushi(work, handCount) || checkChitoi(work, handCount) || checkHead(work, 0, handCount)
}

function checkKokushi(counts: number[], handCount: number): boolean {
  if (handCount !== 14) return false
  const terminals = [0, 8, 9, 17, 18, 26, 27, 28, 29, 30, 31, 32, 33]
  let hasPair = false
  for (const serial of terminals) {
    if (counts[serial] === 2) hasPair = true
    else if (counts[serial] !== 1) return false
  }
  return hasPair
}

function checkChitoi(counts: number[], handCount: number): boolean {
  if (handCount !== 14) return false
  return counts.every(count => count === 0 || count === 2)
}

function checkHead(counts: number[], top: number, count: number): boolean {
  if (count < 2) return false
  while (top < 34 && counts[top] === 0) top++
  if (top >= 34) return false

  if (counts[top] >= 3) {
    counts[top] -= 3
    const triplet = checkHead(counts, top, count - 3)
    counts[top] += 3
    if (triplet) return true
    counts[top] -= 2
    const pair = checkMent(counts, top, count - 2)
    counts[top] += 2
    return pair
  }
  if (counts[top] === 2) {
    counts[top] -= 2
    let result = checkMent(counts, top + 1, count - 2)
    counts[top] += 2
    if (result) return true
    if (top < 27 && top % 9 < 7 && counts[top + 1] >= 2 && counts[top + 2] >= 2) {
      counts[top] -= 2
      counts[top + 1] -= 2
      counts[top + 2] -= 2
      result = checkHead(counts, top + 1, count - 6)
      counts[top] += 2
      counts[top + 1] += 2
      counts[top + 2] += 2
      return result
    }
    return false
  }
  if (top < 27 && top % 9 < 7 && counts[top + 1] >= 1 && counts[top + 2] >= 1) {
    counts[top]--
    counts[top + 1]--
    counts[top + 2]--
    const result = checkHead(counts, top, count - 3)
    counts[top]++
    counts[top + 1]++
    counts[top + 2]++
    return result
  }
  return false
}

function checkMent(counts: number[], top: number, count: number): boolean {
  if (count === 0) return true
  while (top < 34 && counts[top] === 0) top++
  if (top >= 34) return false
  if (counts[top] === 3) {
    counts[top] -= 3
    const result = checkMent(counts, top + 1, count - 3)
    counts[top] += 3
    return result
  }
  if (top >= 27 || top % 9 >= 7) return false
  if (counts[top] === 4) {
    if (counts[top + 1] <= 0 || counts[top + 2] <= 0) return false
    counts[top] -= 4
    counts[top + 1]--
    counts[top + 2]--
    const result = checkMent(counts, top + 1, count - 6)
    counts[top] += 4
    counts[top + 1]++
    counts[top + 2]++
    return result
  }
  if (counts[top] === 2) {
    if (counts[top + 1] < 2 || counts[top + 2] < 2) return false
    counts[top] -= 2
    counts[top + 1] -= 2
    counts[top + 2] -= 2
    const result = checkMent(counts, top + 1, count - 6)
    counts[top] += 2
    counts[top + 1] += 2
    counts[top + 2] += 2
    return result
  }
  if (counts[top + 1] > 0 && counts[top + 2] > 0) {
    counts[top]--
    counts[top + 1]--
    counts[top + 2]--
    const result = checkMent(counts, top + 1, count - 3)
    counts[top]++
    counts[top + 1]++
    counts[top + 2]++
    return result
  }
  return false
}

/* ========================================================================
 * ゲームステート
 * ======================================================================== */
interface TileState {
  code: number
  bipaiIndex?: number
  isSelected: boolean
}

interface WaitTileGuideEntry {
  code: number
  rest: number
  furiten: boolean
  han?: number
  noYaku?: boolean
  isYakuman?: boolean
}

interface WaitGuidePreviewResponse {
  discardCode: number
  waits: Array<{
    serial: number
    han: number
    noYaku: boolean
    isYakuman: boolean
  }>
}

interface TrainingDiscardRecommendationResponse {
  actionSeq: number
  bipaiIndex: number
  shouldRiichi: boolean
}

interface DiscardState {
  code: number
  bipaiIndex?: number
  isReach: boolean
}

interface DiscardFlightOrigin {
  x: number
  y: number
  scaleX: number
  scaleY: number
  depth: number
}

interface DiscardSourceMarkerState {
  isTedashi: boolean
  displayIdx: number
  handCount: number
}

interface PendingDiscardState {
  odr: number
  bipaiIndex: number
  isReach: boolean
  playReachFeedback: boolean
  flightOrigin?: DiscardFlightOrigin
  animateDiscard: boolean
}

interface ActiveDiscardFlight {
  sprite: Phaser.GameObjects.Image
  target: Phaser.GameObjects.Image
  tween?: Phaser.Tweens.Tween
}

interface ActionButtonControl {
  background: Phaser.GameObjects.Rectangle
  sprite: Phaser.GameObjects.Sprite
}


interface PaiInfoMsgState {
  bIniKyo: boolean
  openPos: number
  tiles: TileState[]
}

interface ResyncHandSnapshot {
  openPos: number
  handTiles: TileState[]
  visibleTiles: TileState[]
}

interface ActionPromptState {
  actionSeq: number
  deadlineAt: number
  localDeadlineAt: number
  seatOrder: number
  playerMode: string
  promptSerial: number
  baseTimeMs: number
  keepTimeMs: number
  timeBankMs: number
  timeBankEnabled: boolean
}

interface MeldTileState {
  code: number
  flag: number
  isDown?: boolean
}

interface MeldState {
  action: Act
  tiles: MeldTileState[]
}

interface PaifuGraphDrawState {
  code: number
  small?: boolean
  noteFrame?: number
}

interface HoraYakuState {
  name: string
  fan: number
  code?: number
  isYakuman?: boolean
  tip?: number
}

interface PlayerHoraState {
  pinType: number
  isHora: boolean
  isHoju: boolean
  isTempai: boolean
  isRichi: boolean
  yaku: HoraYakuState[]
  totalFu?: number
  totalFan?: number
  totalTen?: number
  tipBal?: number
}

interface PaifuGraphRoundState {
  kyokuCnt: number
  left: number
  ribo: number
  renchan: number
  dice: number[]
  waremeOdr: number
  roomOption: string
  dora: number[]
  uraDora: number[]
}

interface PlayerState {
  hand: TileState[]       // 手牌
  discards: DiscardState[] // 捨て牌
  melds: MeldState[]      // 副露
  flowers: TileState[]    // 花牌
  isReach: boolean
  reachDiscardCarry: boolean
  score: number
  yakitori: boolean
  tip: number
  hora?: PlayerHoraState
  pix: string
  name: string
  level: string
  rating?: number
  avatarId?: string
  avatarUrl?: string
  fallbackAvatarUrl?: string
  majakTitle?: number
  trickTitle?: number
  richiEffect?: number
  customCostume?: number
  customCostumeType?: number
  sex?: string
  isHost?: boolean
  isProxy?: boolean
}

function createEmptyPlayerState(): PlayerState {
  return {
    hand: [], discards: [], melds: [], flowers: [], isReach: false, reachDiscardCarry: false,
    score: Number.NaN, yakitori: false, tip: 0, pix: '', name: '', level: '', isProxy: false,
  }
}

function readTitleCode(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0
  const text = String(value ?? '')
  if (!text) return 0
  const direct = Number(text)
  if (Number.isFinite(direct)) return direct
  const match = text.match(/mjk([tsc])(\d{3})/i)
  if (!match) return 0
  const code = Number(match[2])
  return match[1].toLowerCase() === 'c' ? code + 1000 : code
}

function readOptionalTitleCode(value: unknown): number | undefined {
  return value === undefined || value === null || value === '' ? undefined : readTitleCode(value)
}

function isViewerPacket(data: Record<string, unknown>) {
  const playerType = data.k57e ?? data.playerType ?? data.type
  return playerType === 'viewer' || playerType === 'v5e' || playerType === 2 || playerType === '2'
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object'
}

function asFiniteNumber(value: unknown): number | undefined {
  const number = Number(value)
  return Number.isFinite(number) ? number : undefined
}

function asBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return Number.isFinite(value) ? value !== 0 : undefined
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'true' || normalized === '1') return true
    if (normalized === 'false' || normalized === '0') return false
  }
  return undefined
}

/* ========================================================================
 * GameScene
 * ======================================================================== */
export default class GameScene extends Phaser.Scene {
  /* Phaser オブジェクト */
  private handSprites: Phaser.GameObjects.Image[][] = [[], [], [], []]
  private initialDealInProgress = false
  private initialDealVisibleCounts = [0, 0, 0, 0]
  private initialDealSerial = 0
  private initialActionPresentationReady = false
  private deferInitialActionPresentation = false
  private actionTimerAwaitingInitialDeal = false
  private mobileOpponentHandCountTexts: Array<Phaser.GameObjects.Text | undefined> = [undefined, undefined, undefined, undefined]
  private responsiveLocalHandOffsetY = 0
  private suteSprites: Phaser.GameObjects.Image[][] = [[], [], [], []]
  private meldSprites: Phaser.GameObjects.Image[][] = [[], [], [], []]
  private deadWallSprites: Phaser.GameObjects.Image[] = []
  private actionPanelBounds?: Phaser.Geom.Rectangle
  private actionPanelFrame?: Phaser.GameObjects.Rectangle
  private actionButtonSprites = new Map<string, ActionButtonControl>()
  private mobileActionButtonsVisible = false
  private horaErrorText?: Phaser.GameObjects.Text
  private boardEffectSprites: Phaser.GameObjects.Image[] = []
  private legacyEffectSprites: Phaser.GameObjects.Image[] = []
  private legacyBackdrop?: Phaser.GameObjects.GameObject
  private reachTileEffectSprites: Phaser.GameObjects.Image[] = []
  private hoverCursor?: Phaser.GameObjects.Image | Phaser.GameObjects.Graphics
  private selectedCursor?: Phaser.GameObjects.Image | Phaser.GameObjects.Graphics
  private drawnTileCursor?: Phaser.GameObjects.Image
  private tenpaiMarkerSprites: Phaser.GameObjects.Image[] = []
  private trainingRecommendationMarker?: Phaser.GameObjects.Image
  private trainingRecommendationTween?: Phaser.Tweens.Tween
  private assistHighlightSprites: Phaser.GameObjects.Image[] = []
  private discardSourceMarkerSprites: Array<Phaser.GameObjects.Image | undefined> = [undefined, undefined, undefined, undefined]
  private latestDiscardFrame?: Phaser.GameObjects.Image
  private waitTileGuideContainer?: Phaser.GameObjects.Container
  private actionBtns: Phaser.GameObjects.Sprite[] = []
  private paifuGraphLayer?: Phaser.GameObjects.Container
  private paifuGraphBody?: Phaser.GameObjects.Container
  private paifuGraphObjects: Phaser.GameObjects.GameObject[] = []
  private boardMaskGraphics?: Phaser.GameObjects.Graphics
  private boardMask?: Phaser.Display.Masks.GeometryMask
  private themeBoardBackground?: Phaser.GameObjects.Rectangle
  private dragonOverlayBg?: Phaser.GameObjects.Image
  private centerInfoBg?: Phaser.GameObjects.Image
  private themeCenterInfoBackground?: Phaser.GameObjects.Rectangle
  private paifuGraphInitialHands: TileState[][] = [[], [], [], []]
  private paifuGraphDraws: PaifuGraphDrawState[][] = [[], [], [], []]
  private paifuGraphDiscards: DiscardState[][] = [[], [], [], []]
  private paifuGraphRound: PaifuGraphRoundState = {
    kyokuCnt: 0, left: 70, ribo: 0, renchan: 0, dice: [], waremeOdr: -1, roomOption: '', dora: [], uraDora: [],
  }
  private actionOfferByName = new Map<string, { code: Act; bipaiIndex: number[] }>()
  private actionChoicesByName = new Map<string, Array<{ code: Act; bipaiIndex: number[] }>>()
  private pendingActionChoice: { def: { act: string; code: Act }; acts: string[]; choices: Array<{ code: Act; bipaiIndex: number[] }> } | null = null
  private currentHoraErrorReason = ''
  private selectedIdx = -1
  private selectedDiscardBipaiIndex?: number
  private touchConfirmDiscardIdx = -1
  private activeAssistTileCode = 0
  private activeAssistHandIdx = -1
  private canDiscardOnTileClick = false
  private autoDiscardTimer?: Phaser.Time.TimerEvent
  private actionResponseTimer?: Phaser.Time.TimerEvent
  private autoControlTimer?: Phaser.Time.TimerEvent
  private timeWarningTimers: Phaser.Time.TimerEvent[] = []
  private currentActionSeatOrder: number | null = null
  private actionPromptSerial = 0
  private flowTraceSerial = 0
  private actionSendInFlight = false
  private gameResyncInFlight = false
  private pendingMatchStartSeatReveal = false
  private gameRestorePending = false
  private gameResyncInvokeResolved = false
  private gameResyncSnapshotReceived = false
  private gameResyncHistoryReceived = false
  private gameResyncHistoryApplied = false
  private deferInitialDeadWallRedraw = false
  private pendingAction: { seatOrder: number; action: number; actionSeq?: number } | null = null
  private currentActionPrompt: ActionPromptState | null = null
  private readonly kyoResultPendingOrders = new Set<number>()
  private timeBankExtensionInFlight = false
  private kyokuTimeFullMs = 0
  private keyboardActionIndex = -1
  private keyboardHandler?: (event: KeyboardEvent) => void
  private contextMenuHandler?: (event: MouseEvent) => void
  private readonly knownPai = new Map<number, number>()
  private readonly pendingDiscardsByBipaiIndex = new Map<number, PendingDiscardState>()
  private activeDiscardFlights: Array<ActiveDiscardFlight | undefined> = [undefined, undefined, undefined, undefined]
  private lastDiscardOdr: number | null = null
  private lastRonSource?: { x: number; y: number; width: number; height: number; isReach: boolean }
  private readonly appliedActionKeys = new Set<string>()
  private paiInfoQueue: PaiInfoMsgState[] = []
  private pendingResyncHandSnapshot?: ResyncHandSnapshot
  private latestActionPaiInfoTiles: TileState[][] = [[], [], [], []]
  private discardSourceMarkers: Array<DiscardSourceMarkerState | undefined> = [undefined, undefined, undefined, undefined]
  private discardAfterCall = [false, false, false, false]
  private claimedDiscardCounts = [0, 0, 0, 0]
  private waitGuideRequestSerial = 0
  private trainingRecommendationRequestSerial = 0
  private readonly waitGuidePreviewCache = new Map<string, Promise<WaitGuidePreviewResponse | null>>()
  private currentActionOffers: string[] = []

  /* ゲームステート */
  private players: PlayerState[] = Array.from({ length: 4 }, createEmptyPlayerState)
  private myOdr = 0   // 自分の順番 (0=南, 1=西, 2=北, 3=東)
  private roomPosToOdr: number[] = [0, 1, 2, 3]
  private memberOdrById = new Map<string, number>()
  private hanchanOrderInitialized = false
  private chicha = 0
  // currentOdr: 将来ターン演出に使用
  private roomId = ''
  private isMeldLayoutFixture = false
  private fixturePreviewApplied = false
  private isReplay = false
  private isViewer = false
  private viewerHistorySyncPending = false
  private layoutMode: IngameLayoutMode = 'desktop'
  private customHaiId = 0
  private usesHighDensityHaiTextures = false
  private replayPaifuData: unknown
  private replayPaifuApplied = false
  private isReplayApplyingHistory = false
  private skipInitialRoomEnter = false
  private requestInitialGameResync = false
  private trainingRecommendations = false
  private replayHandOpen = false
  private signalRHandlers: Array<{ cmd: string; handler: SignalR.MessageHandler }> = []
  private acceptingSignalR = false
  private replayRotateHandler?: EventListener
  private replayHandOpenHandler?: EventListener
  private replayGraphHandler?: EventListener
  private replayPacketHandler?: EventListener
  private autoControlHandler?: EventListener
  private autoPassHoldHandler?: EventListener
  private assistConfigHandler?: EventListener
  private paifuConfigHandler?: EventListener
  private paifuConnectionLostHandler?: () => void
  private paifuRecordMode = 0
  private paifuRoomName = ''
  private paifuNeedsReconnectSeed = false
  private autoControl: AutoControlState = { prox: false, autoTap: false, autoPass: false, autoHora: false }
  private inputConfig = { nSelPasKey: 0 }
  private assistConfig: GameAssistConfig = { ...DEFAULT_GAME_ASSIST_CONFIG }
  private customBgId = 0
  private customBoardType = 0
  private themeBoardTint: number | undefined
  private themeUiTint = 0x1b6b55
  private gemGame = 0
  private currentBgmSkinId: number | undefined
  private currentRoundUsesTengokuBgm = false
  private currentRoundIsCarnival = false
  private mobileHandSummaryStateKey = ''
  private mobileCenterInfoLayoutKey = ''
  private responsiveActionPanelOffsetY = 0
  private deadWallScaleOverride?: 'topHand'

  constructor() {
    super({ key: 'GameScene' })
  }

  private tileScale(): number {
    return this.layoutMode === 'mobileLandscape' ? MOBILE_TILE_SCALE * mobileContentScale() : 1
  }

  private tileRenderScale(scale: number): number {
    return this.usesHighDensityHaiTextures ? scale / 2 : scale
  }

  private handTileScale(odr: number, loc: 0 | 1 | 2 | 3): number {
    if (this.layoutMode !== 'mobileLandscape') return 1
    void odr
    const baseScale = loc === 0 ? MOBILE_SELF_HAND_TILE_SCALE : MOBILE_OTHER_HAND_TILE_SCALE
    return baseScale * mobileContentScale()
  }

  private meldTileScale(odr: number, loc: 0 | 1 | 2 | 3): number {
    void odr
    void loc
    return this.tileScale()
  }

  private deadWallTileScale(): number {
    return this.deadWallScaleOverride === 'topHand'
      ? this.handTileScale(0, 2)
      : this.tileScale()
  }

  private discardTileScale(): number {
    return this.tileScale()
  }

  private shouldUseMobileOpponentHandSummary(odr: number, loc: 0 | 1 | 2 | 3): boolean {
    void odr
    void loc
    return false
  }

  private soundSkinOptions(skinId = this.currentBgmSkinId): { skinId?: number } {
    return skinId ? { skinId } : {}
  }

  init(data: CreateGameOptions & { roomId?: string; myOdr?: number }) {
    this.layoutMode = data.layoutMode ?? 'desktop'
    applyIngameLayout(this.layoutMode)
    this.mobileHandSummaryStateKey = ''
    this.mobileCenterInfoLayoutKey = ''
    this.responsiveActionPanelOffsetY = 0
    this.initialActionPresentationReady = false
    this.deferInitialActionPresentation = true
    this.actionTimerAwaitingInitialDeal = false
    this.deadWallScaleOverride = data.deadWallScale
    this.roomId = data.roomId ?? ''
    this.isMeldLayoutFixture = this.roomId === 'meld-layout-fixture'
    this.fixturePreviewApplied = false
    this.paifuRoomName = data.roomName ?? this.roomId
    this.myOdr  = data.myOdr  ?? 0
    this.roomPosToOdr = [0, 1, 2, 3]
    this.memberOdrById.clear()
    this.hanchanOrderInitialized = false
    this.isReplay = data.mode === 'replay'
    this.isViewer = Boolean(data.isViewer)
    this.viewerHistorySyncPending = this.isViewer && !this.isReplay
    this.customBgId = Number(data.customBgId ?? 0)
    this.customBoardType = Number(data.customBoardType ?? 0)
    this.themeUiTint = themeColorToTint(data.themeUiColor ?? activeThemeBoardColor()) ?? 0x1b6b55
    this.themeBoardTint = getLegacyBoardSkinId(this.customBgId, this.customBoardType) == null
      ? themeColorToTint(data.themeBoardColor ?? activeThemeBoardColor())
      : undefined
    this.customHaiId = Number(data.customHaiId ?? 0)
    this.usesHighDensityHaiTextures = true
    this.gemGame = Number(data.gemGame ?? 0)
    this.replayPaifuData = data.paifu
    this.replayPaifuApplied = false
    this.isReplayApplyingHistory = false
    this.pendingResyncHandSnapshot = undefined
    this.skipInitialRoomEnter = Boolean(data.skipInitialRoomEnter)
    this.requestInitialGameResync = Boolean(data.requestInitialGameResync)
    this.trainingRecommendations = Boolean(data.trainingRecommendations)
    this.gameResyncInFlight = false
    this.gameRestorePending = this.requestInitialGameResync && !this.isReplay
    this.gameResyncInvokeResolved = false
    this.gameResyncSnapshotReceived = false
    this.gameResyncHistoryReceived = false
    this.gameResyncHistoryApplied = false
    this.inputConfig = { nSelPasKey: data.inputConfig?.nSelPasKey === 1 ? 1 : 0 }
    this.assistConfig = toGameAssistConfig(data.assistConfig)
    this.paifuRecordMode = Number(data.recordPaifuMode ?? 0)
    this.paifuNeedsReconnectSeed = false
    if (DEBUG_GAME) console.info('[GameScene] init', {
      roomId: this.roomId,
      myOdr: this.myOdr,
      mode: data.mode,
      layoutMode: this.layoutMode,
      skipInitialRoomEnter: this.skipInitialRoomEnter,
      requestInitialGameResync: this.requestInitialGameResync,
      nSelPasKey: this.inputConfig.nSelPasKey,
    })
    if (Array.isArray(data.players)) {
      data.players.forEach((player, odr) => this.mergePlayerInfoAtOdr(odr, player))
    }
    this.paifuGraphRound.roomOption = data.roomOption ?? this.paifuGraphRound.roomOption
    if (!this.isReplay) {
      beginPaifuRecording({
        mode: this.paifuRecordMode,
        isViewer: this.isViewer,
        roomId: this.roomId,
        roomName: this.paifuRoomName,
        roomOption: this.paifuGraphRound.roomOption,
        members: this.players.map(player => ({ name: player.name })),
      })
    }
  }

  create() {
    const sceneCreatedAt = performance.now()
    this.registry.set('majak:gameStartSceneCreatedAt', sceneCreatedAt)
    console.info('[GameStartTiming] GameScene create started', {
      roomId: this.roomId,
      createdAt: new Date().toISOString(),
    })
    if (!this.isReplay) emitGameLoadProgress('scene')
    if (this.skipInitialRoomEnter) {
      console.info('[GameReconnect] GameScene create entered', {
        roomId: this.roomId,
        acceptingSignalR: this.acceptingSignalR,
        restorePending: this.gameRestorePending,
      })
    }
    this.createBoardMask()

    this.themeBoardBackground = this.clipToBoard(this.add.rectangle(
      BOARD_X + BOARD_W / 2,
      BOARD_Y + BOARD_H / 2,
      BOARD_W,
      BOARD_H,
      this.themeBoardTint ?? 0x176b2a,
    ).setOrigin(0.5, 0.5).setDepth(-100))
    if (this.textures.exists('mj_taku_dragon_skin')) {
      this.dragonOverlayBg = this.clipToBoard(this.add.image(BOARD_X + DRAGON_OVERLAY.x, BOARD_Y + DRAGON_OVERLAY.y, 'mj_taku_dragon_skin')
        .setOrigin(0, 0)
        .setDepth(-90)
        .setBlendMode(Phaser.BlendModes.ADD))
    }

    /* ── サイドバー mj_sideBg.png (225×704) at (794,31) ── */
    if (this.layoutMode !== 'responsiveDesktop') {
      const sideBackground = this.add.image(SIDE_PANEL.x + SIDE_PANEL.width / 2, SIDE_PANEL.y + SIDE_PANEL.height / 2, this.resolveSkinTextureKey('mj_sideBg')).setDepth(-100)
      if (this.themeBoardTint != null) {
        sideBackground.setVisible(false)
        this.add.rectangle(SIDE_PANEL.x + SIDE_PANEL.width / 2, SIDE_PANEL.y + SIDE_PANEL.height / 2, SIDE_PANEL.width, SIDE_PANEL.height, this.themeBoardTint)
          .setOrigin(0.5, 0.5)
          .setDepth(-99)
      }
    }

    /* ── ゲーム情報エリア mj_h_bg.png (265×161) at board-local (262,275) ── */
    const centerInfoTexture = this.resolveSkinTextureKey('mj_h_bg')
    const centerInfoDisplayTexture = this.themeBoardTint == null
      ? centerInfoTexture
      : this.createTransparentCenterInfoTexture(centerInfoTexture)
    this.centerInfoBg = this.clipToBoard(this.add.image(BOARD_X + CENTER_INFO.x + CENTER_INFO.width / 2, BOARD_Y + CENTER_INFO.y + CENTER_INFO.height / 2, centerInfoDisplayTexture).setDepth(-50))
    if (this.themeBoardTint != null) {
      this.themeCenterInfoBackground = this.clipToBoard(this.add.rectangle(BOARD_X + CENTER_INFO.x + CENTER_INFO.width / 2, BOARD_Y + CENTER_INFO.y + CENTER_INFO.height / 2, CENTER_INFO.width, CENTER_INFO.height, darkenThemeTint(this.themeBoardTint))
        .setOrigin(0.5, 0.5)
        .setDepth(-51))
    }
    this.updateCenterInfoLayout()

    if (!this.isReplay) {
      this.updateActionPanelBounds()
      if (!this.isViewer) this.createActionButtons()
    }

    /* ── UIScene 起動 ── */
    this.events.once('uiSceneReady', () => {
      this.events.emit('stateUpdate', { players: this.players, viewOdr: this.myOdr })
    })
    this.scene.launch('UIScene', { gameScene: this, myOdr: this.myOdr, layoutMode: this.layoutMode, isViewer: this.isViewer, isReplay: this.isReplay, customBgId: this.customBgId, customBoardType: this.customBoardType, customHaiId: this.customHaiId, themeBoardColor: this.themeBoardTint == null ? undefined : `#${this.themeBoardTint.toString(16).padStart(6, '0')}`, themeUiColor: `#${this.themeUiTint.toString(16).padStart(6, '0')}` })
    if (this.isMeldLayoutFixture) {
      this.time.delayedCall(300, () => {
        this.showFixtureActionPreview()
      })
    }

    /* ── SignalR イベント登録 ── */
    this.acceptingSignalR = true
    this.setupSignalR()
    if (this.skipInitialRoomEnter) {
      console.info('[GameReconnect] GameScene SignalR handlers registered', {
        roomId: this.roomId,
        handlerCount: this.signalRHandlers.length,
      })
    }
    if (!this.isReplay) {
      if (!this.requestInitialGameResync) this.notifyGameClientReady()
      this.requestInitialRoomState()
    }
    this.setupReplayControlEvents()
    this.setupAutoControlEvents()
    this.setupAssistConfigEvents()
    this.setupPaifuRecordingEvents()
    this.setupKeyboardEvents()
    this.setupContextMenuEvents()
    if (this.isReplay) window.dispatchEvent(new Event(PAIFU_REPLAY_READY_EVENT))
    this.input.on(Phaser.Input.Events.POINTER_DOWN, this.onScenePointerDown, this)
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardownSceneResources())
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardownSceneResources())
  }

  private teardownSceneResources() {
    this.acceptingSignalR = false
    this.clearAllDiscardFlights()
    this.teardownSignalR()
    this.teardownReplayPacketEvents()
    this.teardownReplayControlEvents()
    this.teardownAutoControlEvents()
    this.teardownAssistConfigEvents()
    this.teardownPaifuRecordingEvents()
    this.teardownKeyboardEvents()
    this.teardownContextMenuEvents()
    this.input.off(Phaser.Input.Events.POINTER_DOWN, this.onScenePointerDown, this)
    this.clearAssistHighlights()
    this.clearTrainingRecommendation()
    this.boardMaskGraphics?.destroy()
    this.boardMaskGraphics = undefined
    this.boardMask = undefined
    if (!this.isReplay) cancelPaifuRecording()
  }

  private createBoardMask() {
    const graphics = this.boardMaskGraphics ?? this.make.graphics({ x: 0, y: 0 })
    graphics.clear()
    graphics.fillStyle(0xffffff)
    const responsiveBounds = responsiveDesktopVisibleWorldBounds()
    const maskX = this.layoutMode === 'responsiveDesktop' && responsiveBounds
      ? responsiveBounds.left
      : isMobileIngameLayout(this.layoutMode) ? 0 : BOARD_X
    const maskY = this.layoutMode === 'responsiveDesktop' && responsiveBounds ? responsiveBounds.top : BOARD_Y
    const maskW = this.layoutMode === 'responsiveDesktop' && responsiveBounds
      ? responsiveBounds.right - responsiveBounds.left
      : isMobileIngameLayout(this.layoutMode) ? BOARD_X + BOARD_W : BOARD_W
    const maskH = this.layoutMode === 'responsiveDesktop' && responsiveBounds
      ? responsiveBounds.bottom - responsiveBounds.top
      : BOARD_H
    graphics.fillRect(maskX, maskY, maskW, maskH)
    this.boardMaskGraphics = graphics
    this.boardMask ??= graphics.createGeometryMask()
  }

  private clipToBoard<T extends Phaser.GameObjects.GameObject>(obj: T): T {
    if (this.boardMask) {
      (obj as T & { setMask: (mask: Phaser.Display.Masks.GeometryMask) => T }).setMask(this.boardMask)
    }
    return obj
  }

  private createTransparentCenterInfoTexture(sourceKey: string): string {
    const textureKey = `${sourceKey}_theme_transparent`
    const sourceImage = this.textures.get(sourceKey).getSourceImage() as HTMLImageElement
    const existingTexture = this.textures.exists(textureKey)
      ? this.textures.get(textureKey) as Phaser.Textures.CanvasTexture | null
      : null
    const canvasTexture = existingTexture
      ?? this.textures.createCanvas(textureKey, sourceImage.width, sourceImage.height)
    if (!canvasTexture) return sourceKey
    const context = canvasTexture.context
    context.clearRect(0, 0, sourceImage.width, sourceImage.height)
    context.drawImage(sourceImage, 0, 0)

    const imageData = context.getImageData(0, 0, sourceImage.width, sourceImage.height)
    for (let index = 0; index < imageData.data.length; index += 4) {
      const red = imageData.data[index]
      const green = imageData.data[index + 1]
      const blue = imageData.data[index + 2]
      if (green > 36 && green > red * 1.15 && green > blue * 1.15) imageData.data[index + 3] = 0
    }
    context.putImageData(imageData, 0, 0)
    canvasTexture.refresh()
    return textureKey
  }

  private updateCenterInfoLayout() {
    const offset = this.layoutMode === 'responsiveDesktop'
      ? responsiveDesktopCenterOffset(this.layoutMode)
      : mobileCenterHudOffset(this.layoutMode)
    const centerX = BOARD_X + CENTER_INFO.x + CENTER_INFO.width / 2 + offset.x
    const centerY = BOARD_Y + CENTER_INFO.y + CENTER_INFO.height / 2 + offset.y
    if (this.themeBoardBackground) {
      const visibleBounds = this.layoutMode === 'responsiveDesktop'
        ? responsiveDesktopVisibleWorldBounds()
        : isMobileIngameLayout(this.layoutMode) ? mobileVisibleWorldBounds() : null
      const backgroundScale = visibleBounds
        ? Math.max(
            FLUID_BOARD_BACKGROUND_SCALE,
            (visibleBounds.right - visibleBounds.left) / BOARD_BACKGROUND_SAFE_WIDTH,
            (visibleBounds.bottom - visibleBounds.top) / BOARD_BACKGROUND_SAFE_HEIGHT,
          )
        : this.layoutMode === 'desktop' ? 1 : FLUID_BOARD_BACKGROUND_SCALE
      this.themeBoardBackground
        .setPosition(
          isMobileIngameLayout(this.layoutMode) ? centerX : BOARD_X + BOARD_W / 2 + offset.x,
          isMobileIngameLayout(this.layoutMode) ? centerY : BOARD_Y + BOARD_H / 2 + offset.y,
        )
        .setDisplaySize(BOARD_W * backgroundScale, BOARD_H * backgroundScale)
    }
    if (this.dragonOverlayBg) {
      if (isMobileIngameLayout(this.layoutMode)) {
        this.dragonOverlayBg
          .setOrigin(0.5, 0.5)
          .setScale(0.25)
          .setDepth(-90)
          .setPosition(centerX, centerY)
      } else {
        this.dragonOverlayBg
          .setOrigin(0, 0)
          .setScale(1)
          .setPosition(BOARD_X + DRAGON_OVERLAY.x + offset.x, BOARD_Y + DRAGON_OVERLAY.y + offset.y)
      }
    }
    if (!this.centerInfoBg) return
    this.centerInfoBg.setPosition(centerX, centerY)
    this.themeCenterInfoBackground?.setPosition(centerX, centerY)
    this.updateActionPanelBounds()
    this.updateActionButtonPositions(new Set(this.currentActionOffers))
  }

  /* ======================================================================
   * SignalR イベント
   * ====================================================================== */
  private setupSignalR() {
    this.teardownSignalR()

    /* smmc4e — 牌情報リスト (レガシー SendPaiInfo / ProcessCommand_PaiInfoList) */
    const handlePaiInfo: SignalR.MessageHandler = data => {
      const canHandle = this.canHandleSignalR()
      const isResyncSnapshot = Boolean(data.resyncSnapshot)
      if (this.gameRestorePending || isResyncSnapshot) {
        console.info('[GameReconnect] smmc4e handler entered', {
          roomId: this.roomId,
          canHandle,
          restorePending: this.gameRestorePending,
          isResyncSnapshot,
          openPos: data.openPos,
          isInit: data.bInit ?? data.init,
          paiCount: Array.isArray(data.pai) ? data.pai.length : data.paiCount,
          hasCurrentHand: Array.isArray(data.currentHand),
        })
      }
      if (!canHandle) {
        if (this.gameRestorePending || isResyncSnapshot) {
          console.warn('[GameReconnect] smmc4e ignored because GameScene cannot handle SignalR', {
            roomId: this.roomId,
            acceptingSignalR: this.acceptingSignalR,
          })
        }
        return
        }
      if (!this.isReplayApplyingHistory && !isResyncSnapshot) recordPaifuPacket('smmc4e', data)
      const openPos = Number(data.openPos ?? this.myOdr)
      const isPlayerOpenPos = openPos >= 0 && (openPos < this.players.length || openPos === VIEWER_OPEN_POS)
      const isInit = Boolean(data.bInit ?? data.init)
      const pai = Array.isArray(data.pai) ? data.pai as Array<Record<string, unknown>> : []
      emitGameLoadProgress('tiles', { isInit, isResyncSnapshot, paiCount: pai.length })
      if (this.gameRestorePending && isResyncSnapshot) {
        this.gameResyncSnapshotReceived = true
        this.logResyncProbe('authoritative smmc4e snapshot accepted', {
          openPos,
        isInit,
        paiCount: pai.length,
          currentHandCount: Array.isArray(data.currentHand) ? data.currentHand.length : null,
        })
      }
      if (DEBUG_GAME) console.info('[GameScene] smmc4e PaiInfo', {
        openPos,
        isInit,
        paiCount: pai.length,
        previousMyOdr: this.myOdr,
      })
      const tiles = pai.map(p => {
        const code = Number(p.code ?? 0)
        const bipaiIndex = Number(p.idx ?? p.bipaiIndex ?? -1)
        return { code, bipaiIndex: bipaiIndex >= 0 ? bipaiIndex : undefined, isSelected: false }
      })
      tiles.forEach(tile => {
        if (tile.bipaiIndex !== undefined && tile.bipaiIndex >= 0) this.knownPai.set(tile.bipaiIndex, tile.code)
      })
      const currentHand = (Array.isArray(data.currentHand) ? data.currentHand as Array<Record<string, unknown>> : []).map(p => {
        const code = Number(p.code ?? 0)
        const bipaiIndex = Number(p.idx ?? p.bipaiIndex ?? -1)
        return { code, bipaiIndex: bipaiIndex >= 0 ? bipaiIndex : undefined, isSelected: false }
      })
      this.logDiscardProbe('recv smmc4e', {
        openPos,
        isInit,
        paiCount: tiles.length,
        sample: tiles.slice(0, 16).map(tile => ({ idx: tile.bipaiIndex, code: tile.code })),
        queueLengthBeforePush: this.paiInfoQueue.length,
        pendingDiscardIndexes: [...this.pendingDiscardsByBipaiIndex.keys()],
      })

      if (isResyncSnapshot && openPos >= 0 && openPos < this.players.length && currentHand.length > 0) {
        tiles.forEach(tile => {
          if (tile.bipaiIndex !== undefined && tile.bipaiIndex >= 0) this.knownPai.set(tile.bipaiIndex, tile.code)
        })
        this.paiInfoQueue = []
        this.pendingDiscardsByBipaiIndex.clear()
        this.pendingResyncHandSnapshot = {
          openPos,
          handTiles: currentHand,
          visibleTiles: tiles,
        }
        this.applyResyncHandSnapshot(this.pendingResyncHandSnapshot)
        this.tryCompleteGameResync('authoritative-snapshot-applied')
        return
      }

      if (isPlayerOpenPos && tiles.length > 0) this.pushPaiInfo(isInit, openPos, tiles)
      if (!isInit && this.pendingDiscardsByBipaiIndex.size > 0) {
        tiles.forEach(tile => {
          if (tile.bipaiIndex !== undefined && tile.bipaiIndex >= 0) this.knownPai.set(tile.bipaiIndex, tile.code)
        })
        this.flushPendingDiscards()
      }
    }
    this.onSignalR('smmc4e', handlePaiInfo)

    this.onSignalR('mjkc4e', data => {
      if (!this.canHandleSignalR()) return
      this.gemGame = Number(data.mjkk56e ?? data.gemGame ?? 0)
    })

    const handleMemberList: SignalR.MessageHandler = data => {
      if (!this.canHandleSignalR()) return
      const count = Number(data.k25e ?? data.count ?? 0)
      const hostPix = String(data.k50e ?? data.roomHost ?? '')
      const legacyMembers = Array.from({ length: count }, (_, index) => ({
        pix: data[`k3e${index}`],
        name: data[`k8e${index}`] ?? data[`mjkk34e${index}`] ?? data[`k3e${index}`],
        avatarId: data[`k7e${index}`],
        sex: data[`k11e${index}`],
        playerType: data[`k57e${index}`],
        playerPos: data[`k58e${index}`],
        seatPos: data[`k58e${index}`],
        rating: data[`k31e${index}`],
        slevel: data[`k32e${index}`],
        majakTitle: data[`mjkk47e${index}`],
        trickTitle: data[`mjkk46e${index}`],
        richiEffect: data[`mjkk54e${index}`] ?? data[`richiEffect${index}`],
        customCostume: data[`mjkk136e${index}`],
        customCostumeType: data[`mjkk137e${index}`],
        isHost: hostPix !== '' && String(data[`k3e${index}`] ?? '') === hostPix,
        isProxy: data[`isProxy${index}`] ?? data[`proxy${index}`] ?? data[`isOutPlayer${index}`],
      })).filter(member => member.pix != null && String(member.pix) !== '')
      const members = Array.isArray(data.members) && data.members.length > 0
        ? data.members as Array<Record<string, unknown>>
        : legacyMembers
      members.filter(member => !isViewerPacket(member)).forEach(member => this.mergePlayerInfo(member))
      if (hostPix) this.applyHostPix(hostPix)
      this.emitToUiScene('stateUpdate', { players: this.players, viewOdr: this.myOdr })
    }
    this.onSignalR('c16e', handleMemberList)

    /* playing — レガシー commandGamePlay */
    const handleGamePlay: SignalR.MessageHandler = data => {
      if (!this.canHandleSignalR()) return
      const playType = String(data.playType ?? '')
      if (!this.isReplayApplyingHistory) recordPaifuPacket('playing', data)
      this.traceGameFlow('rx playing', {
        playType,
        seatOrder: data.seatOrder,
        playerMode: data.playerMode,
        action: data.action,
        actionSeq: data.actionSeq,
        actFlags: data.actFlags,
        actions: data.actions,
        tapCandidates: data.tapCandidates,
      })
      if (DEBUG_GAME) console.info('[GameScene] playing packet', {
        playType,
        myOdr: this.myOdr,
        seatOrder: data.seatOrder,
        playerMode: data.playerMode,
        actFlags: data.actFlags,
        action: data.action,
        actions: data.actions,
        tapCandidates: data.tapCandidates,
      })
      const isInitKyokuPacket = playType === 'MJPID_INIKYO'
      const action = Number(data.action ?? -1)
      const actionSeatOrder = Number(data.seatOrder ?? data.order ?? -1)
      const actionBipaiIndex = Array.isArray(data.bipaiIndex) ? data.bipaiIndex.map(Number) : []
      const isKyoConfirmation = Boolean(data.isKyoConfirmation) || this.kyoResultPendingOrders.has(actionSeatOrder)
      if (playType === 'MJPID_ACTION' && action === Act.Pas && isKyoConfirmation) {
        this.kyoResultPendingOrders.delete(actionSeatOrder)
        if (!Boolean(data.autoConfirmed)) {
          window.dispatchEvent(new CustomEvent(KYO_RESULT_CONFIRMED_EVENT, {
            detail: { seatOrder: actionSeatOrder },
          }))
        }
      }
      const replayTurnActions = new Set([Act.Chi, Act.Pon, Act.Kan, Act.Tap, Act.Ank, Act.Cha, Act.Ric, Act.Tao, Act.Tsu, Act.Hua])
      if (this.isReplay && playType === 'MJPID_ACTION' && replayTurnActions.has(action) && actionSeatOrder >= 0 && actionSeatOrder < this.players.length) {
        this.emitToUiScene('turnChange', { odr: actionSeatOrder, viewOdr: this.myOdr })
      }
      if (playType === 'MJPID_ACTION' && (action === Act.Tap || action === Act.Ric)) {
        this.logDiscardProbe('recv discard ACTION before PaiInfo pop', {
          action,
          actionSeatOrder,
          bipaiIndex: actionBipaiIndex,
          actionSeq: data.actionSeq,
          paiInfoQueue: this.paiInfoQueue.map(msg => ({ ini: msg.bIniKyo, openPos: msg.openPos, count: msg.tiles.length })),
          hand: this.handProbe(actionSeatOrder, actionBipaiIndex[0]),
        })
      }
      if (playType === 'MJPID_ACTION') this.popPaiInfo(false)
      if (playType && playType !== 'MJPID_INIHAN' && !this.viewerHistorySyncPending) {
        this.emitGameSync(false, 'game-content-ready')
      }
      if (playType === 'MJPID_INIHAN') {
        this.kyoResultPendingOrders.clear()
        console.info('[GameStartTiming] MJPID_INIHAN received', {
          roomId: this.roomId,
          elapsedSinceSceneCreateMs: Math.round(performance.now() - Number(this.registry.get('majak:gameStartSceneCreatedAt') ?? performance.now())),
        })
        this.chicha = Number(data.chicha ?? data.nChicha ?? this.chicha)
        this.applyHanchanOrder(data)
        const memberInfo = Array.isArray(data.memberInfo) ? data.memberInfo : []
        memberInfo.forEach((member, odr) => {
          if (!isRecord(member)) return
          this.mergePlayerInfoAtOdr(odr, member)
        })
        this.resolveMyOdrFromPlayers()
        this.pendingMatchStartSeatReveal = this.shouldPlayLegacyVisuals()
        this.emitToUiScene('stateUpdate', { players: this.players, viewOdr: this.myOdr })
        return
      }
      if (isInitKyokuPacket) {
        this.initialActionPresentationReady = false
        this.deferInitialActionPresentation = true
        this.kyoResultPendingOrders.clear()
        console.info('[GameStartTiming] MJPID_INIKYO received', {
          roomId: this.roomId,
          presentationId: data.presentationId,
          elapsedSinceSceneCreateMs: Math.round(performance.now() - Number(this.registry.get('majak:gameStartSceneCreatedAt') ?? performance.now())),
        })
        const isLiveRoundStart = !this.isReplayApplyingHistory
        if (!this.isReplay) window.dispatchEvent(new Event(GAME_KYOKU_STARTED_EVENT))
        this.clearLiveActionState('init kyoku')
        this.kyokuTimeFullMs = Math.max(0, Number(data.timeFullMs ?? 0))
        const kyokuCnt = Number(data.kyokuCnt ?? 0)
        const oyaOrder = ((this.chicha + kyokuCnt) % this.players.length + this.players.length) % this.players.length
        const dice = Array.isArray(data.dice) ? data.dice.map(Number) : []
        this.resetRoundState(false)
        if (this.isReplayApplyingHistory) this.applyPendingResyncVisiblePai()
        const points = Array.isArray(data.memberPoints) ? data.memberPoints as unknown[] : []
        const yakitori = Array.isArray(data.yakitori) ? data.yakitori as unknown[] : []
        const tip = Array.isArray(data.tip) ? data.tip as unknown[] : []
        for (let odr = 0; odr < this.players.length; odr++) {
          if (odr < points.length) this.players[odr].score = Number(points[odr] ?? this.players[odr].score)
          const yakitoriValue = asBoolean(yakitori[odr])
          if (yakitoriValue !== undefined) this.players[odr].yakitori = yakitoriValue
          const tipValue = asFiniteNumber(tip[odr])
          if (tipValue !== undefined) this.players[odr].tip = tipValue
        }
        this.paifuGraphRound = {
          kyokuCnt,
          left: Number(data.leftCount ?? 70),
          ribo: Number(data.riboCnt ?? 0),
          renchan: Number(data.renChanCnt ?? 0),
          dice,
          waremeOdr: Number(data.waremeOdr ?? -1),
          roomOption: String(data.roomOption ?? data.k46e ?? this.paifuGraphRound.roomOption ?? ''),
          dora: [],
          uraDora: [],
        }
        const shouldAnimateRoundStart = this.shouldPlayLegacyVisuals()
        this.deferInitialDeadWallRedraw = shouldAnimateRoundStart
        this.popPaiInfo(true, oyaOrder, dice)
        this.deferInitialDeadWallRedraw = false
        if (!shouldAnimateRoundStart) this.redrawDeadWall()
        this.ensureConcealedOpponentHands()
        const seatRevealDelay = this.animateMatchStartSeatReveal()
        const skillDelay = this.playLegacyLevel1Skills(seatRevealDelay)
        const gemDelay = this.playLegacyGemGame(skillDelay)
        const waremeStartDelay = skillDelay + gemDelay
        const dealStartDelay = shouldAnimateRoundStart
          ? waremeStartDelay + LEGACY_WAREME_PRESENTATION_DURATION_MS
          : 0
        console.info('[GameStartTiming] initial presentation scheduled', {
          roomId: this.roomId,
          presentationId: data.presentationId,
          seatRevealDelayMs: seatRevealDelay,
          skillDelayMs: skillDelay,
          gemDelayMs: gemDelay,
          waremeDelayMs: LEGACY_WAREME_PRESENTATION_DURATION_MS,
          dealDelayMs: 1600,
          estimatedTotalMs: dealStartDelay + 1600,
        })
        this.emitToUiScene('stateUpdate', {
          players: this.players,
          kyoku: this.formatKyoku(kyokuCnt),
          kyokuCnt,
          chicha: this.chicha,
          oyaOrder,
          left: Number(data.leftCount ?? 70),
          ribo: Number(data.riboCnt ?? 0),
          renchan: Number(data.renChanCnt ?? 0),
          dice: dice.length > 0 ? dice : undefined,
          waremeOdr: Number(data.waremeOdr ?? -1),
          viewOdr: this.myOdr,
          timeBankMs: Array.isArray(data.timeBankMs) ? Math.max(0, Number(data.timeBankMs[this.myOdr] ?? 0)) : 0,
          timeFullMs: this.kyokuTimeFullMs,
          roundStart: true,
          roundPresentationDelayMs: waremeStartDelay,
          preserveTurnMark: this.isReplayApplyingHistory && !this.isReplay,
        })
        if (shouldAnimateRoundStart) {
          this.playRoundStartSounds(data, waremeStartDelay)
          this.time.delayedCall(waremeStartDelay + LEGACY_WAREME_PRESENTATION_DURATION_MS, () => this.redrawDeadWall())
        }
        this.animateInitialDeal(oyaOrder, dealStartDelay, () => {
          this.initialActionPresentationReady = true
          this.deferInitialActionPresentation = false
          if (!this.shouldSuppressLivePlayback()) this.playRoundBgm(data, kyokuCnt)
          if (!this.isReplay) {
            this.emitToUiScene('turnChange', {
              odr: Number.isFinite(oyaOrder) ? oyaOrder : 0,
              viewOdr: this.myOdr,
            })
          }
          this.resumeActionPromptTimerAfterInitialDeal()
          if (this.currentActionOffers.length > 0) this.showActionButtons(this.currentActionOffers)
          if (isLiveRoundStart) this.notifyGamePresentationReady(Number(data.presentationId ?? 0))
        })
        if (!this.viewerHistorySyncPending) this.emitGameSync(false, 'initial-kyoku-ready')
        return
      }
      if (playType === 'MJPID_ENDKYO') {
        this.clearTrainingRecommendation()
        this.canDiscardOnTileClick = false
        this.currentActionSeatOrder = null
        this.currentActionPrompt = null
        this.clearAutoDiscardTimer()
        this.clearActionResponseTimer()
        this.clearAutoControlTimer()
        this.clearTimeWarningTimers()
        this.clearActionButtons()
        this.applyKyoResultState(data)
        this.playKyoResultHoraEffect(data)
        this.paifuGraphRound.dora = Array.isArray(data.dora) ? data.dora.map(Number) : []
        this.paifuGraphRound.uraDora = Array.isArray(data.uraDora) ? data.uraDora.map(Number) : []
        this.redrawDeadWall()
        this.redrawPaifuGraphContent()
        this.emitToUiScene('stateUpdate', { players: this.players, viewOdr: this.myOdr })
        this.emitToUiScene('kyoResult', data)
        return
      }

      if (playType === 'MJPID_TIME_BANK_EXTENDED') {
        const prompt = this.currentActionPrompt
        const actionSeq = Number(data.actionSeq ?? 0)
        const seatOrder = Number(data.seatOrder ?? -1)
        if (!prompt || prompt.actionSeq !== actionSeq || prompt.seatOrder !== seatOrder) return
        const serverNow = Number(data.serverNow ?? 0)
        const deadlineAt = Number(data.deadlineAt ?? 0)
        const remainingMs = Math.max(0, deadlineAt - serverNow)
        if (!Number.isFinite(remainingMs) || remainingMs <= 0) return

        this.timeBankExtensionInFlight = false
        this.currentActionPrompt = {
          ...prompt,
          deadlineAt,
          localDeadlineAt: performance.now() + remainingMs,
          baseTimeMs: Math.max(0, Number(data.baseTimeMs ?? prompt.baseTimeMs)),
          keepTimeMs: Math.max(0, Number(data.keepTimeMs ?? prompt.keepTimeMs)),
          timeBankMs: Math.max(0, Number(data.timeBankMs ?? prompt.timeBankMs)),
          timeBankEnabled: true,
        }
        this.clearTimeWarningTimers()
        this.scheduleTimeWarnings(remainingMs, prompt.promptSerial)
        this.emitActionPromptTimerWhenReady(remainingMs)
        return
      }

      const actionOffers = this.extractActionOffers(data)
      if (playType === 'MJPID_ACTIONS') {
        const seatOrder = Number(data.seatOrder ?? this.myOdr)
        const hasActionSeatOrder = Number.isInteger(seatOrder) && seatOrder >= 0 && seatOrder < this.players.length
        const isForLocalPlayer = !this.isViewer && hasActionSeatOrder && seatOrder === this.myOdr
        const actionSeq = Number(data.actionSeq ?? 0)
        const serverNow = Number(data.serverNow ?? 0)
        const deadlineAt = Number(data.deadlineAt ?? 0)
        const timeLimitMs = Math.max(0, Number(data.timeLimit ?? 0) * 1000)
        const remainingMs = Number.isFinite(serverNow) && serverNow > 0 && Number.isFinite(deadlineAt) && deadlineAt > 0
          ? Math.max(0, deadlineAt - serverNow)
          : timeLimitMs
        const playerMode = String(data.playerMode ?? '')
        const baseTimeMs = Math.max(0, Number(data.baseTimeMs ?? timeLimitMs))
        const keepTimeMs = Math.max(0, Number(data.keepTimeMs ?? 0))
        const timeBankMs = Math.max(0, Number(data.timeBankMs ?? 0))
        const timeBankEnabled = Boolean(data.timeBankEnabled)
        console.info('[GameTimer] MJPID_ACTIONS received', {
          seatOrder,
          myOdr: this.myOdr,
          playerMode,
          actionSeq,
          serverNow,
          deadlineAt,
          remainingMs,
          timeLimit: data.timeLimit,
          timeBankMs,
          timeBankEnabled,
        })
        if (playerMode === 'Kyo' && hasActionSeatOrder) {
          this.kyoResultPendingOrders.add(seatOrder)
          window.dispatchEvent(new CustomEvent(KYO_RESULT_PROGRESS_EVENT, {
            detail: {
              seatOrder,
              durationMs: remainingMs,
              localDeadlineAt: performance.now() + remainingMs,
            },
          }))
        }
        const isRepeatedCurrentPrompt = isForLocalPlayer
          && this.currentActionPrompt !== null
          && Number.isFinite(actionSeq)
          && actionSeq > 0
          && this.currentActionPrompt.actionSeq === actionSeq
          && this.currentActionPrompt.seatOrder === seatOrder
          && this.currentActionPrompt.playerMode === playerMode
          && performance.now() < this.currentActionPrompt.localDeadlineAt
        if (isRepeatedCurrentPrompt) {
          this.traceGameFlow('ignore repeated ACTIONS prompt', { seatOrder, actionSeq, playerMode, actionSendInFlight: this.actionSendInFlight })
          return
        }
        if (this.actionSendInFlight && isForLocalPlayer) {
          this.clearPendingAction('local ACTIONS arrived while send pending', { seatOrder, actionSeq, playerMode })
        }
        this.currentActionOffers = actionOffers
        this.currentHoraErrorReason = isForLocalPlayer ? String(data.horaErrorReason ?? '') : ''
        if (this.currentHoraErrorReason) this.emitHoraErrorStatus(this.currentHoraErrorReason)
        const isTurnMode = playerMode === 'Turn'
        if (isForLocalPlayer) this.actionPromptSerial++
        const actionPromptSerial = this.actionPromptSerial
        this.currentActionPrompt = isForLocalPlayer && Number.isFinite(actionSeq) && actionSeq > 0 && remainingMs > 0
          ? {
              actionSeq,
              deadlineAt,
              localDeadlineAt: performance.now() + remainingMs,
              seatOrder,
              playerMode,
              promptSerial: actionPromptSerial,
              baseTimeMs,
              keepTimeMs,
              timeBankMs,
              timeBankEnabled,
            }
          : null
        if (isForLocalPlayer && this.currentActionPrompt === null) {
          this.expireCurrentActionPrompt('invalid or expired ACTIONS prompt')
          this.requestGameResync('invalid-or-expired-action-prompt')
          return
        }
        this.clearTimeWarningTimers()
        this.currentActionSeatOrder = isForLocalPlayer ? seatOrder : null
        this.canDiscardOnTileClick = isForLocalPlayer && isTurnMode && actionOffers.includes('Tap')
        if (this.canDiscardOnTileClick) this.reconcileTapCandidates(seatOrder)
        if (isForLocalPlayer) this.redrawHand(this.myOdr)
        if (this.canDiscardOnTileClick) void this.showTrainingRecommendation(actionSeq, actionPromptSerial)
        else this.clearTrainingRecommendation()
        this.clearActionResponseTimer()
        if (isForLocalPlayer && !this.canSendCurrentPrompt('expired on receive')) return
        this.traceGameFlow('resolve ACTIONS', {
          seatOrder,
          isForLocalPlayer,
          playerMode,
          actionSeq,
          remainingMs,
          actionOffers,
          canDiscardOnTileClick: this.canDiscardOnTileClick,
        })
        if (this.canDiscardOnTileClick) {
          this.scheduleAutoDiscard(Number(data.timeLimit ?? 0), actionPromptSerial)
        } else {
          this.clearAutoDiscardTimer()
        }
        const hasInputWarningMode = this.canDiscardOnTileClick
          || (playerMode !== 'Kyo' && playerMode !== 'Aga' && actionOffers.some(action => action !== 'Pass'))
        if (isForLocalPlayer && remainingMs > 0 && hasInputWarningMode && !this.shouldSuppressLivePlayback()) this.scheduleTimeWarnings(remainingMs, actionPromptSerial)
        const shouldStartActionPromptTimer = isForLocalPlayer && remainingMs > 0 && hasInputWarningMode
        if (shouldStartActionPromptTimer && !isTurnMode) {
          this.emitActionPromptTimerWhenReady(remainingMs)
        }
        if (DEBUG_GAME) console.info('[GameScene] MJPID_ACTIONS resolved', {
          seatOrder,
          myOdr: this.myOdr,
          actionOffers,
          canDiscardOnTileClick: this.canDiscardOnTileClick,
          isForLocalPlayer,
          playerMode,
          actFlags: data.actFlags,
        })
        if (DEBUG_GAME && Number.isFinite(seatOrder) && seatOrder !== this.myOdr) {
          console.info('[GameScene] MJPID_ACTIONS received for another player', { seatOrder, myOdr: this.myOdr, data })
        }
        if (!isForLocalPlayer) {
          this.clearActionButtons()
        }
        if (isForLocalPlayer && playerMode === 'Kyo') {
          this.canDiscardOnTileClick = false
          this.clearActionButtons()
          window.dispatchEvent(new CustomEvent(KYO_RESULT_ACTION_EVENT, {
            detail: {
              roomId: this.roomId,
              seatOrder: Number.isFinite(seatOrder) ? seatOrder : this.myOdr,
              timeLimit: Number(data.timeLimit ?? 0),
              actionSeq: this.currentActionPrompt?.actionSeq ?? actionSeq,
              localDeadlineAt: this.currentActionPrompt?.localDeadlineAt,
            },
          }))
          return
        }

        if (isForLocalPlayer && playerMode === 'Aga') {
          this.canDiscardOnTileClick = false
          this.clearActionButtons()
          window.dispatchEvent(new CustomEvent(ASK_END_SET_EVENT, {
            detail: {
              roomId: this.roomId,
              seatOrder: Number.isFinite(seatOrder) ? seatOrder : this.myOdr,
              actionSeq: this.currentActionPrompt?.actionSeq ?? actionSeq,
              localDeadlineAt: this.currentActionPrompt?.localDeadlineAt,
            },
          }))
          return
        }
        if (isForLocalPlayer && !this.canDiscardOnTileClick && actionOffers.includes('Pass')) {
          this.scheduleDefaultPass(actionOffers, Number(data.timeLimit ?? 0), actionPromptSerial)
        }
        if (isTurnMode) {
          if (isForLocalPlayer && !this.shouldSuppressLivePlayback()) playMajakSid(SID_TURN, this.soundSkinOptions())
          const turnOdr = Number.isFinite(seatOrder) ? seatOrder : this.myOdr
          this.clearDiscardSourceMarker(turnOdr)
          this.ensureTurnDrawTile(turnOdr)
          this.traceGameFlow('emit turnChange', {
            seatOrder,
            isForLocalPlayer,
            actionSeq,
            timeLimit: data.timeLimit,
          })
          this.emitToUiScene('turnChange', {
            odr: Number.isFinite(seatOrder) ? seatOrder : this.myOdr,
            viewOdr: this.myOdr,
          })
          if (shouldStartActionPromptTimer) {
            this.emitActionPromptTimerWhenReady(remainingMs)
          }
        }
        if (actionOffers.length > 0 && this.isLocalPlayerOdr(this.currentActionSeatOrder)) {
          this.showActionButtons(actionOffers)
          this.scheduleAutoControl(actionOffers, actionPromptSerial)
        }
      }
      if (playType !== 'MJPID_ACTIONS' && actionOffers.length > 0 && this.isLocalPlayerOdr(this.currentActionSeatOrder)) {
        this.showActionButtons(actionOffers)
        this.scheduleAutoControl(actionOffers)
      }

      if (action >= 0) {
        const actionKey = this.actionPacketKey(data, actionSeatOrder, action)
        if (this.appliedActionKeys.has(actionKey)) {
          this.traceGameFlow('drop duplicate ACTION', { actionKey, actionSeatOrder, action, actionSeq: data.actionSeq })
          this.completePendingActionIfMatched(actionSeatOrder, action, Number(data.actionSeq ?? 0))
          if (DEBUG_GAME) console.warn('[GameScene] duplicate MJPID_ACTION ignored', { actionKey, data })
        } else {
          this.appliedActionKeys.add(actionKey)
          this.traceGameFlow('apply ACTION', { actionKey, actionSeatOrder, action, actionSeq: data.actionSeq, bipaiIndex: data.bipaiIndex })
          this.completePendingActionIfMatched(actionSeatOrder, action, Number(data.actionSeq ?? 0))
          this.actionPromptSerial++
          this.clearAutoDiscardTimer()
          this.clearActionResponseTimer()
          this.clearAutoControlTimer()
          this.clearTimeWarningTimers()
          this.actionOfferByName.clear()
          this.currentActionOffers = []
          this.canDiscardOnTileClick = false
          this.currentActionSeatOrder = null
          this.currentActionPrompt = null
          this.clearTenpaiMarkers()
          this.emitToUiScene('actionPromptEnd', { viewOdr: this.myOdr })
          this.applyActionPacket(data)
        }
      }
      if (Number.isFinite(Number(data.leftCount))) {
        this.paifuGraphRound.left = Number(data.leftCount)
        this.emitToUiScene('stateUpdate', {
          players: this.players,
          left: Number(data.leftCount),
          viewOdr: this.myOdr,
        })
      }
    }
    this.onSignalR('playing', handleGamePlay)

    const handleHistory: SignalR.MessageHandler = data => {
      const canHandle = this.canHandleSignalR()
      console.info('[GameReconnect] history handler entered', {
        roomId: this.roomId,
        canHandle,
        isReplay: this.isReplay,
        restorePending: this.gameRestorePending,
        historyCount: data.historyCount,
      })
      if (!canHandle || this.isReplay) {
        console.warn('[GameReconnect] history ignored before application', {
          roomId: this.roomId,
          canHandle,
          isReplay: this.isReplay,
        })
        return
      }
      if (this.gameRestorePending) this.gameResyncHistoryReceived = true
      emitGameLoadProgress('history', { historyCount: data.historyCount })
      const showHistoryLoading = !this.gameResyncInFlight
      if (showHistoryLoading) this.emitGameSync(true, 'history')
      try {
        const packets = this.extractReplayPackets(data)
        this.logResyncProbe('history received', {
          showHistoryLoading,
          historyCount: data.historyCount,
          packetCount: packets.length,
          packets: packets.map(packet => ({ cmd: packet.cmd, playType: packet.data.playType, openPos: packet.data.openPos, isInit: packet.data.bInit ?? packet.data.init, leftCount: packet.data.leftCount })).slice(0, 24),
          queueBefore: this.paiInfoQueue.map(msg => ({ ini: msg.bIniKyo, openPos: msg.openPos, count: msg.tiles.length })),
        })
        if (packets.length === 0) {
          if (DEBUG_GAME) console.warn('[GameScene] live history has no Web packets to apply', { data })
          this.logResyncProbe('history ignored: no packets', { dataKeys: isRecord(data) ? Object.keys(data) : [] })
          return
        }
        if (this.paifuNeedsReconnectSeed) {
          beginPaifuRecording({
            mode: this.paifuRecordMode,
            isViewer: this.isViewer,
            roomId: this.roomId,
            roomName: this.paifuRoomName,
            roomOption: this.paifuGraphRound.roomOption,
            members: this.players.map(player => ({ name: player.name })),
          })
          this.paifuNeedsReconnectSeed = false
        }
        replaceRecordedPaifuPackets(packets)
        const historyHasInitPaiInfo = packets.some(packet => this.isInitPaiInfoPacket(packet))
        const pendingInitPaiInfo = this.findPendingSeedInitPaiInfo()
        const pendingSeedInitPaiInfo = shouldUsePendingInitPaiInfo(historyHasInitPaiInfo, Boolean(pendingInitPaiInfo))
          ? pendingInitPaiInfo
          : undefined
        if (!this.canApplyLiveHistoryPackets(packets, pendingSeedInitPaiInfo)) {
          this.logResyncProbe('history ignored: incomplete init sequence', { packetCount: packets.length })
          return
        }
        this.logResyncProbe('history apply start', {
          pendingSeedInitPaiInfo: pendingSeedInitPaiInfo ? { openPos: pendingSeedInitPaiInfo.openPos, count: pendingSeedInitPaiInfo.tiles.length } : null,
        })
        this.clearLiveActionState('history resync')
        this.paiInfoQueue = pendingSeedInitPaiInfo ? [this.clonePaiInfoMsg(pendingSeedInitPaiInfo)] : []
        this.isReplayApplyingHistory = true
        try {
          let seededInitConsumed = !pendingSeedInitPaiInfo
          packets.forEach(packet => {
            if (!seededInitConsumed && this.isInitPaiInfoPacket(packet)) return
            if (packet.cmd === 'smmc4e') handlePaiInfo(packet.data)
            else {
              handleGamePlay(packet.data)
              if (String(packet.data.playType ?? '') === 'MJPID_INIKYO') seededInitConsumed = true
            }
          })
        } finally {
          this.isReplayApplyingHistory = false
        }
        this.applyPendingResyncHandSnapshot()
        this.redrawAllPerspectivePai()
        const viewerHistoryWasPending = this.viewerHistorySyncPending
        this.viewerHistorySyncPending = false
        this.emitToUiScene('stateUpdate', { players: this.players, viewOdr: this.myOdr })
        this.logResyncProbe('history apply complete', {
          currentKyoku: this.paifuGraphRound.kyokuCnt,
          left: this.paifuGraphRound.left,
          handCounts: this.players.map(player => player.hand.length),
          discardCounts: this.players.map(player => player.discards.length),
          queueAfter: this.paiInfoQueue.map(msg => ({ ini: msg.bIniKyo, openPos: msg.openPos, count: msg.tiles.length })),
        })
        emitGameLoadProgress('sync', {
          historyPacketCount: packets.length,
          handCounts: this.players.map(player => player.hand.length),
        })
        if (this.gameRestorePending) {
          this.gameResyncHistoryApplied = true
          this.tryCompleteGameResync('authoritative-history-applied')
        }
        if (DEBUG_GAME) console.info('[GameScene] live history applied', { packetCount: packets.length })
        if (viewerHistoryWasPending) this.emitGameSync(false, 'viewer-history-applied')
      } finally {
        if (showHistoryLoading && !this.viewerHistorySyncPending) this.emitGameSync(false, 'history')
      }
    }
    this.onSignalR('history', handleHistory)

    if (this.isReplay) {
      this.applyReplayPaifuData(handlePaiInfo, handleGamePlay)
      this.setupReplayPacketEvents(handlePaiInfo, handleGamePlay)
    }

  }

  private actionPacketKey(data: Record<string, unknown>, seatOrder: number, action: number): string {
    const auditSeq = Number(data.auditSeq ?? 0)
    const actionSeq = Number(data.actionSeq ?? 0)
    const indices = Array.isArray(data.bipaiIndex) ? data.bipaiIndex.map(Number) : []
    if (Number.isFinite(auditSeq) && auditSeq > 0) return `audit:${auditSeq}`
    if (Number.isFinite(actionSeq) && actionSeq > 0) return `${actionSeq}:${seatOrder}:${action}:${indices.join(',')}`
    return `${seatOrder}:${action}:${indices.join(',')}`
  }

  private traceGameFlow(eventName: string, details: Record<string, unknown> = {}) {
    if (!DEBUG_GAME) return
    console.info(`${GAME_FLOW_TRACE_PREFIX} #${++this.flowTraceSerial} ${eventName}`, {
      roomId: this.roomId,
      myOdr: this.myOdr,
      actionSendInFlight: this.actionSendInFlight,
      pendingAction: this.pendingAction,
      currentActionSeatOrder: this.currentActionSeatOrder,
      currentPrompt: this.currentActionPrompt
        ? {
            actionSeq: this.currentActionPrompt.actionSeq,
            seatOrder: this.currentActionPrompt.seatOrder,
            playerMode: this.currentActionPrompt.playerMode,
            localRemainingMs: Math.max(0, Math.round(this.currentActionPrompt.localDeadlineAt - performance.now())),
          }
        : null,
      canDiscardOnTileClick: this.canDiscardOnTileClick,
      ...details,
    })
  }

  private logDiscardProbe(eventName: string, details: Record<string, unknown> = {}) {
    if (!DEBUG_GAME) return
    console.info(DISCARD_PROBE_PREFIX, eventName, {
      roomId: this.roomId,
      isViewer: this.isViewer,
      myOdr: this.myOdr,
      handCounts: this.players.map(player => player.hand.length),
      discardCounts: this.players.map(player => player.discards.length),
      ...details,
    })
  }

  private logResyncProbe(eventName: string, details: Record<string, unknown> = {}) {
    console.info(RESYNC_PROBE_PREFIX, eventName, {
      roomId: this.roomId,
      isViewer: this.isViewer,
      myOdr: this.myOdr,
      kyokuCnt: this.paifuGraphRound.kyokuCnt,
      left: this.paifuGraphRound.left,
      handCounts: this.players.map(player => player.hand.length),
      discardCounts: this.players.map(player => player.discards.length),
      queue: this.paiInfoQueue.map(msg => ({ ini: msg.bIniKyo, openPos: msg.openPos, count: msg.tiles.length })),
      ...details,
    })
  }

  private reportGameDiscardAudit(data: Record<string, unknown>, seatOrder: number, action: number) {
    if (this.isReplay || this.isReplayApplyingHistory || !SignalR.isConnected()) return
    const roomId = Number(this.roomId)
    const auditSeq = Number(data.auditSeq ?? 0)
    if (!Number.isInteger(roomId) || roomId <= 0 || !Number.isInteger(auditSeq) || auditSeq <= 0) return

    void SignalR.invoke('ReportGameDiscardAudit', {
      roomId,
      auditSeq,
      actionSeq: Number(data.actionSeq ?? 0),
      seatOrder,
      action,
      handCounts: this.players.map(player => player.hand.length),
      visibleDiscardCounts: this.players.map(player => player.discards.length),
      claimedDiscardCounts: [...this.claimedDiscardCounts],
      meldCounts: this.players.map(player => player.melds.length),
      pendingDiscardCount: this.pendingDiscardsByBipaiIndex.size,
    }).catch(error => {
      console.warn('[GameDiscardAudit] client report failed', {
        roomId,
        auditSeq,
        errorMessage: error instanceof Error ? error.message : String(error),
      })
    })
  }

  private handProbe(odr: number, bipaiIndex?: number) {
    if (odr < 0 || odr >= this.players.length) return { odr, valid: false }
    const hand = this.players[odr].hand
    const handIdx = bipaiIndex === undefined ? -1 : hand.findIndex(tile => tile.bipaiIndex === bipaiIndex)
    return {
      odr,
      valid: true,
      isLocal: this.isLocalPlayerOdr(odr),
      handLength: hand.length,
      handLengthMod3: hand.length % 3,
      targetBipaiIndex: bipaiIndex,
      handIdx,
      knownCode: bipaiIndex === undefined ? 0 : this.knownPai.get(bipaiIndex) ?? 0,
      handSample: hand.slice(0, 16).map(tile => ({ idx: tile.bipaiIndex, code: tile.code })),
    }
  }

  private completePendingActionIfMatched(seatOrder: number, action: number, actionSeq: number) {
    if (!this.pendingAction) return
    if (this.pendingAction.seatOrder !== seatOrder || this.pendingAction.action !== action) return
    if (this.pendingAction.actionSeq !== undefined && Number.isFinite(actionSeq) && actionSeq > 0 && this.pendingAction.actionSeq !== actionSeq) {
      this.traceGameFlow('pending ACTION not completed: seq mismatch', { seatOrder, action, actionSeq })
      return
    }
    this.clearPendingAction('complete pending ACTION', { seatOrder, action, actionSeq })
  }

  private clearPendingAction(reason: string, details: Record<string, unknown> = {}) {
    this.traceGameFlow(reason, details)
    this.actionSendInFlight = false
    this.pendingAction = null
  }

  private applyReplayPaifuData(handlePaiInfo: SignalR.MessageHandler, handleGamePlay: SignalR.MessageHandler) {
    if (this.replayPaifuApplied) return
    this.replayPaifuApplied = true
    const packets = this.extractReplayPackets(this.replayPaifuData)
    if (packets.length === 0) {
      if (DEBUG_GAME) console.warn('[GameScene] replay paifu has no Web packet history to apply', { paifu: this.replayPaifuData })
      return
    }
    this.time.delayedCall(0, () => {
      if (!this.canHandleSignalR()) return
      this.isReplayApplyingHistory = true
      try {
        packets.forEach(packet => {
          if (packet.cmd === 'smmc4e') handlePaiInfo(packet.data)
          else handleGamePlay(packet.data)
        })
      } finally {
        this.isReplayApplyingHistory = false
      }
      this.redrawAllReplayPai()
      this.emitToUiScene('stateUpdate', { players: this.players, viewOdr: this.myOdr })
      if (DEBUG_GAME) console.info('[GameScene] replay paifu applied', { packetCount: packets.length })
    })
  }

  private setupReplayPacketEvents(handlePaiInfo: SignalR.MessageHandler, handleGamePlay: SignalR.MessageHandler) {
    this.teardownReplayPacketEvents()
    this.replayPacketHandler = (event: Event) => {
      const packet = (event as CustomEvent<{ packet?: { cmd?: unknown; data?: unknown } }>).detail?.packet
      if (!packet || !isRecord(packet.data) || !this.canHandleSignalR()) return
      if (packet.cmd !== 'smmc4e' && packet.cmd !== 'playing') return

      this.isReplayApplyingHistory = true
      try {
        if (packet.cmd === 'smmc4e') handlePaiInfo(packet.data)
        else handleGamePlay(packet.data)
      } finally {
        this.isReplayApplyingHistory = false
      }
      this.redrawAllReplayPai()
      this.emitToUiScene('stateUpdate', { players: this.players, viewOdr: this.myOdr })
    }
    window.addEventListener(PAIFU_REPLAY_PACKET_EVENT, this.replayPacketHandler)
  }

  private teardownReplayPacketEvents() {
    if (!this.replayPacketHandler) return
    window.removeEventListener(PAIFU_REPLAY_PACKET_EVENT, this.replayPacketHandler)
    this.replayPacketHandler = undefined
  }

  private shouldSuppressLivePlayback() {
    return this.isReplayApplyingHistory
  }

  private canApplyLiveHistoryPackets(packets: Array<{ cmd: 'playing' | 'smmc4e'; data: Record<string, unknown> }>, pendingInitPaiInfo?: PaiInfoMsgState) {
    const hasInitKyoku = packets.some(packet => packet.cmd === 'playing' && String(packet.data.playType ?? '') === 'MJPID_INIKYO')
    if (!hasInitKyoku) return true
    const hasInitPaiInfo = packets.some(packet => packet.cmd === 'smmc4e' && Boolean(packet.data.bInit ?? packet.data.init))
    if (hasInitPaiInfo) return true
    if (pendingInitPaiInfo?.bIniKyo && pendingInitPaiInfo.tiles.length > 0) return true
    if (DEBUG_GAME) console.warn('[GameScene] skip incomplete live history: init kyoku without initial PaiInfo', {
      packetCount: packets.length,
      pendingInitPaiInfo: pendingInitPaiInfo ? { openPos: pendingInitPaiInfo.openPos, count: pendingInitPaiInfo.tiles.length } : null,
    })
    return false
  }

  private findPendingSeedInitPaiInfo() {
    const preferredOpenPos = this.isViewer ? VIEWER_OPEN_POS : this.myOdr
    return this.paiInfoQueue.find(msg => msg.bIniKyo && msg.openPos === preferredOpenPos)
      ?? this.paiInfoQueue.find(msg => msg.bIniKyo && msg.openPos !== VIEWER_OPEN_POS)
      ?? this.paiInfoQueue.find(msg => msg.bIniKyo)
  }

  private isInitPaiInfoPacket(packet: { cmd: 'playing' | 'smmc4e'; data: Record<string, unknown> }) {
    if (packet.cmd !== 'smmc4e') return false
    return Boolean(packet.data.bInit ?? packet.data.init)
  }

  private extractReplayPackets(value: unknown): Array<{ cmd: 'playing' | 'smmc4e'; data: Record<string, unknown> }> {
    if (typeof value === 'string') {
      try {
        return this.extractReplayPackets(JSON.parse(value))
      } catch {
        return []
      }
    }
    if (Array.isArray(value)) return value.flatMap(item => this.extractReplayPackets(item))
    if (!isRecord(value)) return []

    const nested = value.paifu ?? value.Paifu
      ?? value.history ?? value.History
      ?? value.packets ?? value.Packets
      ?? value.events ?? value.Events
      ?? value.playHistory ?? value.PlayHistory
      ?? value.data ?? value.Data
    if (nested !== undefined && nested !== value) {
      const nestedPackets = this.extractReplayPackets(nested)
      if (nestedPackets.length > 0) return nestedPackets
    }

    const payload = isRecord(value.payload) ? value.payload
      : isRecord(value.Payload) ? value.Payload
        : isRecord(value.message) ? value.message
          : isRecord(value.Message) ? value.Message
            : isRecord(value.body) ? value.body
              : isRecord(value.Body) ? value.Body
                : isRecord(value.data) ? value.data
                  : isRecord(value.Data) ? value.Data
                    : value
    const rawCmd = String(value.cmd ?? value.Cmd ?? value.command ?? value.Command ?? value.commandCode ?? value.CommandCode ?? value.service ?? value.Service ?? '')
    if (rawCmd === 'smmc4e' || rawCmd === 'PaiInfoList') return [{ cmd: 'smmc4e', data: payload }]
    if (rawCmd === 'playing' || rawCmd === 'GamePlay') return [{ cmd: 'playing', data: payload }]
    if (Array.isArray(payload.pai) && (payload.openPos !== undefined || payload.bInit !== undefined || payload.init !== undefined)) return [{ cmd: 'smmc4e', data: payload }]
    if (typeof payload.playType === 'string') return [{ cmd: 'playing', data: payload }]
    return []
  }

  private canHandleSignalR(): boolean {
    return this.acceptingSignalR && Boolean(this.sys && this.add && this.scene)
  }

  private onSignalR(cmd: string, handler: SignalR.MessageHandler) {
    SignalR.on(cmd, handler)
    this.signalRHandlers.push({ cmd, handler })
  }

  private requestInitialRoomState() {
    if (this.isReplay || !this.roomId) {
      if (this.skipInitialRoomEnter) {
        console.warn('[GameReconnect] initial room state request skipped', {
          roomId: this.roomId,
          isReplay: this.isReplay,
          reason: !this.roomId ? 'missing-room-id' : 'replay',
        })
      }
      return
    }
    const numericRoomId = Number(this.roomId)
    if (!Number.isFinite(numericRoomId) || numericRoomId <= 0) {
      console.warn('[GameReconnect] initial room state request skipped: invalid room id', {
        roomId: this.roomId,
      })
      return
    }
    this.emitGameSync(true, 'initial-room-state')

    if (!this.canHandleSignalR()) {
      console.warn('[GameReconnect] initial room state request skipped: GameScene cannot handle SignalR', {
        roomId: this.roomId,
        acceptingSignalR: this.acceptingSignalR,
      })
      return
    }
    if (this.requestInitialGameResync) {
      this.logResyncProbe('request initial room state through RequestGameResync', { skipInitialRoomEnter: true })
      this.requestGameResync('initial-room-state')
      return
    }
    if (this.skipInitialRoomEnter) return
    this.logResyncProbe('send c14e from GameScene initial room state', { numericRoomId })
    void SignalR.send('c14e', { roomId: numericRoomId, k42e: numericRoomId })
      .then(() => SignalR.send('c16e', {}))
      .catch(() => {})
  }

  private requestGameResync(reason: string) {
    const numericRoomId = Number(this.roomId)
    if (!Number.isFinite(numericRoomId) || numericRoomId <= 0) {
      console.warn('[GameReconnect] RequestGameResync skipped: invalid room id', {
        roomId: this.roomId,
        reason,
      })
      return
    }
    this.gameResyncInFlight = true
    this.gameRestorePending = true
    this.gameResyncInvokeResolved = false
    this.gameResyncSnapshotReceived = false
    this.gameResyncHistoryReceived = false
    this.gameResyncHistoryApplied = false
    this.emitGameSync(true, reason)
    this.logResyncProbe('RequestGameResync invoke start', { reason, numericRoomId })
    if (DEBUG_GAME) console.info('[GameScene] request game resync', { reason, roomId: this.roomId })
    void SignalR.invoke('RequestGameResync', numericRoomId)
      .then(() => {
        this.logResyncProbe('RequestGameResync invoke resolved', { reason, numericRoomId })
        this.gameResyncInvokeResolved = true
        this.tryCompleteGameResync(reason)
      })
      .catch(error => {
        const errorMessage = error instanceof Error ? error.message : String(error)
        console.error('[GameReconnect] RequestGameResync invoke failed', { reason, numericRoomId, errorMessage })
        this.logResyncProbe('RequestGameResync invoke failed', { reason, numericRoomId, errorMessage })
      })
  }

  private tryCompleteGameResync(reason: string) {
    const gate = {
      restorePending: this.gameRestorePending,
      inFlight: this.gameResyncInFlight,
      invokeResolved: this.gameResyncInvokeResolved,
      snapshotReceived: this.gameResyncSnapshotReceived,
      historyReceived: this.gameResyncHistoryReceived,
      historyApplied: this.gameResyncHistoryApplied,
    }
    if (!canCompleteGameResync(gate, this.isViewer)) {
      this.logResyncProbe('resync completion gate waiting', {
        reason,
        gate,
        requiredAuthority: this.isViewer ? 'history' : 'snapshot',
      })
      return
    }

    this.gameResyncInFlight = false
    this.gameRestorePending = false
    if (!this.gameResyncHistoryReceived) {
      emitGameLoadProgress('sync', { reason: 'authoritative-snapshot-only' })
    }
    this.logResyncProbe('resync completion gate passed', { reason, gate })
    this.emitGameSync(false, reason)
  }

  private notifyGameClientReady() {
    const roomId = Number(this.roomId)
    if (!Number.isInteger(roomId) || roomId <= 0) {
      console.warn('[GameReconnect] NotifyGameClientReady skipped: invalid room id', { roomId: this.roomId })
      return
    }
    const startedAt = performance.now()
    console.info('[GameStartTiming] NotifyGameClientReady invoke started', { roomId })
    void SignalR.invoke('NotifyGameClientReady', roomId)
      .then(() => {
        console.info('[GameStartTiming] NotifyGameClientReady invoke completed', {
          roomId,
          durationMs: Math.round(performance.now() - startedAt),
        })
      })
      .catch(error => {
        console.warn('[GameReconnect] NotifyGameClientReady invoke failed', {
          roomId,
          errorMessage: error instanceof Error ? error.message : String(error),
        })
      })
  }

  private notifyGamePresentationReady(presentationId: number) {
    const roomId = Number(this.roomId)
    if (!Number.isInteger(roomId) || roomId <= 0 || !Number.isSafeInteger(presentationId) || presentationId <= 0) return
    const startedAt = performance.now()
    console.info('[GameStartTiming] NotifyGamePresentationReady invoke started', {
      roomId,
      presentationId,
      elapsedSinceSceneCreateMs: Math.round(startedAt - Number(this.registry.get('majak:gameStartSceneCreatedAt') ?? startedAt)),
    })
    void SignalR.invoke('NotifyGamePresentationReady', roomId, presentationId)
      .then(() => {
        console.info('[GameStartTiming] NotifyGamePresentationReady invoke completed', {
          roomId,
          presentationId,
          durationMs: Math.round(performance.now() - startedAt),
        })
      })
      .catch(error => {
        console.warn('[GameScene] NotifyGamePresentationReady invoke failed', {
          roomId,
          presentationId,
          errorMessage: error instanceof Error ? error.message : String(error),
        })
      })
  }

  private teardownSignalR() {
    for (const { cmd, handler } of this.signalRHandlers) {
      SignalR.off(cmd, handler)
    }
    this.signalRHandlers = []
  }

  private setupReplayControlEvents() {
    this.teardownReplayControlEvents()
    if (!this.isReplay && !this.isViewer) return

    this.replayRotateHandler = (event: Event) => {
      const detail = (event as CustomEvent<{ delta?: number }>).detail
      const delta = Number(detail?.delta ?? 0)
      if (delta !== 1 && delta !== 3) return
      this.myOdr = ((this.myOdr + delta) & 3) as 0 | 1 | 2 | 3
      this.redrawAllPerspectivePai()
      this.emitViewOdrChange()
    }
    this.replayHandOpenHandler = (event: Event) => {
      const detail = (event as CustomEvent<{ open?: boolean }>).detail
      this.replayHandOpen = detail?.open !== false
      this.redrawAllPerspectivePai()
    }
    this.replayGraphHandler = (event: Event) => {
      const detail = (event as CustomEvent<{ visible?: boolean }>).detail
      if (detail?.visible === false) this.hidePaifuGraph()
      else this.showPaifuGraph()
    }

    window.addEventListener(PAIFU_ROTATE_EVENT, this.replayRotateHandler)
    window.addEventListener(PAIFU_HAND_OPEN_EVENT, this.replayHandOpenHandler)
    window.addEventListener(PAIFU_GRAPH_EVENT, this.replayGraphHandler)
  }

  private teardownReplayControlEvents() {
    if (this.replayRotateHandler) {
      window.removeEventListener(PAIFU_ROTATE_EVENT, this.replayRotateHandler)
      this.replayRotateHandler = undefined
    }
    if (this.replayHandOpenHandler) {
      window.removeEventListener(PAIFU_HAND_OPEN_EVENT, this.replayHandOpenHandler)
      this.replayHandOpenHandler = undefined
    }
    if (this.replayGraphHandler) {
      window.removeEventListener(PAIFU_GRAPH_EVENT, this.replayGraphHandler)
      this.replayGraphHandler = undefined
    }
    this.hidePaifuGraph()
  }

  private setupAutoControlEvents() {
    this.teardownAutoControlEvents()
    if (this.isReplay) return
    this.autoControlHandler = (event: Event) => {
      const detail = (event as CustomEvent<Partial<AutoControlState>>).detail ?? {}
      const previous = this.autoControl
      this.autoControl = {
        prox: Boolean(detail.prox),
        autoTap: Boolean(detail.autoTap),
        autoPass: Boolean(detail.autoPass),
        autoHora: Boolean(detail.autoHora),
      }
      if (previous.autoTap && !this.autoControl.autoTap) this.clearAutoDiscardTimer()
      if (previous.autoPass && !this.autoControl.autoPass) this.clearActionResponseTimer()
      if (previous.prox && !this.autoControl.prox) {
        this.clearAutoDiscardTimer()
        this.clearActionResponseTimer()
      }
      this.clearAutoControlTimer()
      if (DEBUG_GAME) console.info('[GameScene] auto control updated', this.autoControl)
      if (this.isLocalPlayerOdr(this.currentActionSeatOrder) && this.currentActionOffers.length > 0) {
        this.scheduleAutoControl(this.currentActionOffers)
      }
    }
    window.addEventListener(GAME_AUTO_CONTROL_EVENT, this.autoControlHandler)
    this.autoPassHoldHandler = () => this.suspendAutoPassForCurrentPrompt()
    window.addEventListener(GAME_AUTO_PASS_HOLD_EVENT, this.autoPassHoldHandler)
  }

  private teardownAutoControlEvents() {
    if (this.autoControlHandler) {
      window.removeEventListener(GAME_AUTO_CONTROL_EVENT, this.autoControlHandler)
      this.autoControlHandler = undefined
    }
    if (this.autoPassHoldHandler) {
      window.removeEventListener(GAME_AUTO_PASS_HOLD_EVENT, this.autoPassHoldHandler)
      this.autoPassHoldHandler = undefined
    }
  }

  private setupAssistConfigEvents() {
    this.teardownAssistConfigEvents()
    this.assistConfigHandler = ((event: CustomEvent<GameAssistConfig>) => {
      this.assistConfig = toGameAssistConfig(event.detail)
      if (this.assistConfig.bChkTnp) this.redrawTenpaiMarkers()
      else this.clearTenpaiMarkers()
      if (!this.assistConfig.bChkHor) this.clearWaitTileGuide()
      if (this.assistConfig.bChkPai && this.activeAssistTileCode > 0) this.showAssistHighlights(this.activeAssistTileCode)
      else this.clearAssistHighlights()
      for (let odr = 0; odr < this.players.length; odr++) this.redrawDiscardSourceMarker(odr)
    }) as EventListener
    window.addEventListener(GAME_ASSIST_CONFIG_EVENT, this.assistConfigHandler)
  }

  private teardownAssistConfigEvents() {
    if (!this.assistConfigHandler) return
    window.removeEventListener(GAME_ASSIST_CONFIG_EVENT, this.assistConfigHandler)
    this.assistConfigHandler = undefined
  }

  private setupPaifuRecordingEvents() {
    this.teardownPaifuRecordingEvents()
    if (this.isReplay) return
    this.paifuConfigHandler = ((event: CustomEvent<number>) => {
      this.paifuRecordMode = Number(event.detail ?? 0)
      setPaifuRecordingMode(this.paifuRecordMode, this.isViewer)
    }) as EventListener
    window.addEventListener(GAME_PAIFU_RECORDING_CONFIG_EVENT, this.paifuConfigHandler)
    this.paifuConnectionLostHandler = () => {
      interruptPaifuRecording()
      this.paifuNeedsReconnectSeed = true
    }
    SignalR.onConnectionLost(this.paifuConnectionLostHandler)
  }

  private teardownPaifuRecordingEvents() {
    if (this.paifuConfigHandler) {
      window.removeEventListener(GAME_PAIFU_RECORDING_CONFIG_EVENT, this.paifuConfigHandler)
      this.paifuConfigHandler = undefined
    }
    if (this.paifuConnectionLostHandler) {
      SignalR.offConnectionLost(this.paifuConnectionLostHandler)
      this.paifuConnectionLostHandler = undefined
    }
  }

  private resolveSkinTextureKey(key: string): string {
    const candidate = skinTextureCandidate(key)
    return this.textures.exists(candidate) ? candidate : key
  }

  private resolveSkinTexture(texture: { key: string; frame?: number }): { key: string; frame?: number } {
    return { ...texture, key: this.resolveSkinTextureKey(texture.key) }
  }

  private emitToUiScene(eventName: string, payload: unknown) {
    if (!this.canHandleSignalR()) return
    if (!this.isReplay && this.isReplayApplyingHistory && UI_SINGLE_DELIVERY_EVENTS.has(eventName)) return
    this.events.emit(eventName, payload)
  }

  private emitGameStatus(text: string, color = '#000', bold = false) {
    window.dispatchEvent(new CustomEvent(GAME_STATUS_EVENT, { detail: { text, color, bold } }))
  }

  private emitGameSync(active: boolean, reason: string) {
    if (!active && this.gameRestorePending) {
      this.logResyncProbe('ignore premature game sync completion', { reason })
      return
    }
    if (!active) emitGameLoadProgress('ready', { reason })
    window.dispatchEvent(new CustomEvent(GAME_SYNC_EVENT, { detail: { active, reason } }))
  }

  private emitHoraErrorStatus(reason: string) {
    const text = reason === 'furiten'
      ? 'フリテン'
      : reason === 'sameTurnFuriten'
        ? '同巡フリテン'
        : reason === 'invalid'
          ? '和了無効'
          : ''
    if (text) this.emitGameStatus(text, '#ff1414')
  }

  private emitViewOdrChange() {
    this.emitToUiScene('viewOdrChange', {
      viewOdr: this.myOdr,
      players: this.players,
    })
  }

  private resolveMyOdrFromPlayers() {
    if (this.isViewer) return
    const localPix = useAuthStore.getState().player?.pix ?? ''
    if (!localPix) return
    const engineOrder = this.players.findIndex(player => player.pix === localPix)
    if (engineOrder < 0 || engineOrder === this.myOdr) return
    this.myOdr = engineOrder
    this.emitViewOdrChange()
  }

  private isLocalPlayerOdr(odr: number | null | undefined): boolean {
    return !this.isViewer && odr === this.myOdr
  }

  private mergePlayerInfo(data: Record<string, unknown>) {
    const pos = this.resolvePlayerOdr(data)
    if (pos < 0 || pos >= this.players.length) return
    this.mergePlayerInfoAtOdr(pos, data)
  }

  private applyHanchanOrder(data: Record<string, unknown>) {
    const wasInitialized = this.hanchanOrderInitialized
    const previousRoomPosToOdr = [...this.roomPosToOdr]
    this.roomPosToOdr = Array.from({ length: this.players.length }, (_, index) => index)
    const engineToRoom = Array.isArray(data.players) ? data.players.map(Number) : []
    const engineOrderPlayers = reorderHanchanPlayers(
      this.players,
      wasInitialized ? previousRoomPosToOdr : this.roomPosToOdr,
      engineToRoom,
    ).map(player => player ?? createEmptyPlayerState())
    engineToRoom.forEach((roomPos, odr) => {
      if (Number.isInteger(roomPos) && roomPos >= 0 && roomPos < this.players.length && odr >= 0 && odr < this.players.length) {
        this.roomPosToOdr[roomPos] = odr
      }
    })
    if (engineToRoom.length > 0) this.players = engineOrderPlayers

    this.memberOdrById.clear()
    const memberInfo = Array.isArray(data.memberInfo) ? data.memberInfo : []
    memberInfo.forEach((member, odr) => {
      if (!isRecord(member)) return
      const pix = String(member.k3e ?? member.pix ?? member.playerId ?? '')
      if (pix && odr >= 0 && odr < this.players.length) this.memberOdrById.set(pix, odr)
    })
    this.hanchanOrderInitialized = true
  }

  private resolvePlayerOdr(data: Record<string, unknown>) {
    const pix = String(data.k3e ?? data.pix ?? data.playerId ?? '')
    const mappedOdr = pix ? this.memberOdrById.get(pix) : undefined
    if (mappedOdr !== undefined) return mappedOdr

    const rawPos = Number(data.k58e ?? data.playerPos ?? data.seatPos ?? data.pos ?? -1)
    if (!Number.isInteger(rawPos) || rawPos < 0 || rawPos >= this.players.length) return -1
    return this.hanchanOrderInitialized ? this.roomPosToOdr[rawPos] ?? rawPos : rawPos
  }

  private mergePlayerInfoAtOdr(pos: number, data: Record<string, unknown>) {
    if (pos < 0 || pos >= this.players.length) return
    const player = this.players[pos]
    const pix = String(data.k3e ?? data.pix ?? data.playerId ?? player.pix ?? '')
    const hostPix = String(data.k50e ?? data.roomHost ?? data.hostPix ?? '')
    const avatarId = data.k7e ?? data.avatarId
    const sex = String(data.k11e ?? data.sex ?? '')
    const fallbackSex = sex === 'F' || sex === 'female' ? 'female' : 'male'
    const isNpc = data.isNpc === true
    const customCostume = isNpc ? 0 : Number(data.mjkk136e ?? data.customCostume ?? data.charaId ?? player.customCostume ?? 0)
    const customCostumeType = isNpc ? 0 : Number(data.mjkk137e ?? data.customCostumeType ?? data.charaType ?? player.customCostumeType ?? 0)
    player.pix = pix || player.pix
    player.name = String(data.mjkk34e ?? data.k8e ?? data.nickName ?? data.nickname ?? data.name ?? player.name ?? pix)
    player.level = String(data.k32e ?? data.slevel ?? data.dan ?? player.level ?? '')
    player.rating = Number(data.k31e ?? data.rating ?? player.rating ?? 0)
    const majakTitle = readOptionalTitleCode(data.mjkk47e ?? data.majakTitle)
    const trickTitle = readOptionalTitleCode(data.mjkk46e ?? data.trickTitle)
    const richiEffect = readOptionalTitleCode(data.mjkk54e ?? data.richiEffect)
    if (majakTitle !== undefined) player.majakTitle = majakTitle
    if (trickTitle !== undefined) player.trickTitle = trickTitle
    if (richiEffect !== undefined) player.richiEffect = richiEffect
    if (data.isHost !== undefined) player.isHost = Boolean(data.isHost)
    const proxyValue = asBoolean(data.isProxy ?? data.proxy ?? data.isOutPlayer)
    if (proxyValue !== undefined) player.isProxy = proxyValue
    if (hostPix) this.applyHostPix(hostPix)
    if (isNpc) {
      player.customCostume = 0
      player.customCostumeType = 0
    } else {
      if (Number.isFinite(customCostume) && customCostume > 0) player.customCostume = customCostume
      if (Number.isFinite(customCostumeType) && customCostumeType > 0) player.customCostumeType = customCostumeType
    }
    if (sex) player.sex = sex
    if (avatarId != null && String(avatarId) !== '') {
      player.avatarId = String(avatarId)
      player.avatarUrl = getHighResolutionGameAvatarUrl(player.avatarId)
    }
    player.fallbackAvatarUrl = getDefaultAvatarUrl(fallbackSex)
  }

  private applyKyoResultState(data: Record<string, unknown>) {
    const pinType = Number(data.pinType ?? -1)
    const yakuByPlayer = isRecord(data.yakuByPlayer) ? data.yakuByPlayer : {}
    const totalsByPlayer = isRecord(data.totalsByPlayer) ? data.totalsByPlayer : {}
    const resultPlayers = Array.isArray(data.players) ? data.players : []

    for (let odr = 0; odr < this.players.length; odr++) {
      const playerResult = isRecord(resultPlayers[odr]) ? resultPlayers[odr] : {}
      const totals = isRecord(totalsByPlayer[String(odr)]) ? totalsByPlayer[String(odr)] as Record<string, unknown> : {}
      const yakuList = Array.isArray(yakuByPlayer[String(odr)]) ? yakuByPlayer[String(odr)] as unknown[] : []
      const pointBal = asFiniteNumber(playerResult.tenBal)
      const tipBal = asFiniteNumber(playerResult.tipBal)
      const resultScore = asFiniteNumber(playerResult.point ?? playerResult.score ?? playerResult.gameScore)
      const resultTip = asFiniteNumber(playerResult.tip)
      const resultYakitori = asBoolean(playerResult.yakitori)
      if (resultScore !== undefined) this.players[odr].score = resultScore
      else if (pointBal !== undefined) this.players[odr].score = (Number.isFinite(this.players[odr].score) ? this.players[odr].score : 0) + pointBal
      if (resultTip !== undefined) this.players[odr].tip = resultTip
      else if (tipBal !== undefined) this.players[odr].tip += tipBal
      if (resultYakitori !== undefined) this.players[odr].yakitori = resultYakitori
      else if (asBoolean(playerResult.isHora)) this.players[odr].yakitori = false
      this.players[odr].hora = {
        pinType,
        isHora: Boolean(playerResult.isHora),
        isHoju: Boolean(playerResult.isHoju),
        isTempai: Boolean(playerResult.isTempai),
        isRichi: Boolean(playerResult.isRichi),
        yaku: yakuList.filter(isRecord).map(yaku => ({
          name: String(yaku.name ?? ''),
          fan: Number(yaku.fan ?? 0),
          code: asFiniteNumber(yaku.code),
          isYakuman: asBoolean(yaku.isYakuman),
          tip: asFiniteNumber(yaku.tip),
        })),
        totalFu: asFiniteNumber(totals.totalFu),
        totalFan: asFiniteNumber(totals.totalFan),
        totalTen: asFiniteNumber(totals.totalTen),
        tipBal: asFiniteNumber(totals.tipBal ?? playerResult.tipBal),
      }
    }
  }

  private playKyoResultHoraEffect(data: Record<string, unknown>) {
    if (this.shouldSuppressLivePlayback()) return
    const resultPlayers = Array.isArray(data.players) ? data.players : []
    const presentations = getLegacyHoraPresentations({
      ...data,
      players: resultPlayers.map((value, odr) => isRecord(value)
        ? { ...value, trickTitle: this.players[odr]?.trickTitle }
        : value),
    })
    if (presentations.length === 0) return
    presentations.forEach(presentation => this.playHoraEffectSound(presentation.pinType, presentation.totalTen, presentation.isYakuman))
    if (!this.shouldPlayLegacyVisuals()) return

    const scoreDuration = Math.max(...presentations.map(presentation => this.playLegacyScoreEffect(presentation)))
    const hasHighHora = presentations.some(presentation => presentation.isYakuman || presentation.totalTen >= 3000)
    const hasYakuman = presentations.some(presentation => presentation.isYakuman)
    const skillDuration = Math.max(...presentations.map(presentation => presentation.level2SkillDurationMs))
    if (skillDuration > 0) {
      this.time.delayedCall(scoreDuration, () => {
        presentations.forEach(presentation => {
          if (presentation.level2SkillDurationMs > 0) this.playLegacyLevel2Skill(presentation)
        })
      })
    }
    const middleDuration = skillDuration || (hasHighHora ? LEGACY_HORA_FIRE_DURATION_MS : 0)
    const backdropDuration = scoreDuration
      + middleDuration
      + (hasYakuman ? LEGACY_YAKUMAN_FINISH_DURATION_MS : 0)
    this.playLegacyBackdrop(backdropDuration)

    if (hasHighHora && skillDuration === 0) {
      const presentation = presentations.find(item => item.isYakuman || item.totalTen >= 3000) ?? presentations[0]
      this.time.delayedCall(scoreDuration, () => this.playLegacyHoraFire(presentation))
    }
    if (hasYakuman) {
      const finishDelay = scoreDuration + middleDuration
      this.time.delayedCall(finishDelay, () => {
        playMajakSfx('mjkFan01', this.soundSkinOptions())
        const fallbackPoint = boardLocalPoint({ x: 115, y: 139 })
        const bounds = isMobileIngameLayout(this.layoutMode) ? mobileVisibleWorldBounds() : null
        const point = bounds ? centerMobileEffectPoint(LEGACY_YAKUMAN_FINISH_SIZE, bounds) : fallbackPoint
        this.playLegacyFrameSequence(
          numberedLegacyKeys('mj_ef_yakuman', 13, 0, ''),
          point.x,
          point.y,
          [...Array(12).fill(50), 1350],
          { depth: 10020 },
        )
      })
    }
  }

  private playHoraEffectSound(pinType: number, basicTen: number | undefined, isYakuman: boolean) {
    const ten = basicTen ?? 0
    const level = isYakuman || ten >= 4000 ? 3 : ten >= 3000 ? 2 : 1
    const sid = pinType === 0
      ? level === 3 ? SID_EFFECT_R_LV3 : level === 2 ? SID_EFFECT_R_LV2 : SID_EFFECT_R_LV1
      : level === 3 ? SID_EFFECT_T_LV3 : level === 2 ? SID_EFFECT_T_LV2 : SID_EFFECT_T_LV1
    playMajakSid(sid, this.soundSkinOptions())
  }

  private shouldPlayLegacyVisuals() {
    return !this.isReplay
      && !this.shouldSuppressLivePlayback()
      && !this.gameResyncInFlight
      && document.visibilityState === 'visible'
  }

  private shouldPlayInitialDeal() {
    return !this.isMeldLayoutFixture
      && !this.isReplay
      && !this.shouldSuppressLivePlayback()
      && !this.gameResyncInFlight
      && document.visibilityState === 'visible'
  }

  private playLegacyFrameSequence(
    keys: string[],
    x: number,
    y: number,
    frameDelays: readonly number[],
    options: { delay?: number; depth?: number; additive?: boolean; angle?: number; longSize?: number; displaySize?: { width: number; height: number } } = {},
  ) {
    const delay = options.delay ?? 0
    const duration = frameDelays.reduce((sum, value) => sum + value, 0)
    if (keys.length === 0 || !this.textures.exists(keys[0])) return delay + duration
    if (this.shouldPlayLegacyVisuals()) {
      window.dispatchEvent(new CustomEvent(GAME_EFFECT_WINDOW_EVENT, {
        detail: { durationMs: delay + duration },
      }))
    }
    const start = () => {
      if (!this.shouldPlayLegacyVisuals()) return
      const sprite = this.clipToBoard(this.add.image(x, y, keys[0]).setOrigin(0, 0).setDepth(options.depth ?? 10000))
      if (options.additive) sprite.setBlendMode(Phaser.BlendModes.ADD)
      if (options.displaySize) sprite.setDisplaySize(options.displaySize.width, options.displaySize.height)
      else if (options.longSize) sprite.setDisplaySize(options.longSize, sprite.height)
      const angle = options.angle ?? 0
      if (angle !== 0) {
        const width = sprite.displayWidth
        const height = sprite.displayHeight
        if (angle === -90) sprite.setPosition(x, y + width)
        else if (angle === 90) sprite.setPosition(x + height, y)
        else if (Math.abs(angle) === 180) sprite.setPosition(x + width, y + height)
        sprite.setAngle(angle)
      }
      this.legacyEffectSprites.push(sprite)
      let elapsed = 0
      for (let frame = 1; frame < keys.length; frame++) {
        elapsed += frameDelays[frame - 1] ?? 0
        this.time.delayedCall(elapsed, () => {
          if (sprite.active && this.textures.exists(keys[frame])) sprite.setTexture(keys[frame])
        })
      }
      this.time.delayedCall(duration, () => {
        sprite.destroy()
        this.legacyEffectSprites = this.legacyEffectSprites.filter(item => item !== sprite)
      })
    }
    if (delay > 0) this.time.delayedCall(delay, start)
    else start()
    return delay + duration
  }

  private legacyBoardEffectPlacement() {
    if (this.layoutMode === 'responsiveDesktop') {
      const bounds = responsiveDesktopVisibleWorldBounds()
      if (bounds) {
        return {
          x: bounds.left,
          y: bounds.top,
          displaySize: { width: bounds.right - bounds.left, height: bounds.bottom - bounds.top },
        }
      }
    }
    if (isMobileIngameLayout(this.layoutMode)) {
      const bounds = mobileVisibleWorldBounds()
      if (bounds) {
        return {
          x: bounds.left + (bounds.right - bounds.left - BOARD_W) / 2,
          y: bounds.top + (bounds.bottom - bounds.top - BOARD_H) / 2,
        }
      }
    }
    return { x: BOARD_X, y: BOARD_Y }
  }

  private playLegacyBackdrop(duration: number) {
    this.legacyBackdrop?.destroy()
    const bounds = this.layoutMode === 'responsiveDesktop'
      ? responsiveDesktopVisibleWorldBounds()
      : isMobileIngameLayout(this.layoutMode) ? mobileVisibleWorldBounds() : null
    this.legacyBackdrop = bounds
      ? this.clipToBoard(this.add.rectangle(
          bounds.left,
          bounds.top,
          bounds.right - bounds.left,
          bounds.bottom - bounds.top,
          0x000000,
          64 / 255,
        ).setOrigin(0, 0).setDepth(9990))
      : this.clipToBoard(this.add.image(BOARD_X, BOARD_Y, 'eff_rontumo_black').setOrigin(0, 0).setDepth(9990))
    const backdrop = this.legacyBackdrop
    this.time.delayedCall(duration, () => {
      backdrop.destroy()
      if (this.legacyBackdrop === backdrop) this.legacyBackdrop = undefined
    })
  }

  private playLegacyScoreEffect(presentation: LegacyHoraPresentation) {
    const { pinType, level, isYakuman, totalTen, odr } = presentation
    if (pinType === 1) {
      const loc = odrToLoc(odr, this.myOdr)
      if (isYakuman) {
        playMajakSid(SID_EFFECT_YAKUMAN, this.soundSkinOptions())
        const placement = this.legacyBoardEffectPlacement()
        return this.playLegacyFrameSequence(numberedLegacyKeys('eff_rontumoeff_d', 30, 1), placement.x, placement.y, Array(30).fill(LEGACY_EFFECT_FRAME_MS), { additive: true, displaySize: placement.displaySize })
      }
      const prefix = level === 3 ? 'eff_tumoeff_c' : level === 2 ? 'eff_tumoeff_b' : 'eff_tumoeff'
      const count = level === 3 ? 15 : level === 2 ? 13 : 12
      let position = [{ x: 1, y: 501 }, { x: 650, y: 0 }, { x: 1, y: -14 }, { x: -24, y: 0 }][loc]
      if (isMobileIngameLayout(this.layoutMode)) {
        const desktopHand = DESKTOP_INGAME_LAYOUT.handPosition[loc]
        const mobileHand = this.mobileHandAnchor(odr, loc)
        position = loc % 2 === 0
          ? { ...position, y: position.y + mobileHand.y - BOARD_Y - desktopHand.y }
          : { ...position, x: position.x + mobileHand.x - BOARD_X - desktopHand.x }
      }
      const angle = [0, -90, 180, 90][loc]
      const desktopPoint = boardEdgePoint(position, loc, this.layoutMode)
      const responsiveBounds = this.layoutMode === 'responsiveDesktop' ? responsiveDesktopVisibleWorldBounds() : null
      const point = isMobileIngameLayout(this.layoutMode)
        ? boardLocalPoint(position)
        : responsiveBounds
          ? {
              x: loc === 1 ? responsiveBounds.right - LEGACY_TSUMO_EFFECT_THICKNESS : responsiveBounds.left,
              y: loc === 1 || loc === 3 ? responsiveBounds.top : desktopPoint.y,
            }
          : desktopPoint
      const longSize = responsiveBounds
        ? loc % 2 === 0
          ? responsiveBounds.right - responsiveBounds.left
          : responsiveBounds.bottom - responsiveBounds.top
        : undefined
      return this.playLegacyFrameSequence(numberedLegacyKeys(prefix, count, 1), point.x, point.y, Array(count).fill(LEGACY_EFFECT_FRAME_MS), { additive: true, angle, longSize })
    }

    const source = this.lastRonSource ?? { x: BOARD_X + 399, y: BOARD_Y + 383, width: 0, height: 0, isReach: false }
    const centerX = source.x + source.width / 2
    const centerY = source.y + source.height / 2
    const prefix = level === 3 ? 'eff_roneff_c' : level === 2 ? 'eff_roneff_b' : 'eff_roneff'
    const count = level === 3 ? 27 : level === 2 ? 20 : 11
    const offsets = level === 3 && (isYakuman || totalTen >= 6000)
      ? [{ x: -145, y: -490 }, { x: -185, y: -530 }, { x: -180, y: -500 }, { x: -155, y: -520 }, { x: -145, y: -490 }]
      : [{ x: level === 3 ? -165 : -100, y: level === 3 ? -510 : -530 }]
    let duration = 0
    offsets.forEach((offset, index) => {
      duration = Math.max(duration, this.playLegacyFrameSequence(
        numberedLegacyKeys(prefix, count, 1),
        centerX + offset.x,
        centerY + offset.y,
        Array(count).fill(LEGACY_EFFECT_FRAME_MS),
        { delay: index * 3 * LEGACY_EFFECT_FRAME_MS, additive: true, depth: 10000 + index },
      ))
    })
    if (isYakuman) {
      const yakumanDelay = count * LEGACY_EFFECT_FRAME_MS + 12 * LEGACY_EFFECT_FRAME_MS
      const placement = this.legacyBoardEffectPlacement()
      this.time.delayedCall(yakumanDelay, () => playMajakSid(SID_EFFECT_YAKUMAN, this.soundSkinOptions()))
      duration = Math.max(duration, this.playLegacyFrameSequence(
        numberedLegacyKeys('eff_rontumoeff_d', 30, 1),
        placement.x,
        placement.y,
        Array(30).fill(LEGACY_EFFECT_FRAME_MS),
        { delay: yakumanDelay, additive: true, depth: 10010, displaySize: placement.displaySize },
      ))
    }
    return duration
  }

  private legacySkillDefinition(trickTitle: number | undefined, level: 1 | 2) {
    const title = Number(trickTitle ?? 0)
    const attribute = Math.trunc((title - 1) / 3) - 1
    if (attribute < 0 || attribute >= 4 || (level === 2 && title % 3 < 2)) return undefined
    const element = ['fire', 'water', 'earth', 'wind'][attribute]
    const delays = level === 1
      ? [
          Array(13).fill(100),
          [130, 130, 130, 130, 130, 130, 130, 130, 120, 120, 120],
          [80, 80, 80, 80, 420, 80, 80, 80, 80, 80, 80, 80],
          [100, 100, 100, 100, 100, 100, 100, 100, 100, 100, 150, 150],
        ][attribute]
      : [
          [100, 100, 100, 100, 60, 60, 60, 60, 60, 100, 100, 100],
          [100, 100, 100, 100, 200, 100, 100, 100, 100],
          Array(10).fill(100),
          [100, 100, 100, 100, 100, 100, 100, 60, 60, 60, 60, 60],
        ][attribute]
    return { element, delays, sound: `mjk${element}skill${level}` }
  }

  private playLegacyLevel1Skills(startDelay = 0) {
    if (!this.shouldPlayLegacyVisuals()) return 0
    let duration = startDelay
    this.players.forEach((player, odr) => {
      const definition = this.legacySkillDefinition(player.trickTitle, 1)
      if (!definition) return
      this.emitToUiScene('titleSkill', { odr, element: definition.element, level: 1, delays: definition.delays, delay: startDelay })
      duration = Math.max(duration, startDelay + definition.delays.reduce((sum, delay) => sum + delay, 0) + 500)
    })
    return duration
  }

  private playLegacyLevel2Skill(presentation: LegacyHoraPresentation) {
    const definition = this.legacySkillDefinition(this.players[presentation.odr]?.trickTitle, 2)
    if (!definition) return 0
    playMajakSfx(definition.sound, this.soundSkinOptions())
    this.emitToUiScene('titleSkill', { odr: presentation.odr, element: definition.element, level: 2, delays: definition.delays })
    return definition.delays.reduce((sum, delay) => sum + delay, 0)
  }

  private playLegacyGemGame(delay: number) {
    if (!this.shouldPlayLegacyVisuals() || (this.gemGame !== 1 && this.gemGame !== 2)) return 0
    const prefix = this.gemGame === 1 ? 'mj_ryu_normal' : 'mj_ryu_big'
    const sound = this.gemGame === 1 ? 'mjkryutama01' : 'mjkryutama02'
    this.time.delayedCall(delay, () => {
      playMajakSfx(sound, this.soundSkinOptions())
      const fallbackPoint = boardLocalPoint({ x: 227, y: 212 })
      const bounds = isMobileIngameLayout(this.layoutMode) ? mobileVisibleWorldBounds() : null
      const point = bounds ? centerMobileEffectPoint(LEGACY_GEM_EFFECT_SIZE, bounds) : fallbackPoint
      this.playLegacyFrameSequence(
        numberedLegacyKeys(prefix, 10, 1),
        point.x,
        point.y,
        [50, 50, 100, 100, 100, 100, 100, 100, 100, 1000],
        { depth: 10011 },
      )
    })
    return 1800
  }

  private mobileHandAnchor(odr: number, loc: number) {
    const firstHandSprite = this.handSprites[odr][0]
    return firstHandSprite
      ? { x: firstHandSprite.x, y: firstHandSprite.y }
      : boardLocalPoint(TEH_POS[loc])
  }

  private playLegacyHoraFire(presentation: LegacyHoraPresentation) {
    const loc = odrToLoc(presentation.odr, this.myOdr)
    const handSprite = this.handSprites[presentation.odr][this.handSprites[presentation.odr].length - 1]
    const source = presentation.pinType === 0 ? this.lastRonSource : handSprite
    if (!source) return
    const direction = presentation.pinType === 0 ? (this.lastRonSource?.isReach ? 4 : 3) : loc
    const bitmap = presentation.pinType === 0 ? 0 : [0, 2, 1, 0][loc]
    const offset = [{ x: 2, y: -35 }, { x: -35, y: -35 }, { x: -35, y: 20 }, { x: 10, y: -35 }, { x: -4, y: -35 }][direction]
    playMajakSfx('mjkFire_pai', this.soundSkinOptions())
    this.playLegacyFrameSequence(
      numberedLegacyKeys(`mj_ef_horafire0${bitmap}`, 3),
      source.x + offset.x,
      source.y + offset.y,
      [50, 50, 550],
      { depth: 10015 },
    )
  }

  private applyHostPix(hostPix: string) {
    this.players.forEach(player => { player.isHost = player.pix === hostPix })
  }

  /* ======================================================================
   * 描画メソッド (CMJTblDraw 相当)
   * ====================================================================== */

  /** 手牌再描画 (PutHaipai / PutTsumo 相当) */
  private redrawHand(odr: number) {
    if (this.isReplayApplyingHistory) return
    /* 既存スプライトを破棄 */
    this.handSprites[odr].forEach(s => s.destroy())
    this.handSprites[odr] = []
    this.mobileOpponentHandCountTexts[odr]?.destroy()
    this.mobileOpponentHandCountTexts[odr] = undefined

    const tiles = this.players[odr].hand
    const loc = odrToLoc(odr, this.myOdr)
    const isMe  = !this.isViewer && loc === 0 && !this.isReplay
    const isBottomHand = !this.isViewer && loc === 0

    if (this.shouldUseMobileOpponentHandSummary(odr, loc)) {
      this.redrawMobileOpponentHandSummary(odr, loc, tiles.length)
      this.redrawPaifuGraphContent()
      return
    }

    if (!this.isViewer && odr === this.myOdr) {
      this.hoverCursor?.destroy()
      this.hoverCursor = undefined
      this.selectedCursor?.destroy()
      this.selectedCursor = undefined
      this.drawnTileCursor?.destroy()
      this.drawnTileCursor = undefined
      this.clearTenpaiMarkers()
    }

    tiles.forEach((tile, idx) => {
      const isDrawTile = isDrawnTilePosition(tiles.length, idx, this.discardAfterCall[odr])
      const useOpenOrDownLayout = this.isViewer || (this.isReplay && this.replayHandOpen && (!isMobileIngameLayout(this.layoutMode) || loc !== 0))
      const handScale = this.handTileScale(odr, loc)
      const position = isMobileIngameLayout(this.layoutMode)
        ? mobileOuterHandPos(loc, idx, tiles.length, isDrawTile, handScale) ?? handPos(loc, idx, isDrawTile, useOpenOrDownLayout)
        : handPos(loc, idx, isDrawTile, useOpenOrDownLayout)
      const { x, y } = position
      const texture = this.resolveSkinTexture(handTexture(loc))
      const concealedTexture = this.resolveSkinTexture(concealedHandTexture(loc))
      const depth = loc === 0 && this.layoutMode === 'mobileLandscape'
        ? MOBILE_SELF_HAND_DEPTH + idx * 0.0001
        : handDepth(y, idx)
      let spr: Phaser.GameObjects.Image

      if (isBottomHand && tile.code > 0) {
        /* 自分の手牌: 表向き + インタラクティブ */
        const frame = paiToFrame(tile.code)
        spr = this.add.image(x, y, texture.key, frame)
          .setOrigin(0, 0)
          .setScale(this.tileRenderScale(handScale))
          .setDepth(depth)
        if (isMe) {
          spr.setInteractive({ useHandCursor: true })
            .on('pointerdown', (pointer: Phaser.Input.Pointer) => this.onTilePointerDown(idx, pointer))
            .on('pointerup', (pointer: Phaser.Input.Pointer) => this.onTilePointerUp(idx, pointer))
            .on('pointerover', (pointer: Phaser.Input.Pointer) => this.onTilePointerOver(idx, pointer))
            .on('pointerout', (pointer: Phaser.Input.Pointer) => this.onTilePointerOut(idx, pointer))
        }
        this.clipToBoard(spr)
        if (isMe && isDrawTile && this.initialActionPresentationReady) {
          this.drawnTileCursor = this.createLegacyReceivedTileCursor(spr)
        }
        if (isMe && tile.isSelected) {
          this.selectedCursor = this.layoutMode === 'mobileLandscape'
            ? this.createLegacyHoverCursor(spr)
            : this.createTileSelectionFrame(spr)
        }
      } else if (tile.code > 0 && ((this.isReplay && this.replayHandOpen) || (this.isViewer && this.replayHandOpen))) {
        const frame = paiToFrame(tile.code)
        spr = this.clipToBoard(this.add.image(x, y, this.resolveSkinTextureKey(openHandTexture(loc)), frame)
          .setOrigin(0, 0)
          .setScale(this.tileRenderScale(handScale))
          .setDepth(depth))
      } else if (this.isViewer) {
        spr = this.clipToBoard(this.add.image(x, y, this.resolveSkinTextureKey(downTexture(loc)))
          .setOrigin(0, 0)
          .setScale(this.tileRenderScale(handScale))
          .setDepth(depth))
      } else {
        /* 他家の手牌: レガシー方向別 tachi 画像 */
        spr = this.clipToBoard(this.add.image(x, y, concealedTexture.key)
          .setOrigin(0, 0)
          .setScale(this.tileRenderScale(handScale))
          .setDepth(depth))
      }
      this.handSprites[odr].push(spr)
    })
    this.alignResponsiveLocalHandAbovePanel(odr, loc)
    this.updateMobileActionHandVisibility()
    this.applyInitialDealVisibility(odr)
    if (!this.isViewer && odr === this.myOdr) this.redrawTenpaiMarkers()
    this.redrawDiscardSourceMarker(odr)
    this.redrawPaifuGraphContent()
  }

  private alignResponsiveLocalHandAbovePanel(odr: number, loc: 0 | 1 | 2 | 3) {
    if (this.layoutMode !== 'responsiveDesktop' || loc !== 0 || !this.actionPanelBounds) return
    const sprites = this.handSprites[odr].filter(sprite => sprite.active)
    if (sprites.length === 0) return
    const handBottom = Math.max(...sprites.map(sprite => sprite.getBounds().bottom))
    const panelTop = this.actionPanelBounds.top
    const shiftY = Math.min(0, panelTop - handBottom)
    const meldShiftY = shiftY - this.responsiveLocalHandOffsetY
    this.responsiveLocalHandOffsetY = shiftY
    if (shiftY !== 0) sprites.forEach(sprite => { sprite.y += shiftY })
    if (meldShiftY !== 0) this.meldSprites[odr].forEach(sprite => { sprite.y += meldShiftY })
    if (odr !== this.myOdr || shiftY === 0) return
    if (this.selectedCursor) this.selectedCursor.y += shiftY
    if (this.drawnTileCursor) this.drawnTileCursor.y += shiftY
  }

  getActionPanelBounds() {
    return this.actionPanelBounds ?? null
  }

  getActionButtonBounds() {
    const [first, ...rest] = [...this.actionButtonSprites.values()]
      .filter(button => button.background.visible)
      .map(button => button.background.getBounds())
    if (!first) return null
    return rest.reduce(
      (bounds, buttonBounds) => Phaser.Geom.Rectangle.Union(bounds, buttonBounds, bounds),
      first,
    )
  }

  getInitialLocalHandBounds() {
    const initialHandSprites = this.handSprites[this.myOdr]
      .filter(sprite => sprite.active && sprite.visible)
      .slice(0, 13)
    const [first, ...rest] = initialHandSprites
    if (!first) return null
    return rest.reduce(
      (bounds, sprite) => Phaser.Geom.Rectangle.Union(bounds, sprite.getBounds(), bounds),
      first.getBounds(),
    )
  }

  isActionPresentationReady() {
    return this.initialActionPresentationReady && !this.deferInitialActionPresentation && !this.initialDealInProgress
  }

  private updateActionPanelBounds() {
    if (this.layoutMode === 'mobileLandscape') {
      this.actionPanelBounds = undefined
      return
    }
    const offset = this.layoutMode === 'responsiveDesktop'
      ? responsiveDesktopCenterOffset(this.layoutMode)
      : mobileCenterHudOffset(this.layoutMode)
    this.actionPanelBounds = new Phaser.Geom.Rectangle(
      BOARD_X + X_PANEL + offset.x,
      BOARD_Y + Y_PANEL + offset.y + this.responsiveActionPanelOffsetY,
      W_PANEL,
      H_PANEL,
    )
  }

  private redrawDiscardSourceMarker(odr: number) {
    this.discardSourceMarkerSprites[odr]?.destroy()
    this.discardSourceMarkerSprites[odr] = undefined
    const state = this.discardSourceMarkers[odr]
    if (!this.assistConfig.bChkTap || !state || this.isReplay) return
    const loc = odrToLoc(odr, this.myOdr)
    if (loc === 0) return
    const scale = this.handTileScale(odr, loc)
    const isDrawTile = !state.isTedashi
    const renderedHandTile = this.layoutMode === 'responsiveDesktop'
      ? this.handSprites[odr][state.displayIdx]
      : undefined
    let position = renderedHandTile?.active
      ? { x: renderedHandTile.x, y: renderedHandTile.y }
      : isMobileIngameLayout(this.layoutMode)
        ? mobileOuterHandPos(loc, state.displayIdx, state.handCount, isDrawTile, scale) ?? handPos(loc, state.displayIdx, isDrawTile, true)
        : handPos(loc, state.displayIdx, isDrawTile, true)
    if (isMobileIngameLayout(this.layoutMode)) {
      position = offsetDiscardSourceMarker(position, OPN_OFS[loc], scale)
    }
    const texture = loc % 2 === 0 ? 'mj_tapai_0' : 'mj_tapai_1'
    this.discardSourceMarkerSprites[odr] = this.clipToBoard(this.add.image(position.x, position.y, texture, state.isTedashi ? 0 : 1)
      .setOrigin(0, 0)
      .setScale(this.tileRenderScale(scale))
      .setAlpha(32 / 256)
      .setDepth(DISCARD_SOURCE_MARKER_DEPTH))
  }

  private clearDiscardSourceMarker(odr: number) {
    this.discardSourceMarkers[odr] = undefined
    this.discardSourceMarkerSprites[odr]?.destroy()
    this.discardSourceMarkerSprites[odr] = undefined
  }

  private updateMobileActionHandVisibility() {
    if (this.layoutMode !== 'mobileLandscape' || this.isViewer || this.isReplay) return
    const visible = !this.mobileActionButtonsVisible
    this.handSprites[this.myOdr].forEach(sprite => sprite.setVisible(visible))
    this.selectedCursor?.setVisible(visible)
    this.drawnTileCursor?.setVisible(visible)
    this.tenpaiMarkerSprites.forEach(sprite => sprite.setVisible(visible))
  }

  private redrawMobileOpponentHandSummary(odr: number, loc: 0 | 1 | 2 | 3, count: number) {
    const visibleBacks = Math.min(2, Math.max(0, count))
    const textureKey = this.resolveSkinTextureKey(downTexture(loc))
    const start = handPos(loc, 0, false)
    const step = loc === 1
      ? { x: 0, y: -16 }
      : loc === 3
        ? { x: 0, y: 16 }
        : loc === 2
          ? { x: -20, y: 0 }
          : { x: 20, y: 0 }

    for (let idx = 0; idx < visibleBacks; idx++) {
      const x = start.x + step.x * idx
      const y = start.y + step.y * idx
      const sprite = this.clipToBoard(this.add.image(x, y, textureKey)
        .setOrigin(0, 0)
        .setScale(this.tileRenderScale(MOBILE_OPPONENT_SUMMARY_TILE_SCALE))
        .setDepth(handDepth(y, idx)))
      this.handSprites[odr].push(sprite)
    }

    const lastX = start.x + step.x * Math.max(visibleBacks - 1, 0)
    const lastY = start.y + step.y * Math.max(visibleBacks - 1, 0)
    const badgeX = loc === 1 ? lastX + 16 : loc === 3 ? lastX - 32 : loc === 2 ? lastX - 34 : lastX + 22
    const badgeY = loc === 1 ? lastY + 2 : loc === 3 ? lastY + 12 : loc === 2 ? lastY + 18 : lastY + 18
    this.mobileOpponentHandCountTexts[odr] = this.clipToBoard(this.add.text(badgeX, badgeY, String(count), {
      fontFamily: getUiFontFamily(),
      fontSize: getUiFontSize(14),
      fontStyle: 'bold',
      color: '#ffffff',
      backgroundColor: 'rgba(0, 0, 0, 0.68)',
      padding: { left: 5, right: 5, top: 2, bottom: 2 },
    }).setOrigin(0, 0).setDepth(1003))
  }

  private clearTenpaiMarkers() {
    this.tenpaiMarkerSprites.forEach(sprite => sprite.destroy())
    this.tenpaiMarkerSprites = []
  }

  private clearTrainingRecommendation() {
    this.trainingRecommendationRequestSerial++
    this.trainingRecommendationTween?.stop()
    this.trainingRecommendationTween = undefined
    this.trainingRecommendationMarker?.destroy()
    this.trainingRecommendationMarker = undefined
  }

  private async showTrainingRecommendation(actionSeq: number, promptSerial: number) {
    this.clearTrainingRecommendation()
    if (!this.trainingRecommendations || !this.roomId || !this.canDiscardOnTileClick) return
    const requestSerial = this.trainingRecommendationRequestSerial
    const recommendation = await SignalR.invoke<TrainingDiscardRecommendationResponse | null>(
      'GetTrainingDiscardRecommendation',
      Number(this.roomId),
      actionSeq,
    ).catch(() => null)
    if (!recommendation
      || requestSerial !== this.trainingRecommendationRequestSerial
      || promptSerial !== this.actionPromptSerial
      || recommendation.actionSeq !== this.currentActionPrompt?.actionSeq
      || !this.canDiscardOnTileClick)
      return

    const handIdx = this.players[this.myOdr].hand.findIndex(tile => tile.bipaiIndex === recommendation.bipaiIndex)
    const tile = this.handSprites[this.myOdr][handIdx]
    if (handIdx < 0 || !tile?.active) return

    const marker = this.clipToBoard(this.add.image(
      tile.x + tile.displayWidth / 2,
      tile.y - 2 * tile.scaleY,
      this.resolveSkinTextureKey('mj_tenpaiicon'),
    )
      .setOrigin(0.5, 1)
      .setDepth(1002))
    this.trainingRecommendationMarker = marker
    this.trainingRecommendationTween = this.tweens.add({
      targets: marker,
      alpha: 0.2,
      duration: 350,
      yoyo: true,
      repeat: -1,
    })
  }

  private redrawTenpaiMarkers() {
    this.clearTenpaiMarkers()
    if (!this.assistConfig.bChkTnp || this.isReplay || !this.canDiscardOnTileClick || this.players[this.myOdr].isReach) return
    const hand = this.players[this.myOdr].hand
    for (let idx = 0; idx < hand.length; idx++) {
      if (!checkTempaiAfterDiscard(hand, idx)) continue
      const sprite = this.handSprites[this.myOdr][idx]
      if (!sprite) continue
      this.tenpaiMarkerSprites.push(this.clipToBoard(this.add.image(sprite.x + 9, sprite.y - 8, this.resolveSkinTextureKey('mj_tenpaiicon'))
        .setOrigin(0, 0)
        .setDepth(1001)))
    }
  }

  /** 捨て牌再描画 (PutAction discard 相当) */
  private redrawDiscards(odr: number) {
    if (this.isReplayApplyingHistory) return
    this.clearDiscardFlight(odr)
    this.suteSprites[odr].forEach(s => s.destroy())
    this.suteSprites[odr] = []

    const loc = odrToLoc(odr, this.myOdr)
    let nextFlag = 0
    this.players[odr].discards.forEach((discard, idx) => {
      let flag = discard.isReach ? 2 : nextFlag
      if (idx % DISCARD_COLS === 0 && flag === 1) flag = 0
      nextFlag = flag === 2 ? 1 : flag
      const { x, y } = discardPos(loc, idx, flag, this.layoutMode)
      const frame = paiToFrame(discard.code)
      const spr = this.add.image(x, y, this.resolveSkinTextureKey(discardTexture(loc, flag)), frame)
        .setOrigin(0, 0)
        .setScale(this.tileRenderScale(this.discardTileScale()))
        .setDepth(y)
      this.clipToBoard(spr)
      this.bindAssistTileInput(spr, discard.code)
      this.suteSprites[odr].push(spr)
    })
    this.redrawLatestDiscardFrame()
    this.redrawPaifuGraphContent()
  }

  private redrawLatestDiscardFrame() {
    this.latestDiscardFrame?.destroy()
    this.latestDiscardFrame = undefined
    if (this.lastDiscardOdr === null || this.isReplay) return
    const loc = odrToLoc(this.lastDiscardOdr, this.myOdr)
    const discards = this.suteSprites[this.lastDiscardOdr]
    const target = discards[discards.length - 1]
    if (!target?.active) return
    const scale = this.discardTileScale()
    const texture = loc % 2 === 0 ? 'mj_throw_0' : 'mj_throw_1'
    this.latestDiscardFrame = this.clipToBoard(this.add.image(target.x - 4 * scale, target.y - 4 * scale, texture, 0)
      .setOrigin(0, 0)
      .setScale(this.tileRenderScale(scale))
      .setDepth(1002))
  }

  private shouldAnimateLiveDiscard(): boolean {
    return !this.isReplay
      && !this.shouldSuppressLivePlayback()
      && !this.gameResyncInFlight
      && document.visibilityState === 'visible'
  }

  private captureDiscardFlightOrigin(odr: number, handIdx: number): DiscardFlightOrigin | undefined {
    const sprites = this.handSprites[odr]
    const source = sprites[handIdx] ?? sprites[sprites.length - 1]
    if (!source?.active || !source.visible) return undefined
    return {
      x: source.x,
      y: source.y,
      scaleX: source.scaleX,
      scaleY: source.scaleY,
      depth: source.depth,
    }
  }

  private animateLatestDiscard(odr: number, origin: DiscardFlightOrigin | undefined) {
    if (!origin || !this.shouldAnimateLiveDiscard()) return
    const target = this.suteSprites[odr][this.suteSprites[odr].length - 1]
    if (!target?.active) return

    this.clearDiscardFlight(odr)
    target.setVisible(false)
    const sprite = this.clipToBoard(this.add.image(origin.x, origin.y, target.texture.key, target.frame.name)
      .setOrigin(target.originX, target.originY)
      .setScale(origin.scaleX, origin.scaleY)
      .setDepth(Math.max(origin.depth, target.depth) + 1))
    const flight: ActiveDiscardFlight = { sprite, target }
    this.activeDiscardFlights[odr] = flight
    this.latestDiscardFrame?.setVisible(false)

    const progress = { value: 0 }
    const bounds = isCenteredIngameLayout(this.layoutMode) ? mobileVisibleWorldBounds() : null
    const centerX = bounds ? (bounds.left + bounds.right) / 2 : BOARD_X + BOARD_W / 2
    const centerY = bounds ? (bounds.top + bounds.bottom) / 2 : BOARD_Y + BOARD_H / 2
    const controlX = (origin.x + target.x) / 2 + (centerX - (origin.x + target.x) / 2) * 0.08
    const controlY = (origin.y + target.y) / 2 + (centerY - (origin.y + target.y) / 2) * 0.08
    const tiltDirection = odrToLoc(odr, this.myOdr) % 2 === 0 ? 1 : -1

    sprite.setPosition(origin.x, origin.y + tiltDirection * 4)
    flight.tween = this.tweens.add({
      targets: progress,
      value: 1,
      delay: 42,
      duration: 235,
      ease: 'Quart.Out',
      onUpdate: () => {
        const t = progress.value
        const inverse = 1 - t
        const lift = Math.sin(Math.PI * t) * 1.28
        sprite.setPosition(
          inverse * inverse * origin.x + 2 * inverse * t * controlX + t * t * target.x,
          inverse * inverse * origin.y + 2 * inverse * t * controlY + t * t * target.y,
        )
        sprite.setScale(
          Phaser.Math.Linear(origin.scaleX, target.scaleX, t) * (1 + 0.06 * lift),
          Phaser.Math.Linear(origin.scaleY, target.scaleY, t) * (1 + 0.06 * lift),
        )
        sprite.setAngle(tiltDirection * 9 * lift)
      },
      onComplete: () => {
        if (this.activeDiscardFlights[odr] !== flight) return
        if (target.active) target.setVisible(true)
        if (sprite.active) sprite.destroy()
        this.activeDiscardFlights[odr] = undefined
        this.redrawLatestDiscardFrame()
      },
    })
  }

  private clearDiscardFlight(odr: number) {
    const flight = this.activeDiscardFlights[odr]
    if (!flight) return
    this.activeDiscardFlights[odr] = undefined
    flight.tween?.stop()
    if (flight.target.active) flight.target.setVisible(true)
    if (flight.sprite.active) flight.sprite.destroy()
  }

  private clearAllDiscardFlights() {
    for (let odr = 0; odr < this.activeDiscardFlights.length; odr++) this.clearDiscardFlight(odr)
  }

  private redrawMelds(odr: number) {
    if (this.isReplayApplyingHistory) return
    this.meldSprites[odr].forEach(s => s.destroy())
    this.meldSprites[odr] = []

    const loc = odrToLoc(odr, this.myOdr)
    const player = this.players[odr]
    const flowerOffset = player.flowers.length > 0 ? 1 : 0
    const meldScale = this.meldTileScale(odr, loc)

    player.flowers.forEach((tile, col) => {
      const { x, y } = meldPos(loc, 0, col, 0, this.layoutMode, meldScale)
      const adjustedY = y + (this.layoutMode === 'responsiveDesktop' && loc === 0 ? this.responsiveLocalHandOffsetY : 0)
      const frame = paiToFrame(tile.code)
      const texture = this.resolveSkinTexture(meldTexture(loc, 0, false))
      const spr = this.clipToBoard(this.add.image(x, adjustedY, texture.key, frame).setOrigin(0, 0).setScale(this.tileRenderScale(meldScale)).setDepth(adjustedY))
      this.bindAssistTileInput(spr, tile.code)
      this.meldSprites[odr].push(spr)
    })

    player.melds.forEach((meld, meldIdx) => {
      const row = meldIdx + flowerOffset
      meld.tiles.forEach((tile, col) => {
        const { x, y } = meldPos(loc, row, col, tile.flag, this.layoutMode, meldScale)
        const adjustedY = y + (this.layoutMode === 'responsiveDesktop' && loc === 0 ? this.responsiveLocalHandOffsetY : 0)
        const texture = this.resolveSkinTexture(meldTexture(loc, tile.flag, Boolean(tile.isDown)))
        const frame = texture.frame ?? paiToFrame(tile.code)
        const spr = this.clipToBoard(this.add.image(x, adjustedY, texture.key, frame).setOrigin(0, 0).setScale(this.tileRenderScale(meldScale)).setDepth(adjustedY))
        if (!tile.isDown) this.bindAssistTileInput(spr, tile.code)
        this.meldSprites[odr].push(spr)
      })
    })
    this.redrawPaifuGraphContent()
  }

  private clearDeadWall() {
    this.deadWallSprites.forEach(sprite => sprite.destroy())
    this.deadWallSprites = []
  }

  private animateMatchStartSeatReveal() {
    const shouldReveal = this.pendingMatchStartSeatReveal && this.shouldPlayLegacyVisuals()
    this.pendingMatchStartSeatReveal = false
    if (!shouldReveal) return 0

    const scale = this.tileScale()
    const seatPoint = (loc: 0 | 1 | 2 | 3, offset = { x: 0, y: 0 }) => {
      if (isMobileIngameLayout(this.layoutMode)) {
        const point = mobileOuterHandPos(loc, 6, MOBILE_OTHER_HAND_FIXED_COUNT, false, scale)
        if (point) return { x: point.x + offset.x, y: point.y + offset.y }
      }
      const base = MATCH_START_SEAT_POSITIONS[loc]
      return boardEdgePoint({ x: base.x + offset.x, y: base.y + offset.y }, loc, this.layoutMode)
    }
    const sprites = this.players.map((_player, odr) => {
      const loc = odrToLoc(odr, this.myOdr)
      const offset = MATCH_START_OPEN_OFFSETS[loc]
      const point = seatPoint(loc, offset)
      const sprite = this.clipToBoard(this.add.image(point.x, point.y, this.resolveSkinTextureKey(downTexture(loc)))
        .setOrigin(0, 0)
        .setScale(this.tileRenderScale(scale))
        .setDepth(1000 + loc))
      this.legacyEffectSprites.push(sprite)
      return { sprite, loc, odr }
    })

    this.time.delayedCall(1000, () => {
      sprites.forEach(({ sprite, loc, odr }) => {
        if (!sprite.active) return
        const point = seatPoint(loc)
        const frame = paiToFrame(0x31 + odr)
        if (loc === 0) sprite.setTexture(this.resolveSkinTextureKey('hai_dora'), frame)
        else sprite.setTexture(this.resolveSkinTextureKey(`hai_tachi_${loc}`))
        sprite.setPosition(point.x, point.y)
      })
      playMajakSid(SID_EXPOSE, this.soundSkinOptions())
    })

    this.time.delayedCall(1100, () => {
      sprites.forEach(({ sprite, loc, odr }) => {
        if (!sprite.active) return
        const offset = MATCH_START_OPEN_OFFSETS[loc]
        const point = seatPoint(loc, offset)
        const texture = loc === 0 ? 'hai_sute' : `hai_open_${loc}`
        sprite.setTexture(this.resolveSkinTextureKey(texture), paiToFrame(0x31 + odr))
        sprite.setPosition(point.x, point.y)
      })
      playMajakSid(SID_EXPOSE, this.soundSkinOptions())
    })

    this.time.delayedCall(MATCH_START_SEAT_REVEAL_DURATION_MS, () => {
      sprites.forEach(({ sprite }) => sprite.destroy())
      this.legacyEffectSprites = this.legacyEffectSprites.filter(sprite => sprite.active)
    })
    return MATCH_START_SEAT_REVEAL_DURATION_MS
  }

  private redrawDeadWall(showDora = true) {
    if (this.isReplayApplyingHistory) return
    this.clearDeadWall()
    const haipaiPos = this.currentHaipaiPos()
    if (haipaiPos === undefined) return

    const exposed = new Map<number, number>()
    if (showDora) {
      this.paifuGraphRound.dora.forEach((code, idx) => {
        if (!code || code <= 0) return
        const wallIdx = this.deadWallIndexForBipai(this.getDoraIndex(idx), haipaiPos)
        if (wallIdx !== undefined) exposed.set(wallIdx, code)
      })
    }

    for (let idx = 0; idx < DEAD_WALL_COUNT; idx++) {
      const { x, y } = deadWallPos(idx, this.layoutMode)
      const code = exposed.get(idx)
      const sprite = code
        ? this.add.image(x, y, this.resolveSkinTextureKey('hai_open_2'), paiToFrame(code))
        : this.add.image(x, y, this.resolveSkinTextureKey('hai_ura_2'))
      this.clipToBoard(sprite
        .setOrigin(0, 0)
        .setScale(this.tileRenderScale(this.deadWallTileScale()))
        .setDepth(y + (idx % 2 === 0 ? WAN_EXPOSE_OFFSET_Y * 2 : 0)))
      if (code) this.bindAssistTileInput(sprite, code)
      this.deadWallSprites.push(sprite)
    }
  }

  private animateInitialDeal(oyaOrder: number, startDelay = 0, onComplete?: () => void) {
    if (!this.shouldPlayInitialDeal()) {
      onComplete?.()
      return 0
    }
    const serial = ++this.initialDealSerial
    this.initialDealInProgress = true
    this.initialDealVisibleCounts = [0, 0, 0, 0]
    this.handSprites.forEach((_, odr) => this.applyInitialDealVisibility(odr))
    let step = 0
    let odr = oyaOrder
    for (let round = 0; round < 3; round++) {
      for (let player = 0; player < this.players.length; player++) {
        const revealOdr = odr
        const visibleCount = (round + 1) * 4
        this.time.delayedCall(startDelay + step++ * 100, () => {
          if (!this.initialDealInProgress || serial !== this.initialDealSerial) return
          this.revealInitialDealTiles(revealOdr, visibleCount)
        })
        odr = (odr + 1) % this.players.length
      }
    }
    for (let player = 0; player < this.players.length; player++) {
      const revealOdr = odr
      this.time.delayedCall(startDelay + step++ * 100, () => {
        if (!this.initialDealInProgress || serial !== this.initialDealSerial) return
        this.revealInitialDealTiles(revealOdr, 13)
      })
      odr = (odr + 1) % this.players.length
    }
    this.time.delayedCall(startDelay + step * 100, () => {
      if (!this.initialDealInProgress || serial !== this.initialDealSerial) return
      this.initialDealInProgress = false
      onComplete?.()
    })
    return startDelay + step * 100
  }

  private applyInitialDealVisibility(odr: number) {
    if (!this.initialDealInProgress) return
    const visibleCount = this.initialDealVisibleCounts[odr] ?? 0
    this.handSprites[odr].forEach((sprite, idx) => {
      if (sprite.active) sprite.setVisible(idx < visibleCount)
    })
  }

  private revealInitialDealTiles(odr: number, visibleCount: number) {
    const previousCount = this.initialDealVisibleCounts[odr] ?? 0
    this.initialDealVisibleCounts[odr] = visibleCount
    this.applyInitialDealVisibility(odr)

    const bounds = isCenteredIngameLayout(this.layoutMode) ? mobileVisibleWorldBounds() : null
    const centerX = bounds ? (bounds.left + bounds.right) / 2 : BOARD_X + BOARD_W / 2
    const centerY = bounds ? (bounds.top + bounds.bottom) / 2 : BOARD_Y + BOARD_H / 2
    const loc = odrToLoc(odr, this.myOdr)
    const direction = [{ x: 0, y: 1 }, { x: -1, y: 0 }, { x: 0, y: -1 }, { x: 1, y: 0 }][loc]

    this.handSprites[odr].slice(previousCount, visibleCount).forEach((sprite, index) => {
      if (!sprite.active) return
      const targetX = sprite.x
      const targetY = sprite.y
      sprite.setPosition(centerX + direction.x * 34, centerY + direction.y * 24)
      sprite.setAlpha(0.35)
      sprite.setScale(sprite.scaleX * 0.94, sprite.scaleY * 0.94)
      this.tweens.add({
        targets: sprite,
        x: targetX,
        y: targetY,
        alpha: 1,
        scaleX: sprite.scaleX / 0.94,
        scaleY: sprite.scaleY / 0.94,
        duration: 185,
        delay: index * 18,
        ease: 'Cubic.Out',
      })
    })
  }

  private animateDoraReveal(doraIndexes: number[]) {
    if (!this.shouldPlayLegacyVisuals()) return
    const haipaiPos = this.currentHaipaiPos()
    if (haipaiPos === undefined) return
    doraIndexes.forEach(doraIndex => {
      const wallIndex = this.deadWallIndexForBipai(this.getDoraIndex(doraIndex), haipaiPos)
      const sprite = wallIndex === undefined ? undefined : this.deadWallSprites[wallIndex]
      const code = this.paifuGraphRound.dora[doraIndex]
      if (!sprite?.active || !code || code <= 0) return
      const originalY = sprite.y
      sprite.setTexture(this.resolveSkinTextureKey('hai_ura_2')).setY(originalY - 5 * this.tileScale())
      playMajakSid(SID_EXPOSE, this.soundSkinOptions())
      this.time.delayedCall(100, () => {
        if (!sprite.active) return
        sprite.setTexture(this.resolveSkinTextureKey('hai_open_2'), paiToFrame(code)).setY(originalY)
        playMajakSid(SID_EXPOSE, this.soundSkinOptions())
      })
    })
  }

  private currentHaipaiPos(): number | undefined {
    const oyaOrder = this.mod(this.chicha + this.paifuGraphRound.kyokuCnt, this.players.length)
    return this.getHaipaiPos(oyaOrder, this.paifuGraphRound.dice)
  }

  private deadWallIndexForBipai(bipaiIndex: number | undefined, haipaiPos: number): number | undefined {
    if (bipaiIndex === undefined) return undefined
    const wallIndex = this.mod(bipaiIndex + BIPAI_MAX_COUNT - haipaiPos, BIPAI_MAX_COUNT)
    if (wallIndex < DEAD_WALL_START || wallIndex >= BIPAI_MAX_COUNT) return undefined
    return wallIndex - DEAD_WALL_START
  }

  private showPaifuGraph() {
    if (this.paifuGraphLayer) {
      this.paifuGraphLayer.setVisible(true)
      this.redrawPaifuGraphContent()
      return
    }

    const graph = this.paifuGraphBounds()
    const layer = this.add.container(0, 0).setDepth(3000)
    const shade = this.add.rectangle(0, 0, this.scale.width, this.scale.height, 0x000000, 0.45)
      .setOrigin(0, 0)
      .setInteractive()
    const body = this.add.container(graph.x, graph.y).setScale(graph.scale)
    body.add(this.add.image(0, 0, this.resolveSkinTextureKey('mj_recBg')).setOrigin(0, 0))

    layer.add([shade, body])
    this.paifuGraphLayer = layer
    this.paifuGraphBody = body
    this.redrawPaifuGraphContent()
  }

  private paifuGraphBounds() {
    if (this.layoutMode !== 'mobileLandscape') {
      const scale = 1.22
      return {
        ...PAIFU_GRAPH,
        x: (this.scale.width - PAIFU_GRAPH.w * scale) / 2,
        y: (this.scale.height - PAIFU_GRAPH.h * scale) / 2,
        scale,
      }
    }

    const bounds = mobileVisibleWorldBounds()
    if (!bounds) return { ...PAIFU_GRAPH, x: 1, scale: 0.8 }

    const width = bounds.right - bounds.left
    const height = bounds.bottom - bounds.top
    const scale = Math.min(1, (width - 8) / PAIFU_GRAPH.w, (height - 8) / PAIFU_GRAPH.h)
    const scaledWidth = PAIFU_GRAPH.w * scale
    const scaledHeight = PAIFU_GRAPH.h * scale
    return {
      ...PAIFU_GRAPH,
      x: bounds.left + (width - scaledWidth) / 2,
      y: bounds.top + (height - scaledHeight) / 2,
      scale,
    }
  }

  private hidePaifuGraph() {
    this.paifuGraphObjects.forEach(obj => obj.destroy())
    this.paifuGraphObjects = []
    this.paifuGraphBody = undefined
    this.paifuGraphLayer?.destroy()
    this.paifuGraphLayer = undefined
  }

  private redrawPaifuGraphContent() {
    if (this.isReplayApplyingHistory) return
    if (!this.paifuGraphBody || !this.paifuGraphLayer?.visible) return

    this.paifuGraphObjects.forEach(obj => obj.destroy())
    this.paifuGraphObjects = []

    this.addPaifuGraphRoundInfo()

    for (let odr = 0; odr < this.players.length; odr++) {
      const row = PAIFU_GRAPH_ROWS[odr]
      const player = this.players[odr]
      this.addPaifuGraphText(0, row.name.y - 2, player.name || `Player ${odr + 1}`, row.initial.x, 'center', 14, '#ffffff')
      this.addPaifuGraphText(row.point.x, row.point.y, String(player.score), 56, 'center', 14, '#ffffff')

      const initialHand = this.paifuGraphInitialHands[odr].length > 0
        ? this.paifuGraphInitialHands[odr]
        : player.hand.slice(0, Math.min(13, player.hand.length))
      initialHand.slice(0, 13).forEach((tile, idx) => {
        this.addPaifuGraphTile('mj_recPaeFt', paiToFrame(tile.code), row.initial.x + 20 * idx, row.initial.y)
      })

      this.paifuGraphDraws[odr].forEach((draw, idx) => {
        const texture = draw.small ? 'mj_recPaeSm' : 'mj_recPaeFt'
        const x = row.draw.x + 20 * idx + (draw.small ? 7 : 0)
        const y = row.draw.y + (draw.small ? 10 : 0)
        this.addPaifuGraphTile(texture, paiToFrame(draw.code), x, y)
        if (draw.noteFrame !== undefined) {
          this.addPaifuGraphTile('mj_recNotesIcn', draw.noteFrame, row.draw.x + 20 * idx, row.draw.y - 9)
        }
      })

      const graphDiscards = this.paifuGraphDiscards[odr].length > 0 ? this.paifuGraphDiscards[odr] : player.discards
      graphDiscards.forEach((discard, idx) => {
        const x = row.discard.x + 20 * idx
        this.addPaifuGraphTile('mj_recPaeFt', paiToFrame(discard.code), x, row.discard.y)
        if (discard.isReach) this.addPaifuGraphImage('mj_recReachBar', x, row.discard.y - 3)
      })

      const closedHand = player.hand.slice(0, player.hand.length % 3 === 2 ? -1 : undefined)
      closedHand.forEach((tile, idx) => {
        this.addPaifuGraphTile('mj_recPaeFt', paiToFrame(tile.code), row.final.x + 20 * idx, row.final.y)
      })
      this.addPaifuGraphMelds(row.final.x + 20 * closedHand.length + 23, row.final.y, player)
    }
  }

  private addPaifuGraphRoundInfo() {
    const windFrame = Math.floor(this.paifuGraphRound.kyokuCnt / 4)
    const kyokuFrame = this.paifuGraphRound.kyokuCnt % 4
    this.addPaifuGraphTile('mj_kyoku', Phaser.Math.Clamp(windFrame, 0, 3), 11, 15)
    this.addPaifuGraphTile('mj_kyokuNum', Phaser.Math.Clamp(kyokuFrame, 0, 3), 44, 15)
    this.addPaifuGraphStkNumber(143, 25, this.paifuGraphRound.ribo)
    this.addPaifuGraphStkNumber(170, 25, this.paifuGraphRound.renchan)
    this.paifuGraphRound.dice.slice(0, 2).forEach((dice, idx) => {
      this.addPaifuGraphTile('mj_dice', Phaser.Math.Clamp(Math.trunc(dice), 0, 5), idx === 0 ? 227 : 242, 29)
    })
    this.paifuGraphRound.dora.forEach((code, idx) => {
      this.addPaifuGraphTile('mj_recPaeFt', paiToFrame(code), 295 + 20 * idx, 22)
    })
    this.paifuGraphRound.uraDora.forEach((code, idx) => {
      this.addPaifuGraphTile('mj_recPaeFt', paiToFrame(code), 400 + 20 * idx, 22)
    })
    PAIFU_GRAPH_OPTIONS.forEach((option, idx) => {
      const frame = this.readOptionDigit(this.paifuGraphRound.roomOption, option.optionIndex, option.defaultFrame)
      this.addPaifuGraphTile(option.key, frame, 534 + 17 * idx, 30)
    })
  }

  private addPaifuGraphMelds(startX: number, y: number, player: PlayerState) {
    let x = startX
    const melds = [...player.melds].reverse()
    for (const meld of melds) {
      x += PAIFU_GRAPH_MELD_GAP
      const width = meld.tiles.length === 4 ? 88 : 68
      meld.tiles.forEach((tile, idx) => {
        const tileX = x + 20 * idx
        if (tile.isDown) {
          this.addPaifuGraphImage('mj_recPaeBk', tileX, y)
        } else if (tile.flag === 2) {
          this.addPaifuGraphTile('mj_recPaeSd', paiToFrame(tile.code), tileX - 8, y + 7)
        } else {
          this.addPaifuGraphTile('mj_recPaeFt', paiToFrame(tile.code), tileX + (tile.flag === 1 ? -8 : 0), y)
        }
      })
      x += width
    }
    if (player.flowers.length > 0) x += PAIFU_GRAPH_MELD_GAP
    player.flowers.forEach((tile, idx) => {
      this.addPaifuGraphTile('mj_recPaeFt', paiToFrame(tile.code), x + 20 * idx, y)
    })
  }

  private addPaifuGraphStkNumber(x: number, y: number, value: number) {
    String(Math.max(0, Math.trunc(value))).split('').forEach((digit, idx) => {
      this.addPaifuGraphTile('mj_num_rh', Number(digit), x + 10 * idx, y)
    })
  }

  private addPaifuGraphText(
    x: number,
    y: number,
    text: string,
    width: number,
    align: 'left' | 'right' | 'center' = 'center',
    fontSize = 12,
    color = '#000000',
  ) {
    const obj = this.add.text(x, y, text, {
      fontFamily: getUiFontFamily(), fontSize: getUiFontSize(fontSize), color,
      fixedWidth: width, align,
    }).setCrop(0, 0, width, Math.max(14, fontSize + 4))
    this.paifuGraphBody?.add(obj)
    this.paifuGraphObjects.push(obj)
  }

  private addPaifuGraphTile(texture: string, frame: number, x: number, y: number) {
    const obj = this.add.image(x, y, this.resolveSkinTextureKey(texture), frame).setOrigin(0, 0)
    this.paifuGraphBody?.add(obj)
    this.paifuGraphObjects.push(obj)
  }

  private addPaifuGraphImage(texture: string, x: number, y: number) {
    const obj = this.add.image(x, y, this.resolveSkinTextureKey(texture)).setOrigin(0, 0)
    this.paifuGraphBody?.add(obj)
    this.paifuGraphObjects.push(obj)
  }

  private addPaifuGraphCall(odr: number, code: number, action: Act) {
    if (code <= 0 || odr < 0 || odr >= this.players.length) return
    const noteFrame = action === Act.Chi ? 1
      : action === Act.Pon ? 0
        : action === Act.Hua ? 5
          : 2
    this.paifuGraphDraws[odr].push({
      code,
      small: action === Act.Kan || action === Act.Ank || action === Act.Cha || action === Act.Hua,
      noteFrame,
    })
    this.redrawPaifuGraphContent()
  }

  private cloneTiles(tiles: TileState[]): TileState[] {
    return tiles.map(tile => ({ ...tile }))
  }

  private readOptionDigit(option: string, index: number, fallback: number): number {
    const digit = option[index]
    return digit >= '0' && digit <= '9' ? Number(digit) : fallback
  }

  /* ======================================================================
   * タイル選択 / 打牌 (CMJUserIF 相当)
   * ====================================================================== */
  private onTilePointerOver(idx: number, pointer?: Phaser.Input.Pointer) {
    if (isTouchPointer(pointer)) return
    if (DEBUG_GAME && this.canDiscardOnTileClick) console.info('[GameScene] tile hover discard candidate', {
      idx,
      myOdr: this.myOdr,
      tile: this.players[this.myOdr].hand[idx],
    })
    const hand = this.players[this.myOdr].hand
    if (idx < 0 || idx >= hand.length) return
    this.activeAssistTileCode = hand[idx].code
    this.activeAssistHandIdx = idx
    this.showAssistHighlights(hand[idx].code)
    const tileSprite = this.handSprites[this.myOdr][idx]
    this.hoverCursor?.destroy()
    if (tileSprite && this.canDiscardOnTileClick) this.hoverCursor = this.createLegacyHoverCursor(tileSprite)
    if (this.canDiscardOnTileClick) this.showWaitTileGuide(idx)
  }

  private createLegacyHoverCursor(tile: Phaser.GameObjects.Image) {
    const cursorScale = this.usesHighDensityHaiTextures ? 2 : 1
    return this.clipToBoard(this.add.image(tile.x, tile.y - 5 * tile.scaleY, 'cursor_mouse')
      .setOrigin(0, 0)
      .setScale(tile.scaleX * cursorScale, tile.scaleY * cursorScale)
      .setDepth(1001))
  }

  private createLegacyReceivedTileCursor(tile: Phaser.GameObjects.Image) {
    const cursorScale = this.usesHighDensityHaiTextures ? 2 : 1
    return this.clipToBoard(this.add.image(tile.x, tile.y - 6 * tile.scaleY, 'cursor_keyboard')
      .setOrigin(0, 0)
      .setScale(tile.scaleX * cursorScale, tile.scaleY * cursorScale)
      .setDepth(1000))
  }

  private createTileSelectionFrame(tile: Phaser.GameObjects.Image) {
    return this.createTileOutline(tile, 0xe52b2b)
  }

  private createTileOutline(tile: Phaser.GameObjects.Image, color: number) {
    const lineWidth = Math.max(1, 1.5 * tile.scaleX)
    const marginX = 3 * tile.scaleX
    const marginY = 3 * tile.scaleY
    const frame = this.add.graphics()
      .lineStyle(lineWidth, color, 1)
      .strokeRect(
        tile.x - marginX,
        tile.y - marginY,
        tile.displayWidth + marginX * 2,
        tile.displayHeight + marginY * 2,
      )
      .setDepth(tile.depth + 0.002)
    return this.clipToBoard(frame)
  }

  private onTilePointerOut(_idx: number, pointer?: Phaser.Input.Pointer) {
    if (isTouchPointer(pointer)) return
    this.hoverCursor?.destroy()
    this.hoverCursor = undefined
    this.clearWaitTileGuide()
    this.activeAssistTileCode = 0
    this.activeAssistHandIdx = -1
    this.clearAssistHighlights()
  }

  private bindAssistTileInput(sprite: Phaser.GameObjects.Image, code: number) {
    if (code <= 0) return
    sprite.setInteractive({ useHandCursor: true })
      .on('pointerover', (pointer: Phaser.Input.Pointer) => {
        if (isTouchPointer(pointer)) return
        this.activeAssistTileCode = code
        this.showAssistHighlights(code)
      })
      .on('pointerout', (pointer: Phaser.Input.Pointer) => {
        if (isTouchPointer(pointer)) return
        this.activeAssistTileCode = 0
        this.clearAssistHighlights()
      })
      .on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        if (!isTouchPointer(pointer)) return
        this.activeAssistTileCode = code
        this.activeAssistHandIdx = -1
        this.clearWaitTileGuide()
        this.showAssistHighlights(code)
      })
  }

  private showAssistHighlights(sourceCode: number) {
    this.clearAssistHighlights()
    if (!this.assistConfig.bChkPai || sourceCode <= 0) return
    const addMask = (code: number, sprite?: Phaser.GameObjects.Image) => {
      if (!sprite?.active || !sprite.visible) return
      const mask = assistTileMask(sourceCode, code)
      if (mask === 0) return
      const texture = sprite.displayWidth > sprite.displayHeight ? 'mj_tonari_1' : 'mj_tonari_0'
      this.assistHighlightSprites.push(this.clipToBoard(this.add.image(sprite.x, sprite.y, texture, mask - 1)
        .setOrigin(0, 0)
        .setDisplaySize(sprite.displayWidth, sprite.displayHeight)
        .setAlpha(0.55)
        .setBlendMode(Phaser.BlendModes.MULTIPLY)
        .setDepth(sprite.depth + 0.001)))
    }

    this.players.forEach((player, odr) => {
      player.discards.forEach((discard, idx) => addMask(discard.code, this.suteSprites[odr][idx]))
      let spriteIdx = 0
      player.flowers.forEach(tile => addMask(tile.code, this.meldSprites[odr][spriteIdx++]))
      player.melds.forEach(meld => meld.tiles.forEach(tile => {
        const sprite = this.meldSprites[odr][spriteIdx++]
        if (!tile.isDown) addMask(tile.code, sprite)
      }))
    })

    const haipaiPos = this.currentHaipaiPos()
    if (haipaiPos === undefined) return
    this.paifuGraphRound.dora.forEach((code, idx) => {
      const wallIdx = this.deadWallIndexForBipai(this.getDoraIndex(idx), haipaiPos)
      if (wallIdx !== undefined) addMask(code, this.deadWallSprites[wallIdx])
    })
  }

  private clearAssistHighlights() {
    this.assistHighlightSprites.forEach(sprite => sprite.destroy())
    this.assistHighlightSprites = []
  }

  private clearWaitTileGuide() {
    this.waitGuideRequestSerial++
    this.waitTileGuideContainer?.destroy()
    this.waitTileGuideContainer = undefined
  }

  private showWaitTileGuide(idx: number) {
    this.clearWaitTileGuide()
    if (!this.assistConfig.bChkHor || this.isReplay || !this.canDiscardOnTileClick || this.players[this.myOdr].isReach) return
    const hand = this.players[this.myOdr].hand
    if (idx < 0 || idx >= hand.length) return
    const entries = this.calculateWaitTileGuide(idx)
    if (entries.length === 0) return
    const requestSerial = this.waitGuideRequestSerial
    this.renderWaitTileGuide(idx, entries)
    void this.enrichWaitTileGuide(idx, entries, requestSerial)
  }

  private renderWaitTileGuide(idx: number, entries: WaitTileGuideEntry[]) {
    this.waitTileGuideContainer?.destroy()
    this.waitTileGuideContainer = undefined

    const wL = 30
    const wM = 34
    const wR = 9
    const xN = 39
    const yN = 3
    const yP = 16
    const yF = 58
    const yH = 71
    const guideHeight = 85
    let yPos = 488
    if (isMobileIngameLayout(this.layoutMode)) {
      const bounds = mobileVisibleWorldBounds()
      const handScale = MOBILE_SELF_HAND_TILE_SCALE * mobileContentScale()
      const handStart = mobileOuterHandPos(0, 0, MOBILE_SELF_HAND_FIXED_COUNT, false, handScale)
      if (bounds && handStart) {
        yPos = waitGuideWorldY(handStart.y, bounds.top, bounds.bottom, guideHeight) - BOARD_Y
      }
    }
    const width = wL + wR + wM * entries.length
    const tileSprite = this.handSprites[this.myOdr][idx]
    const responsiveBounds = this.layoutMode === 'responsiveDesktop' ? responsiveDesktopVisibleWorldBounds() : null
    const point = responsiveBounds && tileSprite?.active
      ? {
          x: Phaser.Math.Clamp(tileSprite.x + 37 / 2 - width / 2, responsiveBounds.left, Math.max(responsiveBounds.left, responsiveBounds.right - width)),
          y: waitGuideWorldY(tileSprite.y, responsiveBounds.top, responsiveBounds.bottom, guideHeight),
        }
      : (() => {
          const rawX = (tileSprite?.x ?? BOARD_X) - BOARD_X + 37 / 2 - width / 2
          const localX = Math.min(Math.max(0, rawX), BOARD_W - width)
          return boardLocalPoint({ x: localX, y: yPos })
        })()
    const guide = this.clipToBoard(this.add.container(point.x, point.y).setDepth(2000))

    guide.add(this.add.image(0, 0, this.resolveSkinTextureKey('mj_machihai_base01')).setOrigin(0, 0).setAlpha(0.5))
    guide.add(this.add.image(0, 0, this.resolveSkinTextureKey('mj_machihai_frame01')).setOrigin(0, 0))
    entries.forEach((entry, entryIdx) => {
      const x = wL + entryIdx * wM
      guide.add(this.add.image(x, 0, this.resolveSkinTextureKey('mj_machihai_base02')).setOrigin(0, 0).setAlpha(0.5))
      guide.add(this.add.image(x, 0, this.resolveSkinTextureKey('mj_machihai_frame02')).setOrigin(0, 0))
      guide.add(this.add.image(xN + entryIdx * wM, yN, this.resolveSkinTextureKey('mj_machihai_num'), Math.max(0, Math.min(9, entry.rest))).setOrigin(0, 0))
      guide.add(this.add.image(x, yP, this.resolveSkinTextureKey('hai_omote'), paiToFrame(entry.code)).setOrigin(0, 0))
      if (entry.furiten) guide.add(this.add.image(x, yF, this.resolveSkinTextureKey('mj_machihai_furiten')).setOrigin(0, 0))
      if (entry.noYaku) {
        guide.add(this.add.image(x, yH, this.resolveSkinTextureKey('mj_machihai_yakunashi')).setOrigin(0, 0))
      } else if (entry.isYakuman) {
        guide.add(this.add.image(x, yH, this.resolveSkinTextureKey('mj_machihai_yakuman')).setOrigin(0, 0))
      } else if (entry.han !== undefined) {
        const digits = String(Math.max(0, Math.min(99, entry.han))).padStart(2, '0').slice(-2)
        digits.split('').forEach((digit, digitIdx) => {
          guide.add(this.add.image(xN - 9 + entryIdx * wM + digitIdx * 9, yH, this.resolveSkinTextureKey('mj_machihai_num'), Number(digit)).setOrigin(0, 0))
        })
        guide.add(this.add.image(x, yH, this.resolveSkinTextureKey('mj_machihai_han')).setOrigin(0, 0))
      }
    })
    const rightX = wL + wM * entries.length
    guide.add(this.add.image(rightX, 0, this.resolveSkinTextureKey('mj_machihai_base03')).setOrigin(0, 0).setAlpha(0.5))
    guide.add(this.add.image(rightX, 0, this.resolveSkinTextureKey('mj_machihai_frame03')).setOrigin(0, 0))
    this.waitTileGuideContainer = guide
  }

  private async enrichWaitTileGuide(idx: number, entries: WaitTileGuideEntry[], requestSerial: number) {
    const tile = this.players[this.myOdr].hand[idx]
    const bipaiIndex = tile?.bipaiIndex
    const actionSeq = this.currentActionPrompt?.actionSeq
    if (bipaiIndex === undefined || actionSeq === undefined || !this.roomId) return
    const cacheKey = `${actionSeq}:${bipaiIndex}`
    let request = this.waitGuidePreviewCache.get(cacheKey)
    if (!request) {
      request = SignalR.invoke<WaitGuidePreviewResponse | null>('GetWaitGuidePreview', Number(this.roomId), bipaiIndex)
        .catch(() => null)
      this.waitGuidePreviewCache.set(cacheKey, request)
    }
    const preview = await request
    const currentTile = this.players[this.myOdr].hand[idx]
    if (!preview
      || requestSerial !== this.waitGuideRequestSerial
      || !this.assistConfig.bChkHor
      || this.activeAssistHandIdx !== idx
      || this.currentActionPrompt?.actionSeq !== actionSeq
      || currentTile?.bipaiIndex !== bipaiIndex
      || preview.discardCode !== tile.code)
      return

    const yakuBySerial = new Map(preview.waits.map(wait => [wait.serial, wait]))
    const enriched = entries.map(entry => {
      const yaku = yakuBySerial.get(paiToSerial(entry.code))
      return yaku ? { ...entry, han: yaku.han, noYaku: yaku.noYaku, isYakuman: yaku.isYakuman } : entry
    })
    this.renderWaitTileGuide(idx, enriched)
  }

  private calculateWaitTileGuide(discardIdx: number): WaitTileGuideEntry[] {
    const hand = this.players[this.myOdr].hand
    const counts = Array.from({ length: 34 }, () => 0)
    for (let idx = 0; idx < hand.length; idx++) {
      if (idx === discardIdx) continue
      const serial = paiToSerial(hand[idx].code)
      if (serial < 0) return []
      counts[serial]++
    }
    const handCount = Math.max(0, hand.length - 1)
    const visibleCounts = this.visibleTileCounts()
    const selfDiscardCounts = this.selfDiscardCounts()
    const discardSerial = paiToSerial(hand[discardIdx]?.code ?? 0)
    if (discardSerial >= 0) selfDiscardCounts[discardSerial]++

    const entries: WaitTileGuideEntry[] = []
    for (let serial = 0; serial < 34; serial++) {
      if (!hasHoraFormAfterAdd(counts, handCount, serial)) continue
      entries.push({
        code: serialToPaiCode(serial),
        rest: Math.max(0, 4 - visibleCounts[serial]),
        furiten: selfDiscardCounts[serial] > 0,
      })
    }
    return entries.slice(0, 13)
  }

  private visibleTileCounts(): number[] {
    const counts = Array.from({ length: 34 }, () => 0)
    const addCode = (code: number) => {
      const serial = paiToSerial(code)
      if (serial >= 0) counts[serial]++
    }
    this.players[this.myOdr].hand.forEach(tile => addCode(tile.code))
    this.players.forEach(player => {
      player.discards.forEach(tile => addCode(tile.code))
      player.flowers.forEach(tile => addCode(tile.code))
      player.melds.forEach(meld => meld.tiles.forEach(tile => addCode(tile.code)))
    })
    this.paifuGraphRound.dora.forEach(addCode)
    return counts
  }

  private selfDiscardCounts(): number[] {
    const counts = Array.from({ length: 34 }, () => 0)
    this.players[this.myOdr].discards.forEach(tile => {
      const serial = paiToSerial(tile.code)
      if (serial >= 0) counts[serial]++
    })
    return counts
  }

  private setupKeyboardEvents() {
    this.teardownKeyboardEvents()
    this.keyboardHandler = (event: KeyboardEvent) => this.handleKeyboardEvent(event)
    window.addEventListener('keydown', this.keyboardHandler)
  }

  private teardownKeyboardEvents() {
    if (!this.keyboardHandler) return
    window.removeEventListener('keydown', this.keyboardHandler)
    this.keyboardHandler = undefined
  }

  private setupContextMenuEvents() {
    this.teardownContextMenuEvents()
    this.contextMenuHandler = (event: MouseEvent) => this.handleContextMenu(event)
    this.game.canvas.addEventListener('contextmenu', this.contextMenuHandler)
  }

  private teardownContextMenuEvents() {
    if (!this.contextMenuHandler) return
    this.game.canvas.removeEventListener('contextmenu', this.contextMenuHandler)
    this.contextMenuHandler = undefined
  }

  private handleContextMenu(event: MouseEvent) {
    event.preventDefault()
    if (this.isReplay || this.actionSendInFlight) return
    if (!this.isLocalPlayerOdr(this.currentActionSeatOrder)) return
    if (!this.actionOfferByName.has('Pass')) return
    const visibleActs = this.resolveVisibleActions(this.currentActionOffers)
    if (visibleActs.size === 1 && visibleActs.has('Pass')) return
    const pass = this.ACT_BTNS.find(button => button.act === 'Pass')
    if (pass) void this.sendAction(pass, this.currentActionOffers)
  }

  private handleKeyboardEvent(event: KeyboardEvent) {
    if (event.code === 'Escape') {
      window.dispatchEvent(new CustomEvent(GAME_FOCUS_CHAT_EVENT))
      event.preventDefault()
      return
    }
    if (this.isReplay || this.actionSendInFlight) return
    if (this.currentActionSeatOrder !== null && !this.isLocalPlayerOdr(this.currentActionSeatOrder)) return
    const canSelectTile = this.canDiscardOnTileClick || this.pendingActionChoice !== null
    const canSelectAction = this.getKeyboardActionDefs().length > 0
    if (!canSelectTile && !canSelectAction) return

    switch (event.code) {
      case 'ArrowLeft':
      case 'Numpad4':
        this.keyboardActionIndex = -1
        if (this.moveHandSelection(-1)) event.preventDefault()
        break
      case 'ArrowRight':
      case 'Numpad6':
        this.keyboardActionIndex = -1
        if (this.moveHandSelection(1)) event.preventDefault()
        break
      case 'ArrowDown':
      case 'Numpad2':
        {
          const heldAutoPass = this.suspendAutoPassForCurrentPrompt()
          const movedSelection = this.moveKeyboardActionSelection(1)
          if (heldAutoPass || movedSelection) event.preventDefault()
        }
        break
      case 'ArrowUp':
      case 'Numpad8':
        if (this.inputConfig.nSelPasKey === 1
          ? this.executeSelectedPassKeyboardAction() || this.cancelKeyboardActionSelection()
          : this.cancelKeyboardActionSelection()) event.preventDefault()
        break
      case 'Enter':
      case 'Space':
      case 'Numpad0':
        if (this.pendingActionChoice
          ? this.executeSelectedHandTile()
          : this.executeConfiguredKeyboardAction() || this.executeSelectedHandTile()) event.preventDefault()
        break
    }
  }

  private resolveVisibleActions(acts: string[]) {
    const visibleActs = new Set(acts)
    if (visibleActs.has('Tap')) {
      visibleActs.delete('Pass')
      if (visibleActs.has('Tsumo')) visibleActs.delete('Ron')
    } else if (visibleActs.has('Ron')) {
      visibleActs.delete('Tsumo')
    }
    if (!visibleActs.has('Tap') && visibleActs.has('Pass')) {
      visibleActs.delete('Tao')
      visibleActs.delete('Hua')
    }
    return visibleActs
  }

  private getKeyboardActionDefs() {
    const visibleActs = this.resolveVisibleActions(this.currentActionOffers)
    if (visibleActs.size === 1 && visibleActs.has('Pass')) return []
    return this.ACT_BTNS.filter(def => visibleActs.has(def.act))
  }

  private setKeyboardActionSelection(index: number) {
    const defs = this.getKeyboardActionDefs()
    if (defs.length === 0) {
      this.keyboardActionIndex = -1
      return false
    }
    this.keyboardActionIndex = (index + defs.length) % defs.length
    const selected = defs[this.keyboardActionIndex]
    const visibleActs = new Set(defs.map(def => def.act))
    this.setActionButtonsEnabled(visibleActs)
    for (const def of defs) {
      const btn = this.actionButtonSprites.get(def.act)
      if (btn) this.setActionButtonState(btn, def.act === selected.act ? 'hover' : 'normal')
    }
    return true
  }

  private moveKeyboardActionSelection(delta: -1 | 1) {
    const next = this.keyboardActionIndex >= 0 ? this.keyboardActionIndex + delta : 0
    return this.setKeyboardActionSelection(next)
  }

  private cancelKeyboardActionSelection() {
    if (this.pendingActionChoice) {
      this.pendingActionChoice = null
      this.keyboardActionIndex = -1
      this.redrawHand(this.myOdr)
      this.showActionButtons(this.currentActionOffers)
      return true
    }
    const defs = this.getKeyboardActionDefs()
    if (defs.length === 0) return false
    this.keyboardActionIndex = -1
    this.setActionButtonsEnabled(new Set(defs.map(def => def.act)))
    return true
  }

  private executeSelectedPassKeyboardAction() {
    const defs = this.getKeyboardActionDefs()
    if (defs.length === 0 || this.keyboardActionIndex < 0) return false
    const selected = defs[this.keyboardActionIndex]
    if (selected.act !== 'Pass') return false
    void this.sendAction(selected, this.currentActionOffers)
    return true
  }

  private executeConfiguredKeyboardAction() {
    if (this.inputConfig.nSelPasKey !== 1) return this.executeSelectedKeyboardAction()
    const defs = this.getKeyboardActionDefs()
    if (defs.length === 0 || this.keyboardActionIndex < 0) return false
    if (defs[this.keyboardActionIndex].act === 'Pass') return true
    return this.executeSelectedKeyboardAction()
  }

  private executeSelectedKeyboardAction() {
    const defs = this.getKeyboardActionDefs()
    if (defs.length === 0 || this.keyboardActionIndex < 0) return false
    const def = defs[this.keyboardActionIndex]
    void this.sendAction(def, this.currentActionOffers)
    return true
  }

  private moveHandSelection(delta: -1 | 1) {
    const hand = this.players[this.myOdr].hand
    if (hand.length === 0) return false
    if (this.selectedIdx >= 0 && this.selectedIdx < hand.length) hand[this.selectedIdx].isSelected = false
    this.selectedIdx = this.selectedIdx >= 0
      ? (this.selectedIdx + delta + hand.length) % hand.length
      : delta > 0 ? 0 : hand.length - 1
    this.selectedDiscardBipaiIndex = hand[this.selectedIdx].bipaiIndex
    hand[this.selectedIdx].isSelected = true
    this.redrawHand(this.myOdr)
    return true
  }

  private executeSelectedHandTile() {
    const hand = this.players[this.myOdr].hand
    if (hand.length === 0) return false
    const idx = this.selectedIdx >= 0 && this.selectedIdx < hand.length ? this.selectedIdx : hand.length - 1
    if (this.pendingActionChoice) {
      void this.selectActionChoiceTile(idx)
      return true
    }
    if (!this.canDiscardOnTileClick) return false
    void this.discard(idx)
    return true
  }

  private onTilePointerDown(idx: number, pointer?: Phaser.Input.Pointer) {
    const hand = this.players[this.myOdr].hand
    if (DEBUG_GAME) console.info('[GameScene] tile pointerdown', {
      idx,
      myOdr: this.myOdr,
      handLength: hand.length,
      canDiscardOnTileClick: this.canDiscardOnTileClick,
      selectedIdx: this.selectedIdx,
      tile: hand[idx],
    })
    if (this.pendingActionChoice) {
      void this.selectActionChoiceTile(idx)
      return
    }
    if (idx < 0 || idx >= hand.length) {
      if (DEBUG_GAME) console.warn('[GameScene] tile click ignored: invalid index', { idx, handLength: hand.length })
      return
    }

    if (!this.canDiscardOnTileClick) {
      if (!isTouchPointer(pointer)) return
      this.touchConfirmDiscardIdx = -1
      if (this.selectedIdx >= 0 && this.selectedIdx < hand.length) hand[this.selectedIdx].isSelected = false
      this.selectedIdx = idx
      this.selectedDiscardBipaiIndex = hand[idx].bipaiIndex
      hand[idx].isSelected = true
      this.activeAssistTileCode = hand[idx].code
      this.activeAssistHandIdx = idx
      this.hoverCursor?.destroy()
      this.redrawHand(this.myOdr)
      this.hoverCursor = this.createLegacyHoverCursor(this.handSprites[this.myOdr][idx])
      this.clearWaitTileGuide()
      this.showAssistHighlights(hand[idx].code)
      return
    }

    this.hoverCursor?.destroy()
    this.hoverCursor = undefined

    if (isTouchPointer(pointer)) {
      const decision = decideTouchTileAction(this.selectedIdx, idx)
      this.touchConfirmDiscardIdx = decision.confirmDiscard ? idx : -1
      if (this.selectedIdx >= 0 && this.selectedIdx < hand.length) hand[this.selectedIdx].isSelected = false
      this.selectedIdx = decision.selectedIdx
      this.selectedDiscardBipaiIndex = hand[idx].bipaiIndex
      hand[idx].isSelected = true
      this.activeAssistTileCode = hand[idx].code
      this.activeAssistHandIdx = idx
      this.redrawHand(this.myOdr)
      this.showAssistHighlights(hand[idx].code)
      this.showWaitTileGuide(idx)
      return
    }

    if (this.selectedIdx >= 0 && this.selectedIdx < hand.length) hand[this.selectedIdx].isSelected = false
    this.selectedIdx = idx
    this.selectedDiscardBipaiIndex = hand[idx].bipaiIndex
    hand[idx].isSelected = true
    this.redrawHand(this.myOdr)
  }

  private onTilePointerUp(idx: number, pointer?: Phaser.Input.Pointer) {
    const hand = this.players[this.myOdr].hand
    if (DEBUG_GAME) console.info('[GameScene] tile pointerup', {
      idx,
      myOdr: this.myOdr,
      handLength: hand.length,
      canDiscardOnTileClick: this.canDiscardOnTileClick,
      selectedIdx: this.selectedIdx,
      tile: hand[idx],
    })
    if (idx < 0 || idx >= hand.length) {
      if (DEBUG_GAME) console.warn('[GameScene] tile pointerup ignored: invalid index', { idx, handLength: hand.length })
      return
    }
    if (this.pendingActionChoice) return
    if (!this.canDiscardOnTileClick) {
      if (DEBUG_GAME) console.warn('[GameScene] tile pointerup ignored: discard is not allowed yet', {
        idx,
        myOdr: this.myOdr,
        canDiscardOnTileClick: this.canDiscardOnTileClick,
      })
      return
    }
    if (isTouchPointer(pointer)) {
      const confirmDiscard = this.touchConfirmDiscardIdx === idx
      this.touchConfirmDiscardIdx = -1
      if (!confirmDiscard) return
      this.activeAssistTileCode = 0
      this.activeAssistHandIdx = -1
      this.clearAssistHighlights()
      this.clearWaitTileGuide()
    }
    this.discard(idx)
  }

  private onScenePointerDown(pointer: Phaser.Input.Pointer, currentlyOver: Phaser.GameObjects.GameObject[]) {
    if (!isTouchPointer(pointer) || currentlyOver.length > 0 || (this.selectedIdx < 0 && this.activeAssistHandIdx < 0)) return
    const hand = this.players[this.myOdr].hand
    if (this.selectedIdx >= 0 && this.selectedIdx < hand.length) hand[this.selectedIdx].isSelected = false
    this.selectedIdx = -1
    this.selectedDiscardBipaiIndex = undefined
    this.touchConfirmDiscardIdx = -1
    this.hoverCursor?.destroy()
    this.hoverCursor = undefined
    this.activeAssistTileCode = 0
    this.activeAssistHandIdx = -1
    this.clearAssistHighlights()
    this.clearWaitTileGuide()
    this.redrawHand(this.myOdr)
  }

  /** 打牌送信 (WM_LBUTTONDOWN → PutSutepai 相当) */
  private async discard(idx: number) {
    if (this.isReplay) return
    if (this.actionSendInFlight) return
    if (!this.canSendCurrentPrompt('discard')) return
    const actionSeatOrder = this.getActionSeatOrder()
    const actionSeq = this.currentActionPrompt?.actionSeq
    const tile = this.players[actionSeatOrder].hand[idx]
    if (!tile) {
      console.error('[GameScene] discard failed: tile not found', { idx, myOdr: this.myOdr, actionSeatOrder, hand: this.players[actionSeatOrder].hand })
      return
    }
    const tapOffer = this.actionOfferByName.get('Tap')
    if (tapOffer && !tapOffer.bipaiIndex.includes(tile.bipaiIndex ?? -1)) {
      console.warn('[GameScene] discard ignored: tile is not in server Tap candidates', { idx, tile, tapCandidates: tapOffer.bipaiIndex })
      return
    }
    if (tile.bipaiIndex === undefined) {
      console.warn('[GameScene] discard ignored: missing bipaiIndex', { idx, tile, myOdr: this.myOdr, actionSeatOrder })
      return
    }
    this.players[this.myOdr].hand.forEach(handTile => { handTile.isSelected = false })
    this.selectedIdx = -1
    this.selectedDiscardBipaiIndex = undefined
    this.touchConfirmDiscardIdx = -1
    this.selectedCursor?.destroy()
    this.selectedCursor = undefined
    this.drawnTileCursor?.destroy()
    this.drawnTileCursor = undefined
    this.activeAssistTileCode = 0
    this.activeAssistHandIdx = -1
    this.clearAssistHighlights()
    this.actionPromptSerial++
    this.clearAutoDiscardTimer()
    this.clearActionResponseTimer()
    this.clearAutoControlTimer()
    this.hoverCursor?.destroy()
    this.hoverCursor = undefined
    this.clearTenpaiMarkers()
    this.clearTrainingRecommendation()
    this.clearWaitTileGuide()
    this.actionOfferByName.clear()
    this.currentActionOffers = []
    this.currentActionSeatOrder = null
    this.actionSendInFlight = true
    this.canDiscardOnTileClick = false
    this.emitToUiScene('actionPromptEnd', { viewOdr: this.myOdr })
    const payload = {
      playType: 'MJPID_ACTION',
      roomId: this.roomId,
      seatOrder: actionSeatOrder,
      action: Act.Tap,
      bipaiIndex: [tile.bipaiIndex],
      actionSeq,
    }
    this.pendingAction = { seatOrder: payload.seatOrder, action: payload.action, actionSeq: payload.actionSeq }
    this.traceGameFlow('tx discard ACTION', { payload })
    if (DEBUG_GAME) console.info('[GameScene] sending discard', { idx, tile, payload })
    try {
      await SignalR.send('playing', payload)
      if (DEBUG_GAME) console.info('[GameScene] discard sent', payload)
    } catch (error) {
      this.actionSendInFlight = false
      this.pendingAction = null
      console.error('[GameScene] discard send failed', { payload, error })
      if (this.isConnectionSendError(error)) {
        this.expireCurrentActionPrompt('discard send connection lost')
        return
      }
      this.canDiscardOnTileClick = true
      this.scheduleAutoDiscard(3)
    }
  }

  private async sendAction(def: { act: string; code: Act }, acts: string[]) {
    if (this.actionSendInFlight) return
    const offer = this.actionOfferByName.get(def.act)
    const choices = this.actionChoicesByName.get(def.act) ?? []
    if (choices.length > 1) {
      this.beginActionChoice(def, acts, choices)
      return
    }
    const bipaiIndex = choices[0]?.bipaiIndex
      ?? offer?.bipaiIndex
      ?? []
    const actionCode = offer?.code ?? choices[0]?.code ?? def.code
    if (this.actionRequiresBipai(actionCode) && bipaiIndex.length === 0) {
      console.warn('[GameScene] action ignored: missing server bipaiIndex', { action: def.act, actionCode })
      return
    }
    await this.sendActionWithBipai(def, acts, actionCode, bipaiIndex)
  }

  private actionRequiresBipai(actionCode: Act) {
    return actionCode === Act.Tap
      || actionCode === Act.Ric
      || actionCode === Act.Ank
      || actionCode === Act.Cha
      || actionCode === Act.Kan
      || actionCode === Act.Pon
      || actionCode === Act.Chi
      || actionCode === Act.Hua
  }

  private beginActionChoice(def: { act: string; code: Act }, acts: string[], choices: Array<{ code: Act; bipaiIndex: number[] }>) {
    this.keyboardActionIndex = -1
    this.pendingActionChoice = { def, acts, choices }
    this.canDiscardOnTileClick = false
    this.selectedIdx = -1
    this.selectedDiscardBipaiIndex = undefined
    this.clearActionButtons()
    this.redrawHand(this.myOdr)
  }

  private async selectActionChoiceTile(idx: number) {
    const pending = this.pendingActionChoice
    if (!pending) return
    const hand = this.players[this.myOdr].hand
    const tile = hand[idx]
    const bipaiIndex = tile?.bipaiIndex
    if (bipaiIndex === undefined) return

    const selectedBipai = this.selectedIdx >= 0 ? hand[this.selectedIdx]?.bipaiIndex : undefined
    const exact = selectedBipai !== undefined && selectedBipai !== bipaiIndex
      ? pending.choices.find(choice => choice.bipaiIndex.includes(selectedBipai) && choice.bipaiIndex.includes(bipaiIndex))
      : undefined
    if (exact) {
      await this.sendActionWithBipai(pending.def, pending.acts, exact.code, exact.bipaiIndex)
      return
    }

    const matches = pending.choices.filter(choice => choice.bipaiIndex.includes(bipaiIndex))
    if (matches.length === 1) {
      await this.sendActionWithBipai(pending.def, pending.acts, matches[0].code, matches[0].bipaiIndex)
      return
    }

    if (this.selectedIdx >= 0 && this.selectedIdx < hand.length) hand[this.selectedIdx].isSelected = false
    this.selectedIdx = idx
    tile.isSelected = true
    this.redrawHand(this.myOdr)
  }

  private async sendActionWithBipai(def: { act: string; code: Act }, acts: string[], actionCode: Act, bipaiIndex: number[]) {
    if (this.actionSendInFlight) return
    if (!this.canSendCurrentPrompt('sendAction')) return
    const actionSeatOrder = this.getActionSeatOrder()
    const actionSeq = this.currentActionPrompt?.actionSeq
    this.actionPromptSerial++
    this.clearActionButtons()
    this.clearAutoDiscardTimer()
    this.clearActionResponseTimer()
    this.clearAutoControlTimer()
    this.hoverCursor?.destroy()
    this.hoverCursor = undefined
    this.clearTenpaiMarkers()
    this.clearTrainingRecommendation()
    this.clearWaitTileGuide()
    this.actionOfferByName.clear()
    this.actionChoicesByName.clear()
    this.pendingActionChoice = null
    this.currentActionOffers = []
    this.currentActionSeatOrder = null
    this.actionSendInFlight = true
    this.canDiscardOnTileClick = false
    this.emitToUiScene('actionPromptEnd', { viewOdr: this.myOdr })
    const payload = {
      playType: 'MJPID_ACTION',
      roomId: this.roomId,
      seatOrder: actionSeatOrder,
      action: actionCode,
      bipaiIndex,
      actionSeq,
    }
    this.pendingAction = { seatOrder: payload.seatOrder, action: payload.action, actionSeq: payload.actionSeq }
    this.traceGameFlow('tx button ACTION', { def, payload })
    if (DEBUG_GAME) console.info('[GameScene] sending action', { def, payload })
    try {
      await SignalR.send('playing', payload)
      if (DEBUG_GAME) console.info('[GameScene] action sent', payload)
    } catch (error) {
      this.actionSendInFlight = false
      this.pendingAction = null
      console.error('[GameScene] action send failed', { payload, error })
      if (this.isConnectionSendError(error)) {
        this.expireCurrentActionPrompt('action send connection lost')
        return
      }
      this.showActionButtons(acts)
    }
  }

  private isConnectionSendError(error: unknown) {
    if (!SignalR.isConnected()) return true
    const message = error instanceof Error ? error.message : String(error ?? '')
    return message.includes('underlying connection being closed')
      || message.includes('SignalR not connected')
      || message.includes('Invocation canceled')
  }

  private applyAutoControl(acts: string[]) {
    if (this.isReplay) return
    const autoAction = resolveAutoControlAction(this.autoControl, acts)
    const choose = (act: string) => this.ACT_BTNS.find(button => button.act === act)

    if (autoAction === 'Tap') {
      this.discard(this.players[this.getActionSeatOrder()].hand.length - 1)
      return
    }

    const def = autoAction ? choose(autoAction) : undefined
    if (def) void this.sendAction(def, acts)
  }

  private scheduleAutoControl(acts: string[], promptSerial = this.actionPromptSerial) {
    this.clearAutoControlTimer()
    if (this.isReplay) return
    const autoAction = resolveAutoControlAction(this.autoControl, acts)
    if (!autoAction) return
    const delayMs = getAutoControlDelayMs(autoAction, this.paifuGraphRound.roomOption)
    this.autoControlTimer = this.time.delayedCall(delayMs, () => {
      if (promptSerial !== this.actionPromptSerial) return
      if (!this.isLocalPlayerOdr(this.currentActionSeatOrder)) return
      if (!this.canSendCurrentPrompt('auto control')) return
      if (!resolveAutoControlAction(this.autoControl, acts)) return
      this.applyAutoControl(acts)
    })
  }

  private suspendAutoPassForCurrentPrompt() {
    const isTurnMode = this.currentActionPrompt?.playerMode === 'Turn' || this.canDiscardOnTileClick
    const heldAutoPass = shouldSuspendAutoPassForPrompt(this.autoControl, this.currentActionOffers, isTurnMode)
    if (heldAutoPass) this.clearAutoControlTimer()
    const requestedTimeBank = this.requestTimeBankForCurrentPrompt()
    return heldAutoPass || requestedTimeBank
  }

  private requestTimeBankForCurrentPrompt() {
    const prompt = this.currentActionPrompt
    if (!prompt || this.timeBankExtensionInFlight || prompt.timeBankEnabled || prompt.timeBankMs <= 0) return false
    if (prompt.playerMode !== 'Furo' && prompt.playerMode !== 'Chan') return false
    if (performance.now() >= prompt.localDeadlineAt) {
      this.expireCurrentActionPrompt('time bank extension expired')
      return false
    }

    this.timeBankExtensionInFlight = true
    void SignalR.send('playing', {
      playType: 'MJPID_EXTEND_TIME_BANK',
      roomId: this.roomId,
      seatOrder: prompt.seatOrder,
      actionSeq: prompt.actionSeq,
    }).catch(error => {
      this.timeBankExtensionInFlight = false
      if (this.isConnectionSendError(error)) this.expireCurrentActionPrompt('time bank extension connection lost')
      else console.error('[GameScene] time bank extension failed', error)
    })
    return true
  }

  private buildActionPromptTimerPayload(remainingMs: number) {
    const prompt = this.currentActionPrompt
    return {
      timeLimit: remainingMs,
      baseTimeMs: prompt?.baseTimeMs ?? remainingMs,
      keepTimeMs: prompt?.keepTimeMs ?? 0,
      timeBankMs: prompt?.timeBankMs ?? 0,
      timeBankEnabled: prompt?.timeBankEnabled ?? false,
      visualMode: prompt?.playerMode === 'Turn'
        ? 'input'
        : resolveLegacyTimerMode(
          prompt?.playerMode ?? '',
          this.currentActionOffers,
          prompt?.timeBankEnabled ?? false,
          this.autoControl,
        ),
      maxTimeMs: this.kyokuTimeFullMs,
      viewOdr: this.myOdr,
    }
  }

  private emitActionPromptTimerWhenReady(remainingMs: number) {
    if (!this.initialActionPresentationReady || this.deferInitialActionPresentation || this.initialDealInProgress) {
      this.actionTimerAwaitingInitialDeal = true
      return
    }
    this.actionTimerAwaitingInitialDeal = false
    this.emitToUiScene('actionPromptStart', this.buildActionPromptTimerPayload(remainingMs))
  }

  private resumeActionPromptTimerAfterInitialDeal() {
    if (!this.actionTimerAwaitingInitialDeal || !this.currentActionPrompt) return
    const remainingMs = Math.max(0, this.currentActionPrompt.localDeadlineAt - performance.now())
    if (remainingMs <= 0) return
    this.emitActionPromptTimerWhenReady(remainingMs)
  }

  private scheduleAutoDiscard(timeLimitSeconds: number, promptSerial = this.actionPromptSerial) {
    this.clearAutoDiscardTimer()
    if (this.isReplay || !this.canDiscardOnTileClick) return
    const delay = decideAutoDiscardDelayMs(
      this.currentActionPrompt?.localDeadlineAt,
      performance.now(),
      timeLimitSeconds,
    )
    this.autoDiscardTimer = this.time.delayedCall(delay, () => {
      if (promptSerial !== this.actionPromptSerial) return
      if (!this.canSendCurrentPrompt('auto discard timeout')) return
      if (!this.isLocalPlayerOdr(this.currentActionSeatOrder)) return
      if (!this.canDiscardOnTileClick) return
      const actionSeatOrder = this.getActionSeatOrder()
      const hand = this.players[actionSeatOrder].hand
      const idx = decideTimedDiscardIndex(
        hand.map(tile => tile.bipaiIndex),
        this.selectedIdx,
        this.selectedDiscardBipaiIndex,
      )
      if (idx < 0) {
        console.error('[GameScene] auto discard failed: hand is empty', { myOdr: this.myOdr, actionSeatOrder })
        return
      }
      if (DEBUG_GAME) console.warn('[GameScene] auto discard by turn timeout', { myOdr: this.myOdr, actionSeatOrder, idx, tile: hand[idx] })
      this.discard(idx)
    })
  }

  private scheduleDefaultPass(acts: string[], timeLimitSeconds: number, promptSerial = this.actionPromptSerial) {
    this.clearActionResponseTimer()
    if (this.isReplay) return
    const pass = this.ACT_BTNS.find(button => button.act === 'Pass')
    if (!pass) return
    const passOnly = acts.length > 0 && acts.every(action => action === 'Pass')
    if (passOnly) {
      if (promptSerial !== this.actionPromptSerial) return
      if (!this.isLocalPlayerOdr(this.currentActionSeatOrder)) return
      if (this.canDiscardOnTileClick) return
      if (!this.actionOfferByName.has('Pass')) return
      if (DEBUG_GAME) console.warn('[GameScene] immediate default pass for pass-only prompt', { myOdr: this.myOdr, actionSeatOrder: this.getActionSeatOrder(), acts })
      void this.sendAction(pass, acts)
      return
    }
    const delay = Math.max(1000, Math.trunc((Number.isFinite(timeLimitSeconds) && timeLimitSeconds > 0 ? timeLimitSeconds : 5) * 1000))
    this.actionResponseTimer = this.time.delayedCall(delay, () => {
      if (promptSerial !== this.actionPromptSerial) return
      if (!this.canSendCurrentPrompt('default pass timeout')) return
      if (!this.isLocalPlayerOdr(this.currentActionSeatOrder)) return
      if (this.canDiscardOnTileClick) return
      if (!this.actionOfferByName.has('Pass')) return
      if (DEBUG_GAME) console.warn('[GameScene] default pass by action timeout', { myOdr: this.myOdr, actionSeatOrder: this.getActionSeatOrder(), acts })
      void this.sendAction(pass, acts)
    })
  }

  private scheduleTimeWarnings(remainingMs: number, promptSerial = this.actionPromptSerial) {
    this.clearTimeWarningTimers()
    if (this.isReplay || remainingMs <= 0) return
    const firstWarning = Math.min(TIME_WARNING_START_MS, Math.max(0, Math.trunc((remainingMs - 500) / 1000) * 1000))
    for (let warningMs = firstWarning; warningMs > 0; warningMs -= 1000) {
      const delay = remainingMs - warningMs
      if (delay < 0) continue
      this.timeWarningTimers.push(this.time.delayedCall(delay, () => {
        if (promptSerial !== this.actionPromptSerial) return
        if (!this.currentActionPrompt) return
        this.emitGameStatus(`${Math.ceil(warningMs / 1000)}秒・・・`, '#ff1414', true)
        playMajakSid(SID_TIME, this.soundSkinOptions())
      }))
    }
  }
  private getActionSeatOrder() {
    return this.currentActionSeatOrder ?? this.myOdr
  }

  private canSendCurrentPrompt(reason: string) {
    const prompt = this.currentActionPrompt
    if (!prompt) return true
    if (performance.now() < prompt.localDeadlineAt) return true
    this.expireCurrentActionPrompt(reason)
    return false
  }

  private expireCurrentActionPrompt(reason: string) {
    if (DEBUG_GAME) console.warn('[GameScene] action prompt expired', { reason, prompt: this.currentActionPrompt, localNow: performance.now() })
    this.actionPromptSerial++
    this.clearAutoDiscardTimer()
    this.clearActionResponseTimer()
    this.clearAutoControlTimer()
    this.clearTimeWarningTimers()
    this.clearActionButtons()
    this.actionOfferByName.clear()
    this.actionChoicesByName.clear()
    this.pendingActionChoice = null
    this.currentActionOffers = []
    this.currentActionSeatOrder = null
    this.currentActionPrompt = null
    this.actionTimerAwaitingInitialDeal = false
    this.timeBankExtensionInFlight = false
    this.canDiscardOnTileClick = false
    this.selectedIdx = -1
    this.selectedDiscardBipaiIndex = undefined
    this.selectedDiscardBipaiIndex = undefined
    this.clearTenpaiMarkers()
    this.clearTrainingRecommendation()
    this.clearWaitTileGuide()
    this.emitToUiScene('actionPromptEnd', { viewOdr: this.myOdr })
  }

  private reconcileTapCandidates(seatOrder: number) {
    const tapOffer = this.actionOfferByName.get('Tap')
    if (!tapOffer || seatOrder < 0 || seatOrder >= this.players.length) return
    const hand = this.players[seatOrder].hand
    const byBipaiIndex = new Map<number, TileState>()
    hand.forEach(tile => {
      if (tile.bipaiIndex !== undefined) byBipaiIndex.set(tile.bipaiIndex, tile)
    })
    if (tapOffer.bipaiIndex.length < Math.max(hand.length, 13)) {
      const missingCandidates = tapOffer.bipaiIndex
        .filter(bipaiIndex => !byBipaiIndex.has(bipaiIndex))
        .map(bipaiIndex => this.tileFromKnownPai(bipaiIndex))
        .filter((tile): tile is TileState => Boolean(tile))
      if (this.isLocalPlayerOdr(seatOrder) && hand.length % 3 === 1 && missingCandidates.length === 1) {
        hand.push(missingCandidates[0])
        this.redrawHand(seatOrder)
      }
      return
    }
    const reconciled = tapOffer.bipaiIndex
      .map(bipaiIndex => byBipaiIndex.get(bipaiIndex) ?? this.tileFromKnownPai(bipaiIndex))
      .filter((tile): tile is TileState => Boolean(tile))
    if (reconciled.length !== tapOffer.bipaiIndex.length) return

    const sameHand = hand.length === reconciled.length
      && hand.every((tile, idx) => tile.bipaiIndex === reconciled[idx].bipaiIndex)
    if (sameHand) return
    this.players[seatOrder].hand = reconciled
    if (this.paifuGraphInitialHands[seatOrder].length === 0 && reconciled.length >= 13) {
      this.paifuGraphInitialHands[seatOrder] = this.cloneTiles(reconciled)
    }
    if (DEBUG_GAME) console.warn('[GameScene] hand reconciled to server Tap candidates', { seatOrder, tapCandidates: tapOffer.bipaiIndex })
    this.redrawHand(seatOrder)
  }

  private tileFromKnownPai(bipaiIndex: number): TileState | undefined {
    const code = this.knownPai.get(bipaiIndex)
    return code ? { code, bipaiIndex, isSelected: false } : undefined
  }

  private clearAutoDiscardTimer() {
    this.autoDiscardTimer?.destroy()
    this.autoDiscardTimer = undefined
  }

  private clearLiveActionState(reason: string) {
    this.traceGameFlow('clear live action state', { reason })
    this.actionPromptSerial++
    this.actionSendInFlight = false
    this.pendingAction = null
    this.currentActionPrompt = null
    this.actionTimerAwaitingInitialDeal = false
    this.timeBankExtensionInFlight = false
    this.currentActionSeatOrder = null
    this.canDiscardOnTileClick = false
    this.selectedIdx = -1
    this.selectedDiscardBipaiIndex = undefined
    this.keyboardActionIndex = -1
    this.actionOfferByName.clear()
    this.actionChoicesByName.clear()
    this.pendingActionChoice = null
    this.currentActionOffers = []
    this.currentHoraErrorReason = ''
    this.clearAutoDiscardTimer()
    this.clearActionResponseTimer()
    this.clearAutoControlTimer()
    this.clearTimeWarningTimers()
    this.clearActionButtons()
    this.clearTenpaiMarkers()
    this.clearTrainingRecommendation()
    this.clearWaitTileGuide()
    this.emitToUiScene('actionPromptEnd', { viewOdr: this.myOdr })
  }

  private clearActionResponseTimer() {
    this.actionResponseTimer?.destroy()
    this.actionResponseTimer = undefined
  }

  private clearAutoControlTimer() {
    this.autoControlTimer?.destroy()
    this.autoControlTimer = undefined
  }

  private clearTimeWarningTimers() {
    this.timeWarningTimers.forEach(timer => timer.destroy())
    this.timeWarningTimers = []
  }

  /* ======================================================================
   * 操作ボタン (CMJUserIF1 相当)
   * ====================================================================== */
  private get ACT_BTNS(): { key: string; labelKey: string; label: string; act: string; code: Act; x: number; y: number; w: number; h: number }[] {
    return [
      { key: 'btn_kan', labelKey: 'action_label_kan', label: 'カン', act: 'Kan', code: Act.Kan, x: ACTION_BUTTON_LAYOUT.kan.x, y: ACTION_BUTTON_LAYOUT.kan.y, w: ACTION_BUTTON_LAYOUT.kan.width, h: ACTION_BUTTON_LAYOUT.kan.height },
      { key: 'btn_pon', labelKey: 'action_label_pon', label: 'ポン', act: 'Pon', code: Act.Pon, x: ACTION_BUTTON_LAYOUT.pon.x, y: ACTION_BUTTON_LAYOUT.pon.y, w: ACTION_BUTTON_LAYOUT.pon.width, h: ACTION_BUTTON_LAYOUT.pon.height },
      { key: 'btn_chi', labelKey: 'action_label_chi', label: 'チー', act: 'Chi', code: Act.Chi, x: ACTION_BUTTON_LAYOUT.chi.x, y: ACTION_BUTTON_LAYOUT.chi.y, w: ACTION_BUTTON_LAYOUT.chi.width, h: ACTION_BUTTON_LAYOUT.chi.height },
      { key: 'btn_reach', labelKey: 'action_label_reach', label: 'リーチ', act: 'Reach', code: Act.Ric, x: ACTION_BUTTON_LAYOUT.reach.x, y: ACTION_BUTTON_LAYOUT.reach.y, w: ACTION_BUTTON_LAYOUT.reach.width, h: ACTION_BUTTON_LAYOUT.reach.height },
      { key: 'btn_ron', labelKey: 'action_label_ron', label: 'ロン', act: 'Ron', code: Act.Ron, x: ACTION_BUTTON_LAYOUT.ron.x, y: ACTION_BUTTON_LAYOUT.ron.y, w: ACTION_BUTTON_LAYOUT.ron.width, h: ACTION_BUTTON_LAYOUT.ron.height },
      { key: 'btn_tsumo', labelKey: 'action_label_tsumo', label: 'ツモ', act: 'Tsumo', code: Act.Tsu, x: ACTION_BUTTON_LAYOUT.tsumo.x, y: ACTION_BUTTON_LAYOUT.tsumo.y, w: ACTION_BUTTON_LAYOUT.tsumo.width, h: ACTION_BUTTON_LAYOUT.tsumo.height },
      { key: 'btn_pass', labelKey: 'action_label_pass', label: 'パス', act: 'Pass', code: Act.Pas, x: ACTION_BUTTON_LAYOUT.pass.x, y: ACTION_BUTTON_LAYOUT.pass.y, w: ACTION_BUTTON_LAYOUT.pass.width, h: ACTION_BUTTON_LAYOUT.pass.height },
      { key: 'btn_flow', labelKey: 'action_label_flow', label: '流局', act: 'Tao', code: Act.Tao, x: ACTION_BUTTON_LAYOUT.flow.x, y: ACTION_BUTTON_LAYOUT.flow.y, w: ACTION_BUTTON_LAYOUT.flow.width, h: ACTION_BUTTON_LAYOUT.flow.height },
      { key: 'btn_hua', labelKey: 'action_label_hua', label: '花', act: 'Hua', code: Act.Hua, x: ACTION_BUTTON_LAYOUT.hua.x, y: ACTION_BUTTON_LAYOUT.hua.y, w: ACTION_BUTTON_LAYOUT.hua.width, h: ACTION_BUTTON_LAYOUT.hua.height },
    ]
  }
  private readonly DEFAULT_ACTION_BUTTONS = new Set(['Kan', 'Pon', 'Chi', 'Reach', 'Ron', 'Pass'])
  private readonly MOBILE_HAND_ACTION_BUTTONS = new Set(['Kan', 'Pon', 'Chi', 'Reach', 'Ron', 'Pass'])

  private actionButtonDisplaySize(def: { w: number; h: number }) {
    const scale = this.layoutMode === 'mobileLandscape' ? 1 : 1.1
    return { width: Math.round(def.w * scale), height: Math.round(def.h * scale) }
  }

  private createActionButtons() {
    this.actionPanelFrame = this.clipToBoard(this.add.rectangle(0, 0, 1, 1, mixThemeTint(this.themeUiTint, 0x000000, 0.3), 1)
      .setStrokeStyle(2, mixThemeTint(this.themeUiTint, 0xffffff, 0.62), 1)
      .setDepth(Z_PANEL + 9)
      .setVisible(false))
    for (const def of this.ACT_BTNS) {
      const pos = boardLocalPoint({ x: def.x, y: def.y })
      const size = this.actionButtonDisplaySize(def)
      const x = pos.x + size.width / 2
      const y = pos.y + size.height / 2
      const background = this.clipToBoard(this.add.rectangle(x, y, size.width, size.height, mixThemeTint(this.themeUiTint, 0x000000, 0.12), 1)
        .setStrokeStyle(1, mixThemeTint(this.themeUiTint, 0xffffff, 0.5), 1)
        .setDepth(Z_PANEL + 10)
        .setVisible(true))
      const sprite = this.clipToBoard(this.add.sprite(x, y, def.labelKey, 1)
        .setDisplaySize(size.width, size.height)
        .setDepth(Z_PANEL + 11)
        .setVisible(true))
      this.actionButtonSprites.set(def.act, { background, sprite })
    }
    const horaErrorLayout = ACTION_BUTTON_LAYOUT.horaError
    const horaPos = boardLocalPoint({ x: horaErrorLayout.x, y: horaErrorLayout.y })
    this.horaErrorText = this.clipToBoard(this.add.text(horaPos.x + horaErrorLayout.width / 2, horaPos.y + horaErrorLayout.height / 2, '', {
      fontFamily: 'sans-serif',
      fontSize: '13px',
      color: '#fff2cc',
      align: 'center',
      wordWrap: { width: horaErrorLayout.width },
    }).setOrigin(0.5)
      .setDepth(Z_PANEL + 11)
      .setVisible(false))
    this.setActionButtonsEnabled(new Set())
  }

  private setActionButtonPosition(button: ActionButtonControl, x: number, y: number) {
    button.background.setPosition(x, y)
    button.sprite.setPosition(x, y)
  }

  private setActionButtonVisible(button: ActionButtonControl, visible: boolean) {
    button.background.setVisible(visible)
    button.sprite.setVisible(visible)
  }

  private setActionButtonState(button: ActionButtonControl, state: 'disabled' | 'normal' | 'hover' | 'pressed') {
    button.sprite.setFrame(state === 'disabled' ? 1 : state === 'hover' ? 2 : state === 'pressed' ? 3 : 0)
    const fill = state === 'disabled'
      ? mixThemeTint(this.themeUiTint, 0x000000, 0.5)
      : state === 'hover'
        ? mixThemeTint(this.themeUiTint, 0xffffff, 0.12)
        : state === 'pressed'
          ? mixThemeTint(this.themeUiTint, 0x000000, 0.25)
          : mixThemeTint(this.themeUiTint, 0x000000, 0.12)
    button.background
      .setFillStyle(fill, state === 'disabled' ? 0.7 : 1)
      .setStrokeStyle(1, mixThemeTint(this.themeUiTint, 0xffffff, state === 'disabled' ? 0.22 : 0.5), state === 'disabled' ? 0.6 : 1)
  }

  private updateActionPanelFrame(left: number, top: number, width: number, height: number, visible: boolean) {
    this.actionPanelFrame?.setPosition(left + width / 2, top + height / 2)
      .setSize(width, height)
      .setDisplaySize(width, height)
      .setFillStyle(mixThemeTint(this.themeUiTint, 0x000000, 0.3), 1)
      .setStrokeStyle(2, mixThemeTint(this.themeUiTint, 0xffffff, 0.62), 1)
      .setVisible(visible)
  }

  private visibleActionButtonDefs(visibleActs: Set<string>) {
    return this.ACT_BTNS.filter(def => visibleActs.has(def.act))
  }

  private displayedActionButtonDefs(visibleActs: Set<string>) {
    if (this.layoutMode === 'mobileLandscape' && this.shouldReplaceMobileHandWithActions(visibleActs)) {
      return this.ACT_BTNS.filter(def => this.MOBILE_HAND_ACTION_BUTTONS.has(def.act) || visibleActs.has(def.act))
    }
    return this.visibleActionButtonDefs(visibleActs)
  }

  private shouldReplaceMobileHandWithActions(visibleActs: Set<string>) {
    if (this.layoutMode !== 'mobileLandscape' || this.isViewer || this.isReplay) return false
    if (this.canDiscardOnTileClick || this.currentActionPrompt?.playerMode === 'Turn') return false
    return this.visibleActionButtonDefs(visibleActs).length > 0
  }

  private updateActionButtonPositions(visibleActs: Set<string>) {
    if (this.layoutMode !== 'mobileLandscape') {
      const offset = this.layoutMode === 'responsiveDesktop'
        ? { x: 0, y: this.responsiveActionPanelOffsetY }
        : mobileCenterHudOffset(this.layoutMode)
      for (const def of this.ACT_BTNS) {
        const btn = this.actionButtonSprites.get(def.act)
        if (!btn) continue
        const pos = boardLocalPoint({ x: def.x, y: def.y })
        const size = this.actionButtonDisplaySize(def)
        this.setActionButtonPosition(btn, pos.x + offset.x + size.width / 2, pos.y + offset.y + size.height / 2)
      }
      if (!this.isViewer) {
        const handSprites = this.handSprites[this.myOdr].filter(sprite => sprite.active && sprite.visible)
        if (handSprites.length > 0) {
          const handBounds = handSprites.slice(1).reduce(
            (bounds, sprite) => Phaser.Geom.Rectangle.Union(bounds, sprite.getBounds(), bounds),
            handSprites[0].getBounds(),
          )
          const displayedDefs = this.ACT_BTNS.filter(def => visibleActs.has(def.act) || this.DEFAULT_ACTION_BUTTONS.has(def.act))
          const gap = 4
          const frameTop = handBounds.bottom + 10
          const frameHeight = 90
          const frameWidth = 501
          const totalWidth = displayedDefs.reduce((sum, def) => sum + this.actionButtonDisplaySize(def).width, 0) + gap * Math.max(0, displayedDefs.length - 1)
          let x = handBounds.left + frameWidth / 2 - totalWidth / 2
          const y = frameTop + 54
          for (const def of displayedDefs) {
            const btn = this.actionButtonSprites.get(def.act)
            const size = this.actionButtonDisplaySize(def)
            if (btn) this.setActionButtonPosition(btn, x + size.width / 2, y)
            x += size.width + gap
          }
          this.updateActionPanelFrame(handBounds.left, frameTop, frameWidth, frameHeight, displayedDefs.length > 0)
          this.actionPanelBounds = new Phaser.Geom.Rectangle(handBounds.left, frameTop, frameWidth, frameHeight)
          this.events.emit('actionPanelLayout')
        } else if (visibleActs.size === 0) {
          this.actionPanelFrame?.setVisible(false)
        }
      }
      const horaErrorLayout = ACTION_BUTTON_LAYOUT.horaError
      const horaPos = boardLocalPoint({ x: horaErrorLayout.x, y: horaErrorLayout.y })
      this.horaErrorText?.setPosition(
        horaPos.x + offset.x + horaErrorLayout.width / 2,
        horaPos.y + offset.y + horaErrorLayout.height / 2,
      )
      return
    }

    const displayedDefs = this.displayedActionButtonDefs(visibleActs)
    if (displayedDefs.length === 0) {
      this.actionPanelFrame?.setVisible(false)
      return
    }
    const replaceHand = this.shouldReplaceMobileHandWithActions(visibleActs)
    const gap = 4
    const totalWidth = displayedDefs.reduce((sum, def) => sum + def.w, 0) + gap * Math.max(0, displayedDefs.length - 1)
    const maxHeight = Math.max(...displayedDefs.map(def => def.h))
    const handCount = MOBILE_SELF_HAND_FIXED_COUNT
    const handScale = MOBILE_SELF_HAND_TILE_SCALE * mobileContentScale()
    const handStart = mobileOuterHandPos(0, 0, handCount, false, handScale) ?? handPos(0, 0, false)
    const handHeight = 63 * handScale
    const framePadding = 6
    const frameWidth = totalWidth
    const frameLeft = handStart.x + (37 * handScale * handCount) / 2 - frameWidth / 2
    let x = frameLeft + frameWidth / 2 - totalWidth / 2
    const y = replaceHand
      ? handStart.y + Math.max(0, (handHeight - maxHeight) / 2) - 2
      : handStart.y - maxHeight - 8

    this.updateActionPanelFrame(
      frameLeft - framePadding,
      y - framePadding,
      frameWidth + framePadding * 2,
      maxHeight + framePadding * 2,
      replaceHand,
    )

    for (const def of displayedDefs) {
      const btn = this.actionButtonSprites.get(def.act)
      if (btn) this.setActionButtonPosition(btn, x + def.w / 2, y + def.h / 2)
      x += def.w + gap
    }
  }

  private setHoraErrorVisible(reason: string) {
    if (!this.horaErrorText) return
    const message = reason === 'furiten' ? 'フリテンです'
      : reason === 'sameTurnFuriten' ? '同巡フリテンです'
        : reason === 'invalid' ? '和了できません'
          : ''
    if (!message) {
      this.horaErrorText.setVisible(false)
      return
    }
    this.horaErrorText.setText(message).setVisible(true)
  }

  private setActionButtonsEnabled(visibleActs: Set<string>) {
    const tsumoVisible = visibleActs.has('Tsumo')
    const passAlternateVisible = visibleActs.has('Tao') || visibleActs.has('Hua')
    const replaceMobileHand = this.shouldReplaceMobileHandWithActions(visibleActs)
    const displayedMobileActs = new Set(this.displayedActionButtonDefs(visibleActs).map(def => def.act))
    if (!this.initialActionPresentationReady || this.deferInitialActionPresentation || this.initialDealInProgress) {
      this.actionPanelFrame?.setVisible(false)
      this.actionButtonSprites.forEach(button => this.setActionButtonVisible(button, false))
      this.mobileActionButtonsVisible = false
      this.updateMobileActionHandVisibility()
      return
    }
    this.updateActionButtonPositions(visibleActs)
    for (const def of this.ACT_BTNS) {
      const btn = this.actionButtonSprites.get(def.act)
      if (!btn) continue
      const enabled = visibleActs.has(def.act)
      const defaultVisible = this.layoutMode !== 'mobileLandscape' && this.DEFAULT_ACTION_BUTTONS.has(def.act)
        && !(def.act === 'Ron' && tsumoVisible)
        && !(def.act === 'Pass' && passAlternateVisible)
      const mobileVisible = replaceMobileHand && displayedMobileActs.has(def.act)
      btn.sprite.removeAllListeners()
      btn.sprite.disableInteractive()
      this.setActionButtonState(btn, enabled ? 'normal' : 'disabled')
      if (!enabled && !defaultVisible && !mobileVisible) {
        this.setActionButtonVisible(btn, false)
        continue
      }
      this.setActionButtonVisible(btn, true)
      if (!enabled) continue
      btn.sprite
        .setInteractive({ useHandCursor: true })
        .on('pointerover', () => this.setActionButtonState(btn, 'hover'))
        .on('pointerout',  () => this.setActionButtonState(btn, 'normal'))
        .on('pointerdown', () => this.setActionButtonState(btn, 'pressed'))
        .on('pointerup', async () => {
          this.setActionButtonState(btn, 'hover')
          await this.sendAction(def, [...visibleActs])
        })
    }
    this.mobileActionButtonsVisible = replaceMobileHand
    this.updateMobileActionHandVisibility()
    this.events.emit('actionPanelLayout')
  }

  showActionButtons(acts: string[]) {
    if (this.isReplay) {
      if (DEBUG_GAME) console.info('[GameScene] showActionButtons ignored in replay', { acts })
      return
    }
    this.clearActionButtons()
    this.keyboardActionIndex = -1
    const visibleActs = this.resolveVisibleActions(acts)
    if (DEBUG_GAME) console.info('[GameScene] showActionButtons resolved', { acts, visibleActs: [...visibleActs], myOdr: this.myOdr, selectedIdx: this.selectedIdx })
    if (visibleActs.size === 1 && visibleActs.has('Pass')) {
      if (DEBUG_GAME) console.info('[GameScene] pass-only prompt uses automatic response without an active button', { acts, myOdr: this.myOdr })
      this.setActionButtonsEnabled(new Set())
      this.setHoraErrorVisible(this.currentHoraErrorReason)
      return
    }
    this.setActionButtonsEnabled(visibleActs)
    const showHoraError = !visibleActs.has('Ron') && !visibleActs.has('Tsumo')
    this.setHoraErrorVisible(showHoraError ? this.currentHoraErrorReason : '')
  }

  clearActionButtons() {
    this.actionBtns.forEach(b => b.destroy())
    this.actionBtns = []
    this.setActionButtonsEnabled(new Set())
    this.setHoraErrorVisible('')
  }

  private extractActionOffers(data: Record<string, unknown>): string[] {
    const names = new Set<string>()
    this.actionOfferByName.clear()
    this.actionChoicesByName.clear()

    const addByCode = (code: number, indices?: number[]) => {
      const def = this.findActionButton(code)
      if (!def) return
      names.add(def.act)
      if (indices) {
        const item = { code: code as Act, bipaiIndex: indices }
        this.actionOfferByName.set(def.act, item)
        const choices = this.actionChoicesByName.get(def.act) ?? []
        choices.push(item)
        this.actionChoicesByName.set(def.act, choices)
      }
    }

    const actions = Array.isArray(data.actions) ? data.actions : []
    for (const action of actions) {
      if (typeof action === 'string') {
        const actionName = action === 'Flow' ? 'Tao' : action
        const def = this.ACT_BTNS.find(button => button.act === actionName)
        if (def) names.add(def.act)
        continue
      }
      if (!action || typeof action !== 'object') continue
      const record = action as Record<string, unknown>
      const code = Number(record.code ?? record.action ?? -1)
      const indices = Array.isArray(record.bipaiIndex)
        ? record.bipaiIndex.map(Number)
        : Array.isArray(record.indices)
          ? record.indices.map(Number)
          : undefined
      const defByCode = Number.isFinite(code) && code >= 0 ? this.findActionButton(code) : undefined
      if (defByCode) addByCode(code, indices)
      const rawName = String(record.act ?? record.name ?? '')
      const name = rawName === 'Flow' ? 'Tao' : rawName
      const def = this.ACT_BTNS.find(button => button.act === name)
      if (def && (!defByCode || def.act === defByCode.act)) {
        names.add(def.act)
        if (indices) {
          const item = { code: code >= 0 ? code as Act : def.code, bipaiIndex: indices }
          this.actionOfferByName.set(def.act, item)
          const choices = this.actionChoicesByName.get(def.act) ?? []
          if (!choices.some(choice => choice.code === item.code && choice.bipaiIndex.join(',') === item.bipaiIndex.join(','))) choices.push(item)
          this.actionChoicesByName.set(def.act, choices)
        }
      }
    }

    const tapCandidates = Array.isArray(data.tapCandidates)
      ? data.tapCandidates.map(Number).filter(Number.isFinite)
      : []
    if (tapCandidates.length > 0) {
      names.add('Tap')
      this.actionOfferByName.set('Tap', { code: Act.Tap, bipaiIndex: tapCandidates })
    }

    const flagValue = Number(data.actFlags ?? data.actionFlags ?? data.actFlg ?? data.actFlag ?? -1)
    if (Number.isFinite(flagValue) && flagValue > 0) {
      if ((flagValue & (1 << Act.Tap)) !== 0) names.add('Tap')
      for (const def of this.ACT_BTNS) {
        if ((flagValue & (1 << def.code)) !== 0) addByCode(def.code)
      }
    }

    return [...names]
  }

  private findActionButton(code: number) {
    if (code === Act.Ank || code === Act.Cha) return this.ACT_BTNS.find(button => button.act === 'Kan')
    if (code === Act.Tsu) return this.ACT_BTNS.find(button => button.act === 'Tsumo')
    return this.ACT_BTNS.find(button => button.code === code)
  }

  update() {
    if (this.isMeldLayoutFixture && !this.fixturePreviewApplied && this.handSprites[this.myOdr].some(sprite => sprite.active && sprite.visible)) {
      this.showFixtureActionPreview()
    }
    const centerInfoLayoutKey = mobileVisibleWorldLayoutKey(this.layoutMode)
    if (centerInfoLayoutKey !== this.mobileCenterInfoLayoutKey) {
      this.mobileCenterInfoLayoutKey = centerInfoLayoutKey
      this.createBoardMask()
      this.updateCenterInfoLayout()
      if (isCenteredIngameLayout(this.layoutMode) || this.layoutMode === 'responsiveDesktop') {
        this.players.forEach((_player, odr) => {
          this.redrawHand(odr)
          this.redrawDiscards(odr)
          this.redrawMelds(odr)
        })
        this.redrawDeadWall()
      }
    }

    if (this.layoutMode !== 'mobileLandscape' || this.isReplay) return
    const summaryKey = this.players
      .map((player, odr) => `${odr}:${player.hand.length}:${odrToLoc(odr, this.myOdr)}:${this.mobileOpponentHandCountTexts[odr] ? 1 : 0}:${this.handSprites[odr].length}`)
      .join('|')
    if (summaryKey === this.mobileHandSummaryStateKey) return
    this.mobileHandSummaryStateKey = summaryKey
    this.players.forEach((_player, odr) => {
      const loc = odrToLoc(odr, this.myOdr)
      if (!this.mobileOpponentHandCountTexts[odr] && this.handSprites[odr].length > 2) return
      if (this.players[odr].hand.length <= this.handSprites[odr].length && !this.mobileOpponentHandCountTexts[odr]) return
      void loc
      this.redrawHand(odr)
    })
  }

  private showFixtureActionPreview() {
    if (!this.isMeldLayoutFixture || this.fixturePreviewApplied) return
    if (!this.handSprites[this.myOdr].some(sprite => sprite.active && sprite.visible)) return
    const actions = ['Kan', 'Pon', 'Chi', 'Reach', 'Ron', 'Pass']
    this.currentActionOffers = actions
    this.showActionButtons(actions)
    const uiScene = this.scene.get('UIScene') as Phaser.Scene & { startTimer?: (data: Record<string, number | boolean>, endAt: number) => void }
    const timerData = { timeLimit: 60000, baseTimeMs: 20000, keepTimeMs: 20000, timeBankMs: 40000, timeBankEnabled: true, maxTimeMs: 60000 }
    uiScene.startTimer?.(timerData, performance.now() + timerData.timeLimit)
    this.fixturePreviewApplied = true
  }

  private applyActionPacket(data: Record<string, unknown>) {
    const odr = Number(data.seatOrder ?? data.order ?? 0)
    const action = Number(data.action ?? -1)
    const indices = Array.isArray(data.bipaiIndex) ? data.bipaiIndex.map(Number) : []
    const suppressLivePlayback = this.shouldSuppressLivePlayback()
    let materializedDiscard = false
    if (!suppressLivePlayback && action >= 0 && odr >= 0 && odr < this.players.length) this.showCallAction(odr, action as Act)

    if ((action === Act.Tap || action === Act.Ric) && odr >= 0 && odr < this.players.length) {
      const bipaiIndex = indices[0]
      const hand = this.players[odr].hand
      const handIdx = hand.findIndex(t => t.bipaiIndex === bipaiIndex)
      const handCountBefore = hand.length
      const discardSource = handIdx >= 0
        ? decideDiscardSource(handCountBefore, handIdx, this.discardAfterCall[odr], !this.isLocalPlayerOdr(odr), bipaiIndex)
        : undefined
      this.discardAfterCall[odr] = false
      const animateDiscard = action === Act.Tap && this.shouldAnimateLiveDiscard()
      const flightOrigin = animateDiscard ? this.captureDiscardFlightOrigin(odr, handIdx) : undefined
      this.logDiscardProbe('apply discard ACTION start', {
        action,
        odr,
        bipaiIndex,
        actionSeq: data.actionSeq,
        hand: this.handProbe(odr, bipaiIndex),
      })
      const removed = handIdx >= 0 ? hand.splice(handIdx, 1)[0] : undefined
      if (handIdx >= 0) hand.sort((a, b) => paiToFrame(a.code) - paiToFrame(b.code))
      if (handIdx < 0 && !this.isLocalPlayerOdr(odr)) {
        this.logDiscardProbe('opponent hand missing action bipaiIndex; placeholder pop path', {
          action,
          odr,
          bipaiIndex,
          handLengthBeforeFallback: hand.length,
          handLengthMod3BeforeFallback: hand.length % 3,
          knownCode: this.knownPai.get(bipaiIndex) ?? 0,
          latestActionPaiInfoTiles: (this.latestActionPaiInfoTiles[odr] ?? []).slice(0, 16).map(tile => ({ idx: tile.bipaiIndex, code: tile.code })),
        })
        if (hand.length % 3 === 1) hand.push({ code: 0, isSelected: false })
        if (hand.length > 0) hand.pop()
      }
      const code = handIdx >= 0 && removed && removed.code > 0
        ? removed.code
        : this.knownPai.get(bipaiIndex) ?? 0
      const isDoraDiscard = code > 0 && this.isDoraTile(code)
      if (handIdx < 0 && this.isLocalPlayerOdr(odr)) {
        if (DEBUG_GAME) console.warn('[GameScene] action apply could not find bipaiIndex in hand', { odr, action, bipaiIndex, hand, data })
      }
      if (code > 0) {
        const appended = this.appendDiscard(odr, bipaiIndex, code, action === Act.Ric || this.players[odr].reachDiscardCarry)
        this.logDiscardProbe('materialize discard ACTION', { action, odr, bipaiIndex, code, appended })
        materializedDiscard = appended
      } else if (Number.isInteger(bipaiIndex) && bipaiIndex >= 0) {
        this.pendingDiscardsByBipaiIndex.set(bipaiIndex, {
          odr,
          bipaiIndex,
          isReach: action === Act.Ric || this.players[odr].reachDiscardCarry,
          playReachFeedback: !suppressLivePlayback && action === Act.Ric,
          flightOrigin,
          animateDiscard,
        })
        this.logDiscardProbe('defer discard ACTION until PaiInfo reveals code', { action, odr, bipaiIndex })
      }
      this.lastDiscardOdr = odr
      if (discardSource) this.discardSourceMarkers[odr] = { ...discardSource, handCount: handCountBefore }
      this.redrawHand(odr)
      this.redrawDiscards(odr)
      if (materializedDiscard && animateDiscard) this.animateLatestDiscard(odr, flightOrigin)
      if (!suppressLivePlayback && action === Act.Tap) playMajakSid(isDoraDiscard ? SID_THROW_DORA : SID_THROW, this.soundSkinOptions())
      if (!suppressLivePlayback && action === Act.Ric) {
        this.playReachDiscardSound(odr)
        this.showReachTileEffect(odr)
      }
    }

    if (action < 0) {
      console.warn('[GameScene] action packet ignored: invalid action', { data })
    }

    if (action === Act.Ron && odr >= 0 && odr < this.players.length) {
      const claimedOdr = this.readClaimedOdr(data, odr, action)
      const claimedSprite = this.suteSprites[claimedOdr]?.[this.suteSprites[claimedOdr].length - 1]
      const claimedDiscard = this.players[claimedOdr]?.discards[this.players[claimedOdr].discards.length - 1]
      if (claimedSprite?.active) {
        this.lastRonSource = {
          x: claimedSprite.x,
          y: claimedSprite.y,
          width: claimedSprite.displayWidth,
          height: claimedSprite.displayHeight,
          isReach: Boolean(claimedDiscard?.isReach),
        }
      }
      const claimed = this.players[claimedOdr]?.discards.pop()
      if (claimed?.isReach) this.players[claimedOdr].reachDiscardCarry = true
      if (claimed) {
        this.claimedDiscardCounts[claimedOdr]++
        this.lastDiscardOdr = null
        this.redrawDiscards(claimedOdr)
      }
    }

    if (action === Act.Ric) {
      this.players[odr].isReach = true
      this.emitToUiScene('reach', { odr, viewOdr: this.myOdr })
      if (!this.isReplay && this.isLocalPlayerOdr(odr)) window.dispatchEvent(new Event(GAME_LOCAL_REACH_EVENT))
      if (!suppressLivePlayback && materializedDiscard) {
        this.playReachBgm()
        this.playDefaultReachStickSound(odr)
      }
    }

    if ([Act.Chi, Act.Pon, Act.Kan, Act.Ank, Act.Cha, Act.Hua].includes(action) && odr >= 0 && odr < this.players.length) {
      if (action === Act.Kan || action === Act.Ank || action === Act.Cha) {
        this.syncLiveDoraIndicators(this.paifuGraphRound.dora.length + 1)
      }
      this.discardAfterCall[odr] = action === Act.Chi || action === Act.Pon
      this.applyMeldAction(odr, action as Act, indices, this.readClaimedOdr(data, odr, action as Act), suppressLivePlayback)
    }

    this.reportGameDiscardAudit(data, odr, action)
  }

  private showCallAction(odr: number, action: Act) {
    this.playCallActionSound(odr, action)
    this.showBoardEffect(odr, action)

    const frame = this.callBalloonFrame(action)
    if (frame === undefined) return
    const player = this.players[odr]
    this.emitToUiScene('callAction', {
      odr,
      frame,
      costumeAction: this.costumeActionName(action),
      avatarUrl: this.callAvatarUrl(player),
      fallbackAvatarUrl: player.avatarUrl || player.fallbackAvatarUrl || this.callAvatarUrl(player),
    })
  }

  private callAvatarUrl(player: PlayerState) {
    const costumeId = Number(player.customCostume ?? 0)
    if (Number.isFinite(costumeId) && costumeId > 0 && costumeId !== CUSTOM_DEFAULT_ID_COSTUME && AVAILABLE_COSTUME_IDS.has(costumeId)) {
      const costumeType = Number(player.customCostumeType ?? 0)
      if (costumeType <= 0 || (costumeType >= 30 && costumeType < 40)) {
        const imageId = String(costumeId).padStart(2, '0')
        return `${IMG}/skin/${costumeId}/mj_costume_default_${imageId}.png`
      }
    }
    if (player.avatarId) return getHighResolutionGameAvatarUrl(player.avatarId)
    return player.fallbackAvatarUrl ?? getDefaultAvatarUrl(player.sex === 'F' || player.sex === 'female' ? 'female' : 'male')
  }

  private callBalloonFrame(action: Act): number | undefined {
    if (action === Act.Ric) return 0
    if (action === Act.Tsu) return 1
    if (action === Act.Pon) return 2
    if (action === Act.Kan || action === Act.Ank || action === Act.Cha) return 3
    if (action === Act.Chi) return 4
    if (action === Act.Ron) return 5
    return undefined
  }

  private costumeActionName(action: Act) {
    if (action === Act.Ric) return 'reach'
    if (action === Act.Tsu) return 'tsumo'
    if (action === Act.Pon) return 'pon'
    if (action === Act.Kan || action === Act.Ank || action === Act.Cha) return 'kan'
    if (action === Act.Chi) return 'chi'
    if (action === Act.Ron) return 'ron'
    return undefined
  }

  private appendDiscard(odr: number, bipaiIndex: number | undefined, code: number, isReach: boolean) {
    if (odr < 0 || odr >= this.players.length || code <= 0) return false
    if (bipaiIndex !== undefined && this.players[odr].discards.some(discard => discard.bipaiIndex === bipaiIndex)) {
      this.logDiscardProbe('skip duplicate discard append', { odr, bipaiIndex, code, isReach })
      return false
    }
    const discard = { code, bipaiIndex, isReach }
    this.players[odr].reachDiscardCarry = false
    this.players[odr].discards.push(discard)
    this.paifuGraphDiscards[odr].push(discard)
    return true
  }

  private flushPendingDiscards() {
    if (this.pendingDiscardsByBipaiIndex.size === 0) return
    const changedOdr = new Set<number>()
    const flightOrigins = new Map<number, DiscardFlightOrigin>()
    for (const [bipaiIndex, pending] of [...this.pendingDiscardsByBipaiIndex]) {
      const code = this.knownPai.get(bipaiIndex) ?? 0
      if (code <= 0) continue
      this.pendingDiscardsByBipaiIndex.delete(bipaiIndex)
      this.logDiscardProbe('flush pending discard', { bipaiIndex, code, pending })
      if (!this.appendDiscard(pending.odr, pending.bipaiIndex, code, pending.isReach)) continue
      changedOdr.add(pending.odr)
      if (pending.animateDiscard && pending.flightOrigin) flightOrigins.set(pending.odr, pending.flightOrigin)
      if (pending.isReach && pending.playReachFeedback) {
        this.playReachBgm()
        this.playDefaultReachStickSound(pending.odr)
        this.playReachDiscardSound(pending.odr)
        this.showReachTileEffect(pending.odr)
      }
    }
    changedOdr.forEach(odr => {
      this.redrawDiscards(odr)
      this.animateLatestDiscard(odr, flightOrigins.get(odr))
      this.redrawPaifuGraphContent()
    })
  }

  private showBoardEffect(odr: number, action: Act) {
    const prefix = this.boardEffectPrefix(action)
    if (!prefix) return
    const loc = odrToLoc(odr, this.myOdr)
    const basePoint = isMobileIngameLayout(this.layoutMode)
      ? boardLocalPoint(BOARD_EFFECT_POS[loc])
      : boardEdgePoint(BOARD_EFFECT_POS[loc], loc, this.layoutMode)
    const mobileOffset = mobileCenterHudOffset(this.layoutMode)
    const point = isMobileIngameLayout(this.layoutMode)
      ? { x: basePoint.x + mobileOffset.x, y: basePoint.y + mobileOffset.y }
      : basePoint
    let frame = 1
    const sprite = this.clipToBoard(this.add.image(point.x, point.y, `${prefix}_${String(frame).padStart(2, '0')}`)
      .setOrigin(0, 0)
      .setDepth(Z_PANEL + 5))
    this.boardEffectSprites.push(sprite)
    const timer = this.time.addEvent({
      delay: 45,
      repeat: 13,
      callback: () => {
        frame++
        if (frame <= 14) sprite.setTexture(`${prefix}_${String(frame).padStart(2, '0')}`)
      },
    })
    this.time.delayedCall(45 * 15, () => {
      timer.destroy()
      sprite.destroy()
      this.boardEffectSprites = this.boardEffectSprites.filter(item => item !== sprite)
    })
  }

  private playDefaultReachStickSound(odr: number) {
    const effect = Number(this.players[odr]?.richiEffect ?? 0)
    if (effect < 1 || effect > 3) playMajakSid(SID_RICSTK, this.soundSkinOptions())
  }

  private boardEffectPrefix(action: Act): string | undefined {
    if (action === Act.Ron) return 'mj_ron_w'
    if (action === Act.Tsu) return 'mj_tumo_w'
    return undefined
  }

  private showReachTileEffect(odr: number) {
    const player = this.players[odr]
    const discardIndex = player.discards.length - 1
    if (discardIndex < 0) return

    const loc = odrToLoc(odr, this.myOdr)
    let nextFlag = 0
    let reachFlag = 2
    for (let idx = 0; idx <= discardIndex; idx++) {
      let flag = player.discards[idx].isReach ? 2 : nextFlag
      if (idx % DISCARD_COLS === 0 && flag === 1) flag = 0
      nextFlag = flag === 2 ? 1 : flag
      if (idx === discardIndex) reachFlag = flag
    }

    const tilePoint = discardPos(loc, discardIndex, reachFlag, this.layoutMode)
    const dir = loc & 1 ? '01' : '00'
    const offset = REACH_TILE_EFFECT_OFFSET[loc]
    const move = REACH_TILE_EFFECT_MOVE[loc]
    let effectX = tilePoint.x + offset.x
    let effectY = tilePoint.y + offset.y

    for (let frame = 0; frame < 5; frame++) {
      const sprite = this.clipToBoard(this.add.image(effectX, effectY, `mj_ef_reachhai${dir}_${String(frame).padStart(2, '0')}`)
        .setOrigin(0, 0)
        .setDepth(tilePoint.y + 1))
      sprite.setVisible(false)
      this.reachTileEffectSprites.push(sprite)
      this.time.delayedCall(25 * frame, () => sprite.setVisible(true))
      this.time.delayedCall(25 * (frame + 1), () => sprite.destroy())
      effectX += move.x
      effectY += move.y
    }

    for (let frame = 0; frame < 7; frame++) {
      const sprite = this.clipToBoard(this.add.image(tilePoint.x, tilePoint.y, `mj_ef_reachhai_lgt${dir}_${String(frame).padStart(2, '0')}`)
        .setOrigin(0, 0)
        .setDepth(tilePoint.y + 1))
      sprite.setVisible(false)
      this.reachTileEffectSprites.push(sprite)
      this.time.delayedCall(125 + 50 * frame, () => sprite.setVisible(true))
      this.time.delayedCall(125 + 50 * (frame + 1), () => sprite.destroy())
    }

    this.time.delayedCall(500, () => {
      this.reachTileEffectSprites = this.reachTileEffectSprites.filter(sprite => sprite.active)
    })
  }

  private playCallActionSound(odr: number, action: Act) {
    if (action === Act.Tap) return

    const player = this.players[odr]
    const options = {
      odr,
      sex: player?.sex,
      customCostume: player?.customCostume,
      customCostumeType: player?.customCostumeType,
      soundSkinId: this.currentBgmSkinId,
    }

    if (action === Act.Ric) playMajakCallVoice('reach', options)
    else if (action === Act.Tsu) playMajakCallVoice('tsumo', options)
    else if (action === Act.Ron) playMajakCallVoice('ron', options)
    else if (action === Act.Pon) playMajakCallVoice('pon', options)
    else if (action === Act.Chi) playMajakCallVoice('chi', options)
    else if (action === Act.Kan || action === Act.Ank || action === Act.Cha) playMajakCallVoice('kan', options)
    else if (action === Act.Hua) playMajakCallVoice('hua', options)
  }

  private pushPaiInfo(ini: boolean, openPos: number, tiles: TileState[]) {
    if (this.paiInfoQueue.length === 0 || ini) {
      this.paiInfoQueue.push({ bIniKyo: ini, openPos, tiles: [] })
    }
    const msg = this.paiInfoQueue[this.paiInfoQueue.length - 1]
    msg.tiles.push(...tiles)
  }

  private clonePaiInfoMsg(msg: PaiInfoMsgState): PaiInfoMsgState {
    return { bIniKyo: msg.bIniKyo, openPos: msg.openPos, tiles: this.cloneTiles(msg.tiles) }
  }

  private applyResyncHandSnapshot(snapshot: ResyncHandSnapshot) {
    if (snapshot.openPos < 0 || snapshot.openPos >= this.players.length) return
    this.applyResyncVisiblePai(snapshot)
    this.players[snapshot.openPos].hand = this.cloneTiles(snapshot.handTiles)
    this.redrawHand(snapshot.openPos)
  }

  private applyResyncVisiblePai(snapshot: ResyncHandSnapshot) {
    restoreVisiblePaiCodes(this.knownPai, snapshot.visibleTiles)
  }

  private applyPendingResyncVisiblePai() {
    if (this.pendingResyncHandSnapshot) this.applyResyncVisiblePai(this.pendingResyncHandSnapshot)
  }

  private applyPendingResyncHandSnapshot() {
    const snapshot = this.pendingResyncHandSnapshot
    if (!snapshot) return
    this.pendingResyncHandSnapshot = undefined
    this.applyResyncHandSnapshot(snapshot)
  }

  private popPaiInfo(ini: boolean, initialOyaOrder?: number, initialDice?: number[]) {
    if (ini) {
      let dropped = 0
      while (this.paiInfoQueue.length > 0 && !this.paiInfoQueue[0].bIniKyo) {
        this.paiInfoQueue.shift()
        dropped++
      }
      if (dropped > 0 && DEBUG_GAME) console.warn('[GameScene] dropped stale non-init PaiInfo before init kyoku', { dropped })
    }
    const msg = this.paiInfoQueue[0]
    if (!msg) {
      this.logDiscardProbe('pop PaiInfo skipped: empty queue', { ini })
      return
    }
    if (!ini && msg.bIniKyo) {
      this.logDiscardProbe('pop PaiInfo skipped: next packet is init', { ini, nextOpenPos: msg.openPos, nextCount: msg.tiles.length })
      return
    }
    if (ini && !msg.bIniKyo) {
      if (DEBUG_GAME) console.warn('[GameScene] PaiInfo pop expected init packet', { msg })
      this.logDiscardProbe('pop PaiInfo skipped: expected init packet', { ini, nextOpenPos: msg.openPos, nextCount: msg.tiles.length })
      return
    }
    this.paiInfoQueue.shift()
    this.logDiscardProbe('pop PaiInfo apply', { ini, openPos: msg.openPos, isInit: msg.bIniKyo, paiCount: msg.tiles.length })
    this.applyPaiInfo(msg, initialOyaOrder, initialDice)
  }

  private applyPaiInfo(msg: PaiInfoMsgState, initialOyaOrder?: number, initialDice?: number[]) {
    const openPos = msg.openPos
    msg.tiles.forEach(tile => {
      if (tile.bipaiIndex !== undefined && tile.bipaiIndex >= 0) this.knownPai.set(tile.bipaiIndex, tile.code)
    })
    this.logDiscardProbe('apply PaiInfo', {
      openPos,
      isInit: msg.bIniKyo,
      paiCount: msg.tiles.length,
      sample: msg.tiles.slice(0, 16).map(tile => ({ idx: tile.bipaiIndex, code: tile.code })),
      pendingDiscardIndexes: [...this.pendingDiscardsByBipaiIndex.keys()],
    })
    this.flushPendingDiscards()
    if (msg.bIniKyo) this.applyInitialDoraFromPaiInfo(msg.tiles, initialOyaOrder, initialDice)
    if (openPos === VIEWER_OPEN_POS) {
      this.applyViewerPaiInfo(msg, initialOyaOrder, initialDice)
      return
    }
    if (openPos < 0 || openPos >= this.players.length) return
    this.latestActionPaiInfoTiles[openPos] = msg.bIniKyo ? [] : this.cloneTiles(msg.tiles)
    if (msg.bIniKyo) {
      if (!this.isReplay && !this.isViewer && openPos !== this.myOdr) {
        this.myOdr = openPos
        this.emitViewOdrChange()
      }
      const expectedHandCount = openPos === initialOyaOrder ? 14 : 13
      const initialHand = this.selectInitialHandTiles(openPos, msg.tiles, initialOyaOrder, initialDice, expectedHandCount)
      if (initialHand.length === 0) return
      const displayedHand = sortHandForDisplay(initialHand, initialHand.length % 3 === 2)
      this.players[openPos].hand = this.cloneTiles(displayedHand)
      this.paifuGraphInitialHands[openPos] = this.cloneTiles(displayedHand)
      this.redrawHand(openPos)
    }
  }

  private applyViewerPaiInfo(msg: PaiInfoMsgState, initialOyaOrder?: number, initialDice?: number[]) {
    if (!msg.bIniKyo) {
      return
    }

    for (let odr = 0; odr < this.players.length; odr++) {
      const initialHand = this.buildInitialHandTiles(odr, msg.tiles, initialOyaOrder, initialDice)
      if (initialHand.length === 0) continue
      const displayedHand = sortHandForDisplay(initialHand, initialHand.length % 3 === 2)
      this.players[odr].hand = this.cloneTiles(displayedHand)
      this.paifuGraphInitialHands[odr] = this.cloneTiles(displayedHand.filter(tile => tile.code > 0))
      this.redrawHand(odr)
    }
  }

  private buildInitialHandTiles(openPos: number, tiles: TileState[], oyaOrder?: number, dice?: number[]): TileState[] {
    let handIndices = this.buildInitialHandIndexSet(openPos, oyaOrder, dice)
    if (!handIndices) return []
    const byBipaiIndex = new Map<number, TileState>()
    tiles.forEach(tile => {
      if (tile.bipaiIndex !== undefined) byBipaiIndex.set(tile.bipaiIndex, tile)
    })
    if (handIndices.some(bipaiIndex => !byBipaiIndex.has(bipaiIndex)) && oyaOrder !== undefined) {
      const inferredStart = inferInitialDealStart(tiles.flatMap(tile => tile.bipaiIndex === undefined ? [] : [tile.bipaiIndex]))
      if (inferredStart !== undefined) handIndices = buildInitialHandIndices(openPos, oyaOrder, inferredStart)
    }
    return handIndices.map(bipaiIndex => byBipaiIndex.get(bipaiIndex) ?? { code: 0, bipaiIndex, isSelected: false })
  }

  private selectInitialHandTiles(openPos: number, tiles: TileState[], oyaOrder?: number, dice?: number[], fallbackCount = 13): TileState[] {
    const handIndices = this.buildInitialHandIndexSet(openPos, oyaOrder, dice)
    if (!handIndices) return this.selectInitialHandTilesFromVisiblePai(tiles, this.getDoraIndex(0), fallbackCount)
    const byBipaiIndex = new Map<number, TileState>()
    tiles.forEach(tile => {
      if (tile.bipaiIndex !== undefined) byBipaiIndex.set(tile.bipaiIndex, tile)
    })
    const hand = handIndices
      .map(bipaiIndex => byBipaiIndex.get(bipaiIndex))
      .filter((tile): tile is TileState => Boolean(tile))
    if (hand.length !== handIndices.length && DEBUG_GAME) {
      console.warn('[GameScene] initial PaiInfo did not contain every expected hand tile', { openPos, expected: handIndices, received: tiles })
    }
    return hand.length === handIndices.length
      ? hand
      : this.selectInitialHandTilesFromVisiblePai(tiles, this.getDoraIndex(0), handIndices.length)
  }

  private selectInitialHandTilesFromVisiblePai(tiles: TileState[], excludedBipaiIndex?: number, count = 13): TileState[] {
    return tiles
      .filter(tile => tile.code > 0 && tile.bipaiIndex !== excludedBipaiIndex)
      .slice(0, count)
  }

  private applyInitialDoraFromPaiInfo(tiles: TileState[], oyaOrder?: number, dice?: number[]) {
    const haipaiPos = this.getHaipaiPos(oyaOrder, dice)
    if (haipaiPos === undefined) return
    const doraIndex = (haipaiPos + 130) % 136
    const doraTile = tiles.find(tile => tile.bipaiIndex === doraIndex)
    if (!doraTile || doraTile.code <= 0) return
    this.paifuGraphRound.dora = [doraTile.code]
    if (!this.deferInitialDeadWallRedraw) this.redrawDeadWall()
    this.redrawPaifuGraphContent()
  }

  private syncLiveDoraIndicators(minCount: number) {
    const count = Math.max(0, Math.min(5, Math.trunc(minCount)))
    if (count === 0) return
    const indicators = [...this.paifuGraphRound.dora]
    const revealedIndexes: number[] = []
    let changed = false
    for (let idx = 0; idx < count; idx++) {
      const doraIndex = this.getDoraIndex(idx)
      const code = doraIndex === undefined ? undefined : this.knownPai.get(doraIndex)
      if (!code || code <= 0) continue
      if (indicators[idx] !== code) {
        indicators[idx] = code
        revealedIndexes.push(idx)
        changed = true
      }
    }
    if (!changed) return
    this.paifuGraphRound.dora = indicators
    this.redrawDeadWall()
    this.animateDoraReveal(revealedIndexes)
    this.redrawPaifuGraphContent()
  }

  private buildInitialHandIndexSet(openPos: number, oyaOrder?: number, dice?: number[]): number[] | undefined {
    if (openPos < 0 || openPos >= this.players.length) return undefined
    if (oyaOrder === undefined || oyaOrder < 0 || oyaOrder >= this.players.length) return undefined
    const haipaiPos = this.getHaipaiPos(oyaOrder, dice)
    if (haipaiPos === undefined) return undefined
    return buildInitialHandIndices(openPos, oyaOrder, haipaiPos)
  }

  private getHaipaiPos(oyaOrder?: number, dice?: number[]): number | undefined {
    if (oyaOrder === undefined || oyaOrder < 0 || oyaOrder >= this.players.length) return undefined
    if (!dice || dice.length < 2 || !Number.isFinite(dice[0]) || !Number.isFinite(dice[1])) return undefined
    const wareme = dice[0] + dice[1] + 2
    return (wareme + this.mod(12 + this.players.length - oyaOrder - wareme + 1, this.players.length) * 17) * 2
  }

  private getDoraIndex(index: number): number | undefined {
    const oyaOrder = this.mod(this.chicha + this.paifuGraphRound.kyokuCnt, this.players.length)
    const haipaiPos = this.getHaipaiPos(oyaOrder, this.paifuGraphRound.dice)
    if (haipaiPos === undefined) return undefined
    return this.mod(haipaiPos + 130 - index * 2, 136)
  }

  private mod(value: number, divisor: number) {
    return ((value % divisor) + divisor) % divisor
  }

  private ensureConcealedOpponentHands() {
    for (let odr = 0; odr < this.players.length; odr++) {
      if (this.isLocalPlayerOdr(odr) || this.players[odr].hand.length > 0) continue
      this.ensureConcealedHandCount(odr, 13)
    }
  }

  private ensureConcealedHandCount(odr: number, count: number) {
    if (odr < 0 || odr >= this.players.length || this.isLocalPlayerOdr(odr)) return
    const hand = this.players[odr].hand
    while (hand.length < count) hand.push({ code: 0, isSelected: false })
    if (hand.length > count) hand.splice(count)
    this.redrawHand(odr)
  }

  private ensureTurnDrawTile(odr: number) {
    if (odr < 0 || odr >= this.players.length || this.isLocalPlayerOdr(odr)) return
    const hand = this.players[odr].hand
    if (hand.length > 0 && hand.length % 3 === 1) {
      hand.push(this.latestUnheldPaiInfoTile(odr) ?? { code: 0, isSelected: false })
      this.redrawHand(odr)
      if (!this.shouldSuppressLivePlayback()) playMajakSid(SID_EXPOSE, this.soundSkinOptions())
    }
  }

  private latestUnheldPaiInfoTile(odr: number): TileState | undefined {
    const held = new Set<number>()
    this.players[odr].hand.forEach(tile => {
      if (tile.bipaiIndex !== undefined) held.add(tile.bipaiIndex)
    })
    this.players[odr].discards.forEach(tile => {
      if (tile.bipaiIndex !== undefined) held.add(tile.bipaiIndex)
    })
    return [...(this.latestActionPaiInfoTiles[odr] ?? [])]
      .reverse()
      .find(tile => tile.code > 0 && tile.bipaiIndex !== undefined && !held.has(tile.bipaiIndex))
  }

  private readClaimedOdr(data: Record<string, unknown>, odr: number, action: Act): number {
    const explicit = Number(data.preOdr ?? data.fromOdr ?? data.discardOdr ?? data.claimedOdr ?? -1)
    if (Number.isInteger(explicit) && explicit >= 0 && explicit < this.players.length) return explicit
    if (this.lastDiscardOdr !== null) return this.lastDiscardOdr
    if (action === Act.Chi) return (odr + 3) % 4
    return (odr + 3) % 4
  }

  private applyMeldAction(odr: number, action: Act, indices: number[], claimedOdr: number, suppressLivePlayback = false) {
    if (action === Act.Hua) {
      const tile = this.takeHandTilesByBipai(odr, indices)[0]
      if (tile) {
        this.players[odr].flowers.push(tile)
        this.addPaifuGraphCall(odr, tile.code, action)
      }
      this.redrawHand(odr)
      this.redrawMelds(odr)
      if (!suppressLivePlayback) playMajakSid(SID_FURO, this.soundSkinOptions())
      return
    }

    const handTiles = this.takeHandTilesByBipai(odr, indices)
    if (action === Act.Cha) {
      this.applyAddedKanAction(odr, handTiles[0])
      this.redrawHand(odr)
      this.redrawMelds(odr)
      if (!suppressLivePlayback) playMajakSid(SID_FURO, this.soundSkinOptions())
      return
    }

    const claimed = action === Act.Ank
      ? undefined
      : this.players[claimedOdr]?.discards.pop()

    if (claimed) {
      this.claimedDiscardCounts[claimedOdr]++
      if (claimed.isReach) this.players[claimedOdr].reachDiscardCarry = true
      this.lastDiscardOdr = null
      this.redrawDiscards(claimedOdr)
      this.addPaifuGraphCall(odr, claimed.code, action)
    } else if (action === Act.Ank && handTiles[0]) {
      this.addPaifuGraphCall(odr, handTiles[0].code, action)
    }

    if (action === Act.Ank) {
      const tiles = handTiles.slice(0, 4)
      if (tiles.length === 4) {
        this.players[odr].melds.push({
          action,
          tiles: [
            { code: tiles[1].code, flag: 0, isDown: true },
            { code: tiles[3].code, flag: 0 },
            { code: tiles[2].code, flag: 0 },
            { code: tiles[0].code, flag: 0, isDown: true },
          ],
        })
      }
    } else if (action === Act.Chi && claimed) {
      const tiles = [...handTiles, { code: claimed.code, isSelected: false }]
        .sort((a, b) => paiToFrame(a.code) - paiToFrame(b.code))
      this.players[odr].melds.push({
        action,
        tiles: tiles.map(tile => ({ code: tile.code, flag: tile.code === claimed.code ? 2 : 0 })),
      })
    } else if ((action === Act.Pon || action === Act.Kan) && claimed) {
      const needed = action === Act.Pon ? 3 : 4
      const relative = (4 + claimedOdr - odr) % 4
      this.players[odr].melds.push({
        action,
        tiles: this.arrangeCalledMeldTiles(claimed.code, handTiles, needed, relative),
      })
    }

    this.redrawHand(odr)
    this.redrawMelds(odr)
    if (!suppressLivePlayback && [Act.Chi, Act.Pon, Act.Kan, Act.Ank].includes(action)) playMajakSid(SID_FURO, this.soundSkinOptions())
  }

  private applyAddedKanAction(odr: number, added?: TileState) {
    if (!added) return
    const meld = [...this.players[odr].melds]
      .reverse()
      .find(item => item.action === Act.Pon && item.tiles.some(tile => tile.code === added.code))
    if (!meld) {
      console.warn('[GameScene] added kan ignored: matching pon meld not found', { odr, added })
      return
    }
    const calledIndex = meld.tiles.findIndex(tile => tile.flag === 2)
    const insertIndex = calledIndex >= 0 ? calledIndex : Math.min(2, meld.tiles.length)
    meld.action = Act.Cha
    meld.tiles.splice(insertIndex, 0, { code: added.code, flag: 0 })
    if (meld.tiles.length > 4) meld.tiles.splice(4)
    this.addPaifuGraphCall(odr, added.code, Act.Cha)
  }

  private arrangeCalledMeldTiles(claimedCode: number, handTiles: TileState[], needed: number, relative: number): MeldTileState[] {
    const calledCol = Phaser.Math.Clamp(relative - 1, 0, needed - 1)
    const sorted = [...handTiles].sort((a, b) => paiToFrame(b.code) - paiToFrame(a.code))
    const tiles: MeldTileState[] = []
    for (let col = 0; col < needed; col++) {
      if (col === calledCol) {
        tiles.push({ code: claimedCode, flag: 2 })
      } else {
        const tile = sorted.shift()
        tiles.push({ code: tile && tile.code > 0 ? tile.code : claimedCode, flag: col > calledCol ? 1 : 0 })
      }
    }
    return tiles
  }

  private takeHandTilesByBipai(odr: number, indices: number[]): TileState[] {
    const hand = this.players[odr].hand
    const taken: TileState[] = []
    for (const bipaiIndex of indices) {
      const handIdx = hand.findIndex(tile => tile.bipaiIndex === bipaiIndex)
      const knownCode = this.knownPai.get(bipaiIndex)
      if (handIdx >= 0) {
        const tile = hand.splice(handIdx, 1)[0]
        taken.push(knownCode && knownCode > 0 ? { ...tile, code: knownCode } : tile)
      } else {
        if (!this.isLocalPlayerOdr(odr) && hand.length > 0) {
          const hidden = hand.pop()
          if (hidden) taken.push({ ...hidden, code: knownCode ?? hidden.code, bipaiIndex })
        } else if (knownCode) {
          taken.push({ code: knownCode, bipaiIndex, isSelected: false })
        }
      }
    }
    this.players[odr].hand.sort((a, b) => paiToFrame(a.code) - paiToFrame(b.code))
    return taken
  }

  private playRoundStartSounds(data: Record<string, unknown>, startDelay = 0) {
    const soundSkinId = this.resolveBgmSkinId(data) ?? this.currentBgmSkinId
    const dice = Array.isArray(data.dice) ? data.dice : []
    const start = () => {
      if (!this.shouldPlayLegacyVisuals()) return
      playMajakSid(SID_SIPAI, this.soundSkinOptions(soundSkinId))
      if (dice.length > 0) this.time.delayedCall(2000, () => {
        if (this.shouldPlayLegacyVisuals()) playMajakSid(SID_DICE, this.soundSkinOptions(soundSkinId))
      })
    }
    if (startDelay > 0) this.time.delayedCall(startDelay, start)
    else start()
  }

  private playRoundBgm(data: Record<string, unknown>, kyokuCnt: number) {
    this.currentBgmSkinId = this.resolveBgmSkinId(data)
    this.currentRoundUsesTengokuBgm = this.isTengokuBgm(data, this.currentBgmSkinId)
    this.currentRoundIsCarnival = Boolean(data.isCarnivalChannel ?? data.carnivalChannel)
    const mid = this.currentRoundIsCarnival
      ? MID_FESRIC
      : this.currentRoundUsesTengokuBgm
        ? this.selectTengokuRoundBgm(kyokuCnt)
        : this.selectNormalRoundBgm(kyokuCnt)
    playMajakBgm(mid, this.currentBgmSkinId ? { skinId: this.currentBgmSkinId } : {})
  }

  private playReachBgm() {
    const mid = this.currentRoundIsCarnival
      ? MID_FESRIC
      : this.currentRoundUsesTengokuBgm
        ? this.players.filter(player => player.isReach).length > 1 ? MID_TEN_REACH2 : MID_TEN_REACH1
        : MID_RICHI
    playMajakBgm(mid, this.currentBgmSkinId ? { skinId: this.currentBgmSkinId } : {})
  }

  private selectTengokuRoundBgm(kyokuCnt: number): number {
    const isTonpu = this.readOptionDigit(this.paifuGraphRound.roomOption, 0, 1) === 0
    if ((isTonpu && kyokuCnt === 3) || kyokuCnt === 7) return MID_TEN_ALLLAST
    return kyokuCnt < 4 ? MID_TEN_TONBA : MID_TEN_NANBA
  }

  private selectNormalRoundBgm(kyokuCnt: number): number {
    if (kyokuCnt < 4 || this.isReplay || this.myOdr < 0 || this.myOdr >= this.players.length) return MID_NORMAL
    const scores = this.players.map(player => player.score)
    if (scores.some(score => !Number.isFinite(score))) return MID_NORMAL
    const rank = this.scoreRank(scores, this.myOdr)
    if (rank === 0) {
      const adjusted = [...scores]
      adjusted[this.myOdr] -= 8000
      if (this.scoreRank(adjusted, this.myOdr) === 0) return MID_GOOD
    } else if (rank === 3) {
      const adjusted = [...scores]
      adjusted[this.myOdr] += 8000
      if (this.scoreRank(adjusted, this.myOdr) === 3) return MID_BAD
    }
    return MID_NORMAL
  }

  private scoreRank(scores: number[], odr: number): number {
    return scores
      .map((score, index) => ({ score, index }))
      .sort((a, b) => b.score - a.score || a.index - b.index)
      .findIndex(item => item.index === odr)
  }

  private resolveBgmSkinId(data: Record<string, unknown>): number | undefined {
    const direct = asFiniteNumber(data.bgmSkinId ?? data.customBgmSkinId ?? data.soundSkinId)
    if (direct && direct > 0) return direct
    const board = asFiniteNumber(data.customBoard ?? data.mjkk134e ?? data.boardId) ?? this.customBgId
    if (board === CUSTOM_BGM_ID_EXTRA || board === CUSTOM_BGM_ID_TENGOKU) return board
    const bgType = asFiniteNumber(data.customBoardType ?? data.skinTypeBG ?? data.bgType)
    if (bgType === CUSTOM_ITEM_TYPE_BG_EXTRA) return CUSTOM_BGM_ID_EXTRA
    if (bgType === CUSTOM_ITEM_TYPE_BG_TENGOKU) return CUSTOM_BGM_ID_TENGOKU
    if (board === 100001) return CUSTOM_BGM_ID_EXTRA
    if (board === 100002) return CUSTOM_BGM_ID_TENGOKU
    return undefined
  }

  private isTengokuBgm(data: Record<string, unknown>, bgmSkinId: number | undefined): boolean {
    if (bgmSkinId === CUSTOM_BGM_ID_TENGOKU) return true
    const board = asFiniteNumber(data.customBoard ?? data.mjkk134e ?? data.boardId) ?? this.customBgId
    const bgType = asFiniteNumber(data.customBoardType ?? data.skinTypeBG ?? data.bgType)
    return board === CUSTOM_BGM_ID_TENGOKU || bgType === CUSTOM_ITEM_TYPE_BG_TENGOKU
  }

  private playReachDiscardSound(odr: number) {
    playMajakSfx('mjkReach_pai', this.soundSkinOptions())
    const discardCount = this.players[odr]?.discards.length ?? 0
    if (discardCount % DISCARD_COLS !== 1) playMajakSid(SID_THROW_RICH, this.soundSkinOptions())
  }

  private isDoraTile(code: number): boolean {
    return this.paifuGraphRound.dora.some(dora => this.nextDoraCode(dora) === code)
  }

  private nextDoraCode(code: number): number {
    const kind = (code >> 4) & 0xF
    const number = code & 0xF
    if (kind < 3) return (kind << 4) | (number >= 9 ? 1 : number + 1)
    if (number >= 1 && number <= 4) return (kind << 4) | (number >= 4 ? 1 : number + 1)
    if (number >= 5 && number <= 7) return (kind << 4) | (number >= 7 ? 5 : number + 1)
    return code
  }

  private formatKyoku(kyokuCnt: number): string {
    const wind = kyokuCnt < 4 ? '東' : '南'
    return `${wind}${(kyokuCnt % 4) + 1}局`
  }

  private resetRoundState(preserveHands = false) {
    this.initialDealSerial++
    this.initialDealInProgress = false
    this.initialDealVisibleCounts = [0, 0, 0, 0]
    this.clearAllDiscardFlights()
    this.legacyEffectSprites.forEach(sprite => sprite.destroy())
    this.legacyEffectSprites = []
    this.legacyBackdrop?.destroy()
    this.legacyBackdrop = undefined
    this.selectedIdx = -1
    this.lastDiscardOdr = null
    this.lastRonSource = undefined
    this.appliedActionKeys.clear()
    this.pendingDiscardsByBipaiIndex.clear()
    this.paifuGraphDraws = [[], [], [], []]
    this.paifuGraphDiscards = [[], [], [], []]
    this.latestActionPaiInfoTiles = [[], [], [], []]
    this.discardAfterCall = [false, false, false, false]
    this.claimedDiscardCounts = [0, 0, 0, 0]
    this.waitGuidePreviewCache.clear()
    this.paifuGraphRound = {
      ...this.paifuGraphRound,
      dora: [],
      uraDora: [],
    }
    this.clearDeadWall()
    if (!preserveHands) this.knownPai.clear()
    this.clearActionButtons()
    this.selectedCursor?.destroy()
    this.selectedCursor = undefined
    this.clearTenpaiMarkers()
    this.clearTrainingRecommendation()
    this.clearWaitTileGuide()
    for (let odr = 0; odr < this.players.length; odr++) {
      this.clearDiscardSourceMarker(odr)
      if (!preserveHands) this.players[odr].hand = []
      this.paifuGraphInitialHands[odr] = preserveHands ? this.cloneTiles(this.players[odr].hand) : []
      this.players[odr].discards = []
      this.players[odr].melds = []
      this.players[odr].flowers = []
      this.players[odr].isReach = false
      this.players[odr].reachDiscardCarry = false
      this.players[odr].hora = undefined
      this.redrawHand(odr)
      this.redrawDiscards(odr)
      this.redrawMelds(odr)
    }
  }

  private redrawAllReplayPai() {
    if (!this.isReplay) return
    this.redrawAllPerspectivePai()
  }

  private redrawAllPerspectivePai() {
    this.redrawDeadWall()
    for (let odr = 0; odr < this.players.length; odr++) {
      this.redrawHand(odr)
      this.redrawDiscards(odr)
      this.redrawMelds(odr)
    }
  }
}

const enum Act {
  Inv = 0,
  Pas = 1,
  Chi = 2,
  Pon = 3,
  Kan = 4,
  Ron = 5,
  Tap = 6,
  Ank = 7,
  Cha = 8,
  Ric = 9,
  Tao = 10,
  Tsu = 11,
  Hua = 12,
}

